// Updated: Employee name display fix
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  Ban,
  BarChart3,
  Bell,
  Briefcase,
  Building2,
  CalendarDays,
  CalendarClock,
  CalendarRange,
  CheckCircle2,
  CircleAlert,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Clock3,
  Coffee,
  Download,
  FileText,
  House,
  Eye,
  EyeOff,
  Camera,
  KeyRound,
  LayoutGrid,
  List,
  LogOut,
  MapPin,
  MapPinned,
  Megaphone,
  Menu,
  MessageCircle,
  MoreHorizontal,
  Navigation,
  Network,
  Phone,
  Plus,
  RefreshCw,
  Search,
  SlidersHorizontal,
  PersonStanding,
  Pencil,
  Send,
  Settings,
  ShieldCheck,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Trash2,
  Upload,
  UserRound,
  Users,
  Volume2,
  VolumeX,
  Wifi,
  X,
  Zap,
} from "lucide-react";
import "./App.css";
import "./employees-theme.css";
import "./home-reference-panels.css";
import StaffDashboard from "./StaffDashboard";
import ProfileDataTables from './components/ProfileDataTables';
import './profile-data-tables.css';

const navItems = [
  [House, "Home"],
];
const tabs = [
  "Attendance",
  "Employees",
  "Branches",
  "Shifts",
  "Requests",
  "Reports",
  "Insights",
  "Announcements",
];
const PAGE_PATH_BY_TAB = {
  Dashboard: "/",
  Attendance: "/attendance",
  Employees: "/employees",
  Branches: "/branches",
  Shifts: "/shifts",
  Departments: "/departments",
  Designations: "/designations",
  Requests: "/requests",
  Reports: "/reports",
  Insights: "/insights",
  Announcements: "/announcements",
  Settings: "/profile",
  "Kiosk Accounts": "/kiosk-accounts",
};
const TAB_BY_PAGE_PATH = Object.fromEntries(
  Object.entries(PAGE_PATH_BY_TAB).map(([tab, path]) => [path, tab]),
);
const STAFF_APP_TABS = new Set(["Dashboard", "Attendance", "Requests", "Announcements", "Settings"]);

function getPageForPath(pathname) {
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  const lowerPath = normalizedPath.toLowerCase();
  const tab = TAB_BY_PAGE_PATH[lowerPath] || Object.entries(PAGE_PATH_BY_TAB)
    .find(([candidateTab, path]) => candidateTab !== "Dashboard" && lowerPath.startsWith(`${path}/`))?.[0];
  return {
    activeNav: tab && tab !== "Dashboard" ? "HR PORTAL" : "Home",
    activeTab: tab || "Dashboard",
    known: Boolean(tab) || normalizedPath === "/",
  };
}

function navigateToPath(path, replace = false) {
  window.history[replace ? "replaceState" : "pushState"]({}, "", path);
}

function getPathForPage(activeNav, activeTab) {
  if (activeTab === "Dashboard" || activeNav !== "HR PORTAL") return "/";
  const currentPage = getPageForPath(window.location.pathname);
  if (currentPage.known && currentPage.activeTab === activeTab) return window.location.pathname;
  return PAGE_PATH_BY_TAB[activeTab] || "/";
}

const tabIcons = {
  Attendance: Clock3,
  Employees: Users,
  Branches: Building2,
  Shifts: CalendarClock,
  Requests: Send,
  Announcements: Megaphone,
  "Kiosk Accounts": KeyRound,
  Reports: BarChart3,
  Geofences: MapPinned,
  Insights: TrendingUp,
  More: MoreHorizontal,
  Departments: Network,
  Designations: Briefcase,
};
const metrics = [
  ["CHECK-INS", "0", "Today", "up", "blue", Clock3],
  ["CHECK-OUTS", "0", "Today", "up", "blue", Activity],
  ["ON TIME", "0%", "Geofence pass", "up", "green", ShieldCheck],
  ["PRESENT", "0", "Active staff", "flat", "green", Users],
  ["OUTSIDE GEOFENCE", "0", "Needs review", "up", "rose", CircleHelp],
  ["ACTIVE BRANCHES", "0", "Connected", "up", "rose", Building2],
];
const segments = [
  [
    "Checked in on time",
    "Employees inside the branch geofence today",
    "On track",
    "0",
    "green",
  ],
  [
    "Late arrivals",
    "Employees who checked in after the expected start",
    "Review",
    "0",
    "yellow",
  ],
  [
    "Outside geofence",
    "Check-ins recorded outside the branch radius",
    "Flagged",
    "0",
    "red",
  ],
  [
    "Not checked in",
    "Active employees with no check-in recorded today",
    "Follow up",
    "0",
    "gray",
  ],
  [
    "Selfie available",
    "Attendance records with retained verification photos",
    "Verified",
    "0",
    "green",
  ],
];
const upcomingEvents = [
  {
    title: "Employee Townhall",
    date: "Sep 18 - 10:00 AM",
    detail: "Operations and attendance review",
    tone: "blue",
  },
  {
    title: "Branch Safety Drill",
    date: "Sep 22 - 9:30 AM",
    detail: "Geofence and emergency check",
    tone: "green",
  },
  {
    title: "Leadership Sync",
    date: "Sep 27 - 2:00 PM",
    detail: "Quarterly performance standup",
    tone: "purple",
  },
];
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const APP_NAME = process.env.REACT_APP_NAME || "hiwaga";
const SESSION_IDLE_TIMEOUT_MS = 9 * 60 * 60 * 1000;
const SESSION_ACTIVITY_KEY = "hiwaga_last_activity";

/* --- Reusable pagination hook ------------------------------------------- */
const PAGE_SIZE = 10;

function usePagination(data, pageSize = PAGE_SIZE) {
  const [page, setPage] = useState(1);
  // Reset to page 1 whenever the data array changes (e.g. filter applied)
  const totalItems = data.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageData = data.slice(start, start + pageSize);
  return {
    page: safePage,
    setPage,
    totalPages,
    totalItems,
    pageData,
    pageSize,
  };
}

function Pagination({ page, totalPages, totalItems, pageSize, onPageChange }) {
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);
  // Build page number list with ellipsis
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= page - 1 && i <= page + 1)
    ) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "-") {
      pages.push("-");
    }
  }
  return (
    <div className="pagination">
      <span className="pagination-info">
        {from}-{to} of {totalItems}
      </span>
      <div className="pagination-controls">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          aria-label="Previous page"
        >
          -
        </button>
        {pages.map((p, i) =>
          p === "-" ? (
            <span key={`ellipsis-${i}`} className="pagination-ellipsis">-</span>
          ) : (
            <button
              key={p}
              className={`pagination-btn${p === page ? " active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === page ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          className="pagination-btn"
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          aria-label="Next page"
        >
          -
        </button>
      </div>
    </div>
  );
}

/* --- Custom select dropdown (fully styled, no native OS listbox) --------- */
function SelectFilter({ value, onChange, options, placeholder, clearable = false, clearValue = "", defaultValue = "" }) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const triggerRef = useRef(null);
  const selectedOption = options.find((o) => o.value === value);
  const selectedLabel = selectedOption?.label || placeholder;
  const selectedIcon = selectedOption?.icon;

  // Position the portal menu below the trigger button
  const openMenu = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuStyle({
        position: "fixed",
        top: rect.bottom + 5,
        left: rect.left,
        minWidth: rect.width,
        zIndex: 3000,
      });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const menu = open ? createPortal(
    <ul className="sf-menu" role="listbox" style={menuStyle}>
      {options.map((opt) => (
        <li
          key={opt.value}
          role="option"
          aria-selected={opt.value === value}
          className={`sf-option${opt.value === value ? " sf-selected" : ""}`}
          onMouseDown={(e) => { e.preventDefault(); onChange(opt.value); setOpen(false); }}
        >
          {opt.icon && <span className="sf-option-icon">{opt.icon}</span>}
          <span className="sf-option-label">{opt.label}</span>
        </li>
      ))}
    </ul>,
    document.body
  ) : null;

  // Only show clear button if value is set and different from default value
  const shouldShowClear = clearable && value && value !== defaultValue;
  // When clearing, reset to defaultValue if provided, otherwise use clearValue
  const resetValue = defaultValue || clearValue;

  return (
    <div className={`sf-wrap${open ? " sf-open" : ""}`}>
      <button
        type="button"
        className="sf-trigger"
        ref={triggerRef}
        onClick={() => open ? setOpen(false) : openMenu()}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selectedIcon && <span className="sf-trigger-icon">{selectedIcon}</span>}
        <span className={value ? "sf-value" : "sf-placeholder"}>{selectedLabel}</span>
        {shouldShowClear && (
          <span
            className="sf-clear"
            role="button"
            tabIndex={0}
            aria-label={`Clear ${placeholder || "filter"}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              onChange(resetValue);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                onChange(resetValue);
              }
            }}
          >
            <X size={13} />
          </span>
        )}
        <svg className="sf-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {menu}
    </div>
  );
}

function MaritalStatusSelect({ value, onChange, placeholder = "Select" }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const options = [
    { value: "Single", label: "Single" },
    { value: "Married", label: "Married" },
  ];

  return (
    <div ref={wrapperRef} className="custom-select-wrap">
      <button
        type="button"
        className={`custom-select-field${open ? " open" : ""}`}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="custom-select-value">{value || placeholder}</span>
        <span className="custom-select-chevron" aria-hidden="true">
          <ChevronDown size={14} />
        </span>
      </button>
      {open && (
        <div className="custom-select-menu" role="listbox" aria-label="Marital status">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`custom-select-option${value === option.value ? " selected" : ""}`}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function DateField({ label, value, onChange, maxDate = null }) {
  const [open, setOpen] = useState(false);
  const [showMonthMenu, setShowMonthMenu] = useState(false);
  const [showYearMenu, setShowYearMenu] = useState(false);
  const [monthDate, setMonthDate] = useState(() => {
    if (value) return new Date(`${value}T00:00:00`);
    return new Date();
  });
  const wrapperRef = useRef(null);
  const maxDateValue = maxDate ? new Date(`${maxDate}T00:00:00`) : null;
  const minYear = 1900;
  const maxYear = maxDateValue ? maxDateValue.getFullYear() : new Date().getFullYear() + 20;

  useEffect(() => {
    if (!value) return;
    setMonthDate(new Date(`${value}T00:00:00`));
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
        setShowMonthMenu(false);
        setShowYearMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const firstOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const firstDayIndex = (firstOfMonth.getDay() + 6) % 7;
  const startDate = new Date(firstOfMonth);
  startDate.setDate(1 - firstDayIndex);
  const days = [];

  for (let i = 0; i < 42; i++) {
    const current = new Date(startDate);
    current.setDate(startDate.getDate() + i);
    days.push(current);
  }

  const formattedValue = value
    ? new Date(`${value}T00:00:00`).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";

  const handleDateSelect = (date) => {
    const selected = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (maxDateValue && selected > maxDateValue) return;
    onChange(formatLocalDate(selected));
    setOpen(false);
  };

  const setMonthValue = (nextMonth) => {
    const nextDate = new Date(monthDate.getFullYear(), nextMonth, 1);
    if (maxDateValue && nextDate > new Date(maxDateValue.getFullYear(), maxDateValue.getMonth(), 1)) {
      return;
    }
    setMonthDate(nextDate);
    setShowMonthMenu(false);
  };

  const setYearValue = (nextYear) => {
    const nextDate = new Date(nextYear, monthDate.getMonth(), 1);
    if (maxDateValue && nextDate > new Date(maxDateValue.getFullYear(), maxDateValue.getMonth(), 1)) {
      return;
    }
    setMonthDate(nextDate);
    setShowYearMenu(false);
  };

  return (
    <div ref={wrapperRef} className="custom-date-wrapper">
      <label className="custom-date-control">
        <span>{label}</span>
        <button
          type="button"
          className="custom-date-field"
          onClick={() => setOpen((current) => !current)}
        >
          <span className="custom-date-value">{formattedValue}</span>
          <span className="custom-date-icon" aria-hidden="true">
            <CalendarDays size={14} />
          </span>
        </button>
      </label>
      {open && (
        <div className="custom-date-popover" role="dialog" aria-label={`${label} date picker`}>
          <div className="custom-date-header">
            <button
              type="button"
              className="custom-date-nav"
              onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))}
              aria-label="Previous month"
            >
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M12.5 5.5L7.5 10l5 4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <div className="custom-date-picker-controls">
              <button
                type="button"
                className="custom-date-selector"
                onClick={() => {
                  setShowMonthMenu((current) => !current);
                  setShowYearMenu(false);
                }}
              >
                {new Date(2024, monthDate.getMonth(), 1).toLocaleDateString("en-US", { month: "long" })}
              </button>
              {showMonthMenu && (
                <div className="custom-date-menu custom-date-menu--months" role="listbox" aria-label="Month selector">
                  {Array.from({ length: 12 }, (_, index) => (
                    <button
                      key={index}
                      type="button"
                      className={[
                        "custom-date-option",
                        monthDate.getMonth() === index ? "selected" : "",
                      ].filter(Boolean).join(" ")}
                      onClick={() => setMonthValue(index)}
                    >
                      {new Date(2024, index, 1).toLocaleDateString("en-US", { month: "long" })}
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                className="custom-date-selector"
                onClick={() => {
                  setShowYearMenu((current) => !current);
                  setShowMonthMenu(false);
                }}
              >
                {monthDate.getFullYear()}
              </button>
              {showYearMenu && (
                <div className="custom-date-menu custom-date-menu--years" role="listbox" aria-label="Year selector">
                  {Array.from({ length: Math.max(1, maxYear - minYear + 1) }, (_, index) => {
                    const year = minYear + index;
                    return (
                      <button
                        key={year}
                        type="button"
                        className={[
                          "custom-date-option",
                          monthDate.getFullYear() === year ? "selected" : "",
                        ].filter(Boolean).join(" ")}
                        onClick={() => setYearValue(year)}
                      >
                        {year}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              type="button"
              className="custom-date-nav"
              onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))}
              aria-label="Next month"
            >
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M7.5 14.5L12.5 10l-5-4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
          <div className="custom-date-weekdays">
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>
          <div className="custom-date-grid">
            {days.map((date) => {
              const isCurrentMonth = date.getMonth() === monthDate.getMonth();
              const isSelected = value && value === formatLocalDate(date);
              const isToday = date.toDateString() === new Date().toDateString();
              const isDisabled = maxDateValue ? date.getTime() > maxDateValue.getTime() : false;
              return (
                <button
                  key={date.toISOString()}
                  type="button"
                  disabled={isDisabled}
                  className={[
                    "custom-date-day",
                    isCurrentMonth ? "current" : "muted",
                    isSelected ? "selected" : "",
                    isToday && !isSelected ? "today" : "",
                    isDisabled ? "disabled" : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => !isDisabled && handleDateSelect(date)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
          <div className="custom-date-footer">
            <button type="button" className="custom-date-clear" onClick={() => { onChange(""); setOpen(false); }}>Clear</button>
            <button type="button" className="custom-date-today" onClick={() => { const today = new Date(); setMonthDate(today); onChange(formatLocalDate(today)); setOpen(false); }}>Today</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomDateField({ label, value, onChange, maxDate = null, placeholder = "dd/mm/yyyy" }) {
  const [open, setOpen] = useState(false);
  const [monthDate, setMonthDate] = useState(() => {
    if (value) return new Date(`${value}T00:00:00`);
    return new Date();
  });
  const wrapperRef = useRef(null);
  const maxDateValue = maxDate ? new Date(`${maxDate}T00:00:00`) : null;

  useEffect(() => {
    if (!value) return;
    setMonthDate(new Date(`${value}T00:00:00`));
  }, [value]);

  useEffect(() => {
    if (!maxDateValue) return;
    const currentMonthLimit = new Date(maxDateValue.getFullYear(), maxDateValue.getMonth(), 1);
    if (monthDate > currentMonthLimit) {
      setMonthDate(new Date(currentMonthLimit));
    }
  }, [maxDateValue, monthDate]);

  useEffect(() => {
    if (!maxDateValue || !value) return;
    const selectedValue = new Date(`${value}T00:00:00`);
    if (selectedValue > maxDateValue) {
      onChange(maxDate);
    }
  }, [maxDate, maxDateValue, onChange, value]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const monthLabel = monthDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
  const minYear = 2000;
  const maxYear = maxDateValue ? maxDateValue.getFullYear() : monthDate.getFullYear() + 10;
  const [showMonthMenu, setShowMonthMenu] = useState(false);
  const [showYearMenu, setShowYearMenu] = useState(false);

  const firstOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const firstDayIndex = (firstOfMonth.getDay() + 6) % 7;
  const days = [];
  const startDate = new Date(firstOfMonth);
  startDate.setDate(1 - firstDayIndex);

  for (let i = 0; i < 42; i++) {
    const current = new Date(startDate);
    current.setDate(startDate.getDate() + i);
    if (maxDateValue && current > maxDateValue) {
      current.setHours(0, 0, 0, 0);
    }
    days.push(current);
  }

  const formattedValue = value
    ? new Date(`${value}T00:00:00`).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";

  const handleDateSelect = (date) => {
    const selected = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (maxDateValue && selected > maxDateValue) {
      return;
    }
    onChange(formatLocalDate(selected));
    setOpen(false);
  };

  const setMonthValue = (nextMonth) => {
    const nextDate = new Date(monthDate.getFullYear(), nextMonth, 1);
    if (maxDateValue && nextDate > new Date(maxDateValue.getFullYear(), maxDateValue.getMonth(), 1)) {
      return;
    }
    setMonthDate(nextDate);
    setShowMonthMenu(false);
  };

  const setYearValue = (nextYear) => {
    const nextDate = new Date(nextYear, monthDate.getMonth(), 1);
    if (maxDateValue && nextDate > new Date(maxDateValue.getFullYear(), maxDateValue.getMonth(), 1)) {
      return;
    }
    setMonthDate(nextDate);
    setShowYearMenu(false);
  };

  return (
    <div ref={wrapperRef} className="custom-date-wrapper">
      <label className="custom-date-control">
        <span>{label}</span>
        <button
          type="button"
          className="custom-date-field"
          onClick={() => setOpen((current) => !current)}
        >
          <span className={`custom-date-value${formattedValue ? "" : " custom-date-placeholder"}`}>
            {formattedValue || placeholder}
          </span>
          <span className="custom-date-icon" aria-hidden="true">
            <CalendarDays size={14} />
          </span>
        </button>
      </label>
      {open && (
        <div className="custom-date-popover" role="dialog" aria-label={`${label} date picker`}>
          <div className="custom-date-header">
            <button
              type="button"
              className="custom-date-nav"
              onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))}
              aria-label="Previous month"
            >
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M12.5 5.5L7.5 10l5 4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <div className="custom-date-picker-controls">
              <button
                type="button"
                className="custom-date-selector"
                onClick={() => {
                  setShowMonthMenu((current) => !current);
                  setShowYearMenu(false);
                }}
                aria-label="Select month"
              >
                {new Date(2024, monthDate.getMonth(), 1).toLocaleDateString("en-US", { month: "long" })}
              </button>
              {showMonthMenu && (
                <div className="custom-date-menu custom-date-menu--months" role="listbox" aria-label="Month selector">
                  {Array.from({ length: 12 }, (_, index) => (
                    <button
                      key={index}
                      type="button"
                      className={[
                        "custom-date-option",
                        monthDate.getMonth() === index ? "selected" : "",
                      ].filter(Boolean).join(" ")}
                      onClick={() => setMonthValue(index)}
                    >
                      {new Date(2024, index, 1).toLocaleDateString("en-US", { month: "long" })}
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                className="custom-date-selector"
                onClick={() => {
                  setShowYearMenu((current) => !current);
                  setShowMonthMenu(false);
                }}
                aria-label="Select year"
              >
                {monthDate.getFullYear()}
              </button>
              {showYearMenu && (
                <div className="custom-date-menu custom-date-menu--years" role="listbox" aria-label="Year selector">
                  {Array.from({ length: Math.max(1, maxYear - minYear + 1) }, (_, index) => {
                    const year = minYear + index;
                    return (
                      <button
                        key={year}
                        type="button"
                        className={[
                          "custom-date-option",
                          monthDate.getFullYear() === year ? "selected" : "",
                        ].filter(Boolean).join(" ")}
                        onClick={() => setYearValue(year)}
                      >
                        {year}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              type="button"
              className="custom-date-nav"
              onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))}
              aria-label="Next month"
            >
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M7.5 14.5L12.5 10l-5-4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
          <div className="custom-date-weekdays">
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>
          <div className="custom-date-grid">
            {days.map((date) => {
              const isCurrentMonth = date.getMonth() === monthDate.getMonth();
              const isSelected = value && value === formatLocalDate(date);
              const isToday = date.toDateString() === new Date().toDateString();
              const isDisabled = maxDateValue ? date.getTime() > maxDateValue.getTime() : false;
              return (
                <button
                  key={date.toISOString()}
                  type="button"
                  disabled={isDisabled}
                  className={[
                    "custom-date-day",
                    isCurrentMonth ? "current" : "muted",
                    isSelected ? "selected" : "",
                    isToday && !isSelected ? "today" : "",
                    isDisabled ? "disabled" : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => !isDisabled && handleDateSelect(date)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
          <div className="custom-date-footer">
            <button type="button" className="custom-date-clear" onClick={() => { onChange(""); setOpen(false); }}>
              Clear
            </button>
            <button type="button" className="custom-date-today" onClick={() => { const today = new Date(); setMonthDate(today); onChange(formatLocalDate(today)); setOpen(false); }}>
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Parse a timestamp string from the backend as UTC.
 * FastAPI returns naive datetime strings (no Z/offset) which JavaScript
 * would otherwise treat as local time. Appending 'Z' forces UTC parsing
 * so toLocaleTimeString() correctly converts to the browser's local timezone.
 */
function parseUTC(timestamp) {
  if (!timestamp) return null;
  const s = String(timestamp);
  // Already has timezone info - parse as-is
  if (s.endsWith("Z") || s.includes("+") || /[0-9]-[0-9]{2}:[0-9]{2}$/.test(s)) {
    return new Date(s);
  }
  // Naive datetime - treat as UTC
  return new Date(s + "Z");
}

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem("hiwaga_refresh_token");
  if (!refreshToken) return "";
  const response = await fetch(`${API_URL}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!response.ok) return "";
  const data = await response.json();
  localStorage.setItem("hiwaga_access_token", data.access_token);
  localStorage.setItem("hiwaga_refresh_token", data.refresh_token);
  return data.access_token;
}

async function apiFetch(path, options = {}, token = "") {
  const request = (accessToken) =>
    fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });
  let response = await request(token);
  if (response.status === 401) {
    const refreshedToken = await refreshAccessToken();
    if (refreshedToken) response = await request(refreshedToken);
  }
  return response;
}

async function getJson(path, token) {
  const response = await apiFetch(path, {}, token);
  if (!response.ok) throw new Error("API request failed");
  return response.json();
}

function normalizeErrorMessage(error, fallback = "Request failed") {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  if (error.message) return error.message;
  if (Array.isArray(error)) {
    const items = error
      .map((item) => (typeof item === "string" ? item : item?.msg || item?.message || item?.field))
      .filter(Boolean);
    if (items.length) return items.join("; ");
  }
  if (error.detail) {
    if (typeof error.detail === "string") return error.detail;
    if (Array.isArray(error.detail)) return normalizeErrorMessage(error.detail, fallback);
    if (error.detail.errors && Array.isArray(error.detail.errors)) {
      const items = error.detail.errors
        .map((item) => {
          const row = item?.row ? `Row ${item.row}: ` : "";
          const field = item?.field ? `${item.field} ` : "";
          const msg = item?.message ? String(item.message) : "";
          return `${row}${field}${msg}`.trim();
        })
        .filter(Boolean);
      if (items.length) return items.slice(0, 5).join("; ");
    }
    if (error.detail.msg) return error.detail.msg;
    if (error.detail.message) return error.detail.message;
    if (error.detail.field && error.detail.message) {
      return `${error.detail.field}: ${error.detail.message}`;
    }
  }
  return fallback;
}

const normalizeRoleKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getEffectiveCurrentRole = (staffList = [], fallbackRole = "") => {
  const employeeId = localStorage.getItem("hiwaga_employee_id");
  const matchedUser = staffList.find(
    (person) => String(person.employee_id) === String(employeeId),
  );
  const roleValue = matchedUser?.role_code || matchedUser?.role || fallbackRole || "";
  return normalizeRoleKey(roleValue);
};

const normalizePermissionKey = (value) => normalizeRoleKey(value);

function App() {
  const initialPage = getPageForPath(window.location.pathname);
  const [activeNav, setActiveNav] = useState(
    () => initialPage.activeNav,
  );
  const [activeTab, setActiveTab] = useState(
    () => initialPage.activeTab,
  );
  const historySyncFromPopRef = useRef(false);
  const initialRouteSyncRef = useRef(true);
  const isStaffRoleRef = useRef(false);
  const navigationStateRef = useRef({ activeNav, activeTab });
  const [quickAction, setQuickAction] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [accountMenuPosition, setAccountMenuPosition] = useState({ top: 0, right: 8 });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const accountRef = useRef(null);
  const accountTriggerRef = useRef(null);
  const accountMenuRef = useRef(null);
  const searchDirectoryRequested = useRef(false);

  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (!accountRef.current?.contains(event.target) && !accountMenuRef.current?.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [accountMenuOpen]);
  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const updateAccountMenuPosition = () => {
      const anchor = accountTriggerRef.current?.getBoundingClientRect();
      if (!anchor) return;
      setAccountMenuPosition({
        top: anchor.bottom + 8,
        right: Math.max(8, window.innerWidth - anchor.right),
      });
    };
    updateAccountMenuPosition();
    window.addEventListener("resize", updateAccountMenuPosition);
    window.addEventListener("scroll", updateAccountMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateAccountMenuPosition);
      window.removeEventListener("scroll", updateAccountMenuPosition, true);
    };
  }, [accountMenuOpen]);
  const [branches, setBranches] = useState([]);
  const [staff, setStaff] = useState([]);
  const [authenticatedProfile, setAuthenticatedProfile] = useState(null);
  const [profileRefreshRequest, setProfileRefreshRequest] = useState(0);
  const [roleMetadata, setRoleMetadata] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [isLive, setIsLive] = useState(false);
  const [token, setToken] = useState(
    () => localStorage.getItem("hiwaga_access_token") || "",
  );
  const [sessionChecked, setSessionChecked] = useState(
    () => !localStorage.getItem("hiwaga_access_token"),
  );
  const [loginCode, setLoginCode] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState("request");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordOtp, setForgotPasswordOtp] = useState("");
  const [forgotPasswordNewPassword, setForgotPasswordNewPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [changePasswordValue, setChangePasswordValue] = useState("");
  const [confirmChangePassword, setConfirmChangePassword] = useState("");
  const [changePasswordBusy, setChangePasswordBusy] = useState(false);
  const [changePasswordMessage, setChangePasswordMessage] = useState("");
  const [forgotPasswordConfirmPassword, setForgotPasswordConfirmPassword] = useState("");
  const [forgotPasswordResetToken, setForgotPasswordResetToken] = useState("");
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState("");
  const [forgotPasswordBusy, setForgotPasswordBusy] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);

  const clearSession = () => {
    localStorage.removeItem("hiwaga_access_token");
    localStorage.removeItem("hiwaga_refresh_token");
    localStorage.removeItem("hiwaga_branch_id");
    localStorage.removeItem("hiwaga_role");
    localStorage.removeItem("hiwaga_employee_id");
    localStorage.removeItem(SESSION_ACTIVITY_KEY);
    setAuthenticatedProfile(null);
    searchDirectoryRequested.current = false;
    setBranches([]);
    setStaff([]);
    setAttendance([]);
    setIsLive(false);
    setActiveNav("Home");
    setActiveTab("Dashboard");
    setToken("");
    setIsLive(false);
    // Clear login form fields
    setLoginCode("");
    setLoginPassword("");
    setLoginError("");
  };

  const endSession = async (revoke = true) => {
    if (revoke && token) {
      try {
        await fetch(`${API_URL}/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // The local session must still be cleared if the API is unreachable.
      }
    }
    clearSession();
  };

  useEffect(() => {
    navigationStateRef.current = { activeNav, activeTab };
  }, [activeNav, activeTab]);

  useEffect(() => {
    const handlePopState = () => {
      let page = getPageForPath(window.location.pathname);
      if (!page.known) {
        window.history.replaceState({}, "", "/");
        page = getPageForPath("/");
      }
      const currentPage = navigationStateRef.current;
      if (currentPage.activeNav === page.activeNav && currentPage.activeTab === page.activeTab) return;
      historySyncFromPopRef.current = true;
      setActiveNav(page.activeNav);
      setActiveTab(page.activeTab);
      setQuickAction("");
      setMobileOpen(false);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const roleFallback = isStaffRoleRef.current && !STAFF_APP_TABS.has(activeTab);
    const targetPath = roleFallback ? "/" : getPathForPage(activeNav, activeTab);
    const currentPath = window.location.pathname;
    if (historySyncFromPopRef.current) {
      historySyncFromPopRef.current = false;
      initialRouteSyncRef.current = false;
      return;
    }
    if (currentPath !== targetPath) {
      const replace = (initialRouteSyncRef.current && !initialPage.known) || roleFallback;
      window.history[replace ? "replaceState" : "pushState"]({}, "", targetPath);
    }
    initialRouteSyncRef.current = false;
  }, [activeNav, activeTab, initialPage.known]);

  // Validate the server session after refresh and keep its activity timer alive.
  useEffect(() => {
    if (!token) {
      setSessionChecked(true);
      return undefined;
    }

    let ignore = false;
    let heartbeatBusy = false;
    const markActivity = () => {
      localStorage.setItem(SESSION_ACTIVITY_KEY, String(Date.now()));
    };
    const validateSession = async () => {
      if (heartbeatBusy || ignore) return;
      heartbeatBusy = true;
      const lastActivity = Number(localStorage.getItem(SESSION_ACTIVITY_KEY) || 0);
      if (lastActivity && Date.now() - lastActivity >= SESSION_IDLE_TIMEOUT_MS) {
        heartbeatBusy = false;
        await endSession(false);
        return;
      }
      try {
        const response = await apiFetch("/auth/me", {}, token);
        if (!response.ok && !ignore) await endSession(false);
      } catch {
        if (!ignore) await endSession(false);
      } finally {
        heartbeatBusy = false;
        if (!ignore) setSessionChecked(true);
      }
    };

    // Keep the current view mounted during background token rotation. The
    // initial session and login flows already start with this set to false.
    if (!localStorage.getItem(SESSION_ACTIVITY_KEY)) markActivity();
    const activityEvents = ["click", "keydown", "pointermove", "scroll", "touchstart"];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, markActivity, { passive: true }));
    validateSession();
    const timer = window.setInterval(validateSession, 60_000);
    return () => {
      ignore = true;
      window.clearInterval(timer);
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, markActivity));
    };
  }, [token]);

  useEffect(() => {
    if (!token) {
      setRoleMetadata([]);
      return undefined;
    }
    let ignore = false;
    async function loadRoles() {
      try {
        const response = await apiFetch("/roles", {}, token);
        if (!response.ok) return;
        const data = await response.json();
        const rolesFromApi = Array.isArray(data)
          ? data
          : Array.isArray(data.roles)
            ? data.roles
            : [];

        const normalized = rolesFromApi.map((role) => {
          const rawValue = role?.role_code || role?.code || role?.name || role?.value || "";
          const rawLabel = role?.label || role?.role_name || role?.name || role?.role_code || rawValue;
          const rawPermissions = role?.permissions || role?.permission || role?.access || role?.scopes || [];
          const permissions = Array.isArray(rawPermissions)
            ? rawPermissions.map((permission) => String(permission))
            : typeof rawPermissions === "string"
              ? [rawPermissions]
              : [];

          return {
            value: String(rawValue),
            label: String(rawLabel),
            permissions,
          };
        }).filter((role) => role.value && role.value.trim());

        if (!ignore) setRoleMetadata(normalized);
      } catch {
        if (!ignore) setRoleMetadata([]);
      }
    }

    loadRoles();
    return () => { ignore = true; };
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    const doTokenRefresh = async () => {
      const refreshedToken = await refreshAccessToken();
      if (refreshedToken) setToken(refreshedToken);
    };
    const timer = window.setInterval(
      () => {
        doTokenRefresh().catch(() => {});
      },
      7 * 60 * 1000,
    );
    return () => window.clearInterval(timer);
  }, [token]);

  useEffect(() => {
    if (!token) {
      setAuthenticatedProfile(null);
      return undefined;
    }
    let ignore = false;
    getJson("/auth/me", token)
      .then((profile) => {
        if (!ignore) setAuthenticatedProfile(profile);
      })
      .catch(() => {
        if (!ignore) setAuthenticatedProfile(null);
      });
    return () => {
      ignore = true;
    };
  }, [token, profileRefreshRequest]);

  useEffect(() => {
    if (!token || !sessionChecked) return undefined;
    const branchId = localStorage.getItem("hiwaga_branch_id");
    const roleValue = normalizeRoleKey(localStorage.getItem("hiwaga_role") || "");
    if (["staff", "employee", "team_member"].includes(roleValue)) return undefined;

    const isLimitedBranchRole = ["branch_manager", "floor_manager"].includes(roleValue);
    const needsDashboardData = activeTab === "Dashboard";
    const needsStaff = needsDashboardData || [
      "Attendance", "Announcements", "Reports", "Employees", "Branches", "Permissions",
    ].includes(activeTab);
    const needsAttendance = needsDashboardData || ["Attendance", "Insights"].includes(activeTab);
    const needsBranches = needsStaff || ["Shifts", "Insights"].includes(activeTab);
    if (!needsBranches) return undefined;
    if (needsStaff) searchDirectoryRequested.current = true;

    let ignore = false;
    let loadedBranches = [];
    const fetchAttendance = async () => {
      if (!needsAttendance || !loadedBranches.length || ignore) return;
      const results = await Promise.all(
        loadedBranches.map((branch) =>
          getJson(`/attendance/branch/${branch.branch_id}`, token).catch(() => []),
        ),
      );
      if (!ignore) setAttendance(results.flat());
    };
    const load = async () => {
      let branchData = [];
      try {
        branchData = await getJson("/branches", token);
      } catch {
        if (branchId) {
          try {
            branchData = [await getJson(`/branches/${branchId}`, token)];
          } catch {}
        }
      }

      if (isLimitedBranchRole && branchId) {
        branchData = branchData.filter((branch) => String(branch.branch_id) === String(branchId));
      }

      loadedBranches = branchData;
      if (ignore) return;
      setBranches(branchData);
      const results = await Promise.all(
        branchData.map(async (branch) => {
          const [people, records] = await Promise.all([
            needsStaff ? getJson(`/staff/branch/${branch.branch_id}`, token).catch(() => []) : [],
            needsAttendance ? getJson(`/attendance/branch/${branch.branch_id}`, token).catch(() => []) : [],
          ]);
          return { people, records };
        }),
      );
      if (ignore) return;
      if (needsStaff) setStaff(results.flatMap((item) => item.people));
      if (needsAttendance) {
        setAttendance(results.flatMap((item) => item.records));
        if (needsDashboardData) setIsLive(true);
      }
    };
    load().catch(() => {});
    const poll = needsAttendance ? window.setInterval(() => fetchAttendance().catch(() => {}), 30_000) : null;
    return () => {
      ignore = true;
      if (poll) window.clearInterval(poll);
    };
  }, [token, sessionChecked, activeTab]);

  useEffect(() => {
    if (!token || !sessionChecked) return undefined;
    let ignore = false;
    Promise.all([
      getJson("/departments", token).catch(() => []),
      getJson("/designations", token).catch(() => []),
    ]).then(([departmentData, designationData]) => {
      if (ignore) return;
      setDepartments(Array.isArray(departmentData) ? departmentData : []);
      setDesignations(Array.isArray(designationData) ? designationData : []);
    });
    return () => { ignore = true; };
  }, [token, sessionChecked]);

  const hasGlobalSearchQuery = Boolean(globalSearch.trim());
  useEffect(() => {
    if (!token || !sessionChecked || !hasGlobalSearchQuery || searchDirectoryRequested.current) return;
    searchDirectoryRequested.current = true;
    const loadSearchDirectory = async () => {
      const branchId = localStorage.getItem("hiwaga_branch_id");
      const roleValue = normalizeRoleKey(localStorage.getItem("hiwaga_role") || "");
      let branchData = [];
      try {
        branchData = await getJson("/branches", token);
      } catch {
        if (branchId) {
          try { branchData = [await getJson(`/branches/${branchId}`, token)]; } catch {}
        }
      }
      if (["branch_manager", "floor_manager"].includes(roleValue) && branchId) {
        branchData = branchData.filter((branch) => String(branch.branch_id) === String(branchId));
      }
      const people = await Promise.all(
        branchData.map((branch) => getJson(`/staff/branch/${branch.branch_id}`, token).catch(() => [])),
      );
      if (localStorage.getItem("hiwaga_access_token") !== token) return;
      setBranches(branchData);
      setStaff(people.flat());
    };
    loadSearchDirectory().catch(() => {});
  }, [token, sessionChecked, hasGlobalSearchQuery]);

  async function handleLogin(event) {
    event.preventDefault();
    const loginIdentifier = loginCode.trim();
    const isPhoneNumber = /^\d+$/.test(loginIdentifier);
    if (isPhoneNumber && !/^\d{10}$/.test(loginIdentifier)) {
      setLoginError("Enter a phone number with exactly 10 digits.");
      return;
    }
    if (!isPhoneNumber && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(loginIdentifier)) {
      setLoginError("Enter a valid email address or a 10-digit phone number.");
      return;
    }
    setLoginBusy(true);
    setLoginError("");
    try {
      // Backend expects "username" field - can be email, phone, or employee code
      const response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: loginIdentifier,
          password: loginPassword,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Unable to sign in");
      const resolvedRole = data.role || data.role_code || data.user_role || "staff";
      localStorage.setItem("hiwaga_access_token", data.access_token);
      localStorage.setItem("hiwaga_refresh_token", data.refresh_token);
      localStorage.setItem("hiwaga_role", resolvedRole);
      localStorage.setItem("hiwaga_employee_id", data.employee_id);
      localStorage.setItem(SESSION_ACTIVITY_KEY, String(Date.now()));
      if (data.branch_id)
        localStorage.setItem("hiwaga_branch_id", data.branch_id);
      setSessionChecked(false);
      setToken(data.access_token);
    } catch (error) {
      setLoginError(error.message);
    } finally {
      setLoginBusy(false);
    }
  }

  function returnToLoginWithForgotPasswordNotice(message) {
    const noticeText = typeof message === "string" ? message : normalizeErrorMessage(message, "");
    if (!/contact\s+.{0,50}(?:hr|human resources|branch manager)/i.test(noticeText)) return false;
    window.alert(noticeText);
    setShowForgotPassword(false);
    setForgotPasswordStep("request");
    setForgotPasswordEmail("");
    setForgotPasswordOtp("");
    setForgotPasswordNewPassword("");
    setForgotPasswordConfirmPassword("");
    setForgotPasswordResetToken("");
    setForgotPasswordMessage("");
    return true;
  }

  async function handleForgotPassword(event) {
    event.preventDefault();
    setForgotPasswordBusy(true);
    setForgotPasswordMessage("");
    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: forgotPasswordEmail,
        }),
      });
      const data = await response.json();
      if (returnToLoginWithForgotPasswordNotice(data.detail) || returnToLoginWithForgotPasswordNotice(data.message)) return;
      if (!response.ok) throw new Error(normalizeErrorMessage(data, "Unable to send reset OTP"));
      setForgotPasswordMessage(data.message || "Password reset OTP sent to your email!");
      setForgotPasswordStep("otp");
      setForgotPasswordOtp("");
      setForgotPasswordNewPassword("");
      setForgotPasswordConfirmPassword("");
      setForgotPasswordResetToken("");
    } catch (error) {
      if (!returnToLoginWithForgotPasswordNotice(error.message)) {
        setForgotPasswordMessage(error.message);
      }
    } finally {
      setForgotPasswordBusy(false);
    }
  }

  async function handleVerifyOtp(event) {
    event.preventDefault();
    setForgotPasswordBusy(true);
    setForgotPasswordMessage("");
    try {
      if (!forgotPasswordOtp || forgotPasswordOtp.length !== 6) {
        throw new Error("Enter the 6-digit OTP sent to your email.");
      }

      const response = await fetch(`${API_URL}/auth/verify-reset-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: forgotPasswordEmail,
          otp: forgotPasswordOtp,
        }),
      });
      const data = await response.json();
      if (returnToLoginWithForgotPasswordNotice(data.detail) || returnToLoginWithForgotPasswordNotice(data.message)) return;
      if (!response.ok) throw new Error(normalizeErrorMessage(data, "Unable to verify OTP"));

      setForgotPasswordResetToken(data.reset_token || "");
      setForgotPasswordStep("reset");
      setForgotPasswordMessage("OTP verified successfully. Set your new password.");
      setForgotPasswordNewPassword("");
      setForgotPasswordConfirmPassword("");
    } catch (error) {
      if (!returnToLoginWithForgotPasswordNotice(error.message)) {
        setForgotPasswordMessage(error.message);
      }
    } finally {
      setForgotPasswordBusy(false);
    }
  }

  async function handleResetPassword(event) {
    event.preventDefault();
    setForgotPasswordBusy(true);
    setForgotPasswordMessage("");
    try {
      if (!forgotPasswordNewPassword || forgotPasswordNewPassword.length < 8) {
        throw new Error("Password must be at least 8 characters long.");
      }
      if (forgotPasswordNewPassword !== forgotPasswordConfirmPassword) {
        throw new Error("Passwords do not match.");
      }
      if (!forgotPasswordResetToken) {
        throw new Error("OTP verification is missing. Please retry the reset flow.");
      }

      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reset_token: forgotPasswordResetToken,
          new_password: forgotPasswordNewPassword,
        }),
      });
      const data = await response.json();
      if (returnToLoginWithForgotPasswordNotice(data.detail) || returnToLoginWithForgotPasswordNotice(data.message)) return;
      if (!response.ok) throw new Error(normalizeErrorMessage(data, "Unable to reset password"));

      setForgotPasswordMessage(data.message || "Password reset successfully!");
      setForgotPasswordStep("request");
      setForgotPasswordEmail("");
      setForgotPasswordOtp("");
      setForgotPasswordNewPassword("");
      setForgotPasswordConfirmPassword("");
      setForgotPasswordResetToken("");

      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotPasswordMessage("");
      }, 2500);
    } catch (error) {
      if (!returnToLoginWithForgotPasswordNotice(error.message)) {
        setForgotPasswordMessage(error.message);
      }
    } finally {
      setForgotPasswordBusy(false);
    }
  }

  function handleLogout() {
    setAccountMenuOpen(false);
    endSession(true).catch(() => clearSession());
  }

  function handleProfileClick() {
    setAccountMenuOpen(false);
    setActiveNav("HR PORTAL");
    setActiveTab("Settings");
    setProfileRefreshRequest((request) => request + 1);
  }

  function handleChangePassword() {
    setAccountMenuOpen(false);
    setShowPasswordModal(true);
  }

  function handleChangePassword() {
    setCurrentPassword("");
    setChangePasswordValue("");
    setConfirmChangePassword("");
    setChangePasswordMessage("");
    setShowForgotPassword(true);
  }

  async function submitChangePassword(event) {
    event.preventDefault();
    setChangePasswordMessage("");
    if (changePasswordValue.length < 8) {
      setChangePasswordMessage("New password must be at least 8 characters.");
      return;
    }
    if (changePasswordValue !== confirmChangePassword) {
      setChangePasswordMessage("The new passwords do not match.");
      return;
    }
    setChangePasswordBusy(true);
    try {
      const response = await fetch(`${API_URL}/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ old_password: currentPassword, new_password: changePasswordValue }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || data.message || "Unable to change password.");
      setChangePasswordMessage(data.message || "Password changed successfully.");
      setCurrentPassword("");
      setChangePasswordValue("");
      setConfirmChangePassword("");
      window.setTimeout(() => setShowForgotPassword(false), 1400);
    } catch (error) {
      setChangePasswordMessage(error.message);
    } finally {
      setChangePasswordBusy(false);
    }
  }

  const today = new Date().toDateString();
  const todayRecords = attendance.filter(
    (record) => parseUTC(record.timestamp).toDateString() === today,
  );
  const checkedIn = todayRecords.filter(
    (record) => record.check_type === "in",
  ).length;
  const checkedOut = todayRecords.filter(
    (record) => record.check_type === "out",
  ).length;
  const passed = todayRecords.filter(
    (record) => record.geofence_status === "pass",
  ).length;
  const outside = todayRecords.filter(
    (record) => record.geofence_status !== "pass",
  ).length;
  const onTime = todayRecords.length
    ? `${Math.round((passed / todayRecords.length) * 100)}%`
    : "0%";
  const activeStaff = staff.filter(
    (person) => person.employment_status === "active",
  ).length;
  const liveMetrics = useMemo(
    () => [
      [checkedIn, "Today"],
      [checkedOut, "Today"],
      [onTime, "Geofence pass"],
      [activeStaff, "Active staff"],
      [outside, "Needs review"],
      [branches.filter((branch) => branch.is_active).length, "Connected"],
    ],
    [checkedIn, checkedOut, onTime, activeStaff, outside, branches],
  );
  const liveSegments = [
    [passed, "On track"],
    [0, "Review"],
    [outside, "Flagged"],
    [Math.max(activeStaff - checkedIn, 0), "Follow up"],
    [
      todayRecords.filter((record) => record.selfie_available).length,
      "Verified",
    ],
  ];

  const role = localStorage.getItem("hiwaga_role");
  const employeeId = localStorage.getItem("hiwaga_employee_id");
  const currentUser =
    authenticatedProfile ||
    staff.find(
      (person) =>
        String(person.employee_id) === String(employeeId) ||
        String(person.user_id) === String(employeeId),
    );
  const normalizedRole = getEffectiveCurrentRole(staff, role);
  const currentRoleKey = normalizedRole;
  const isStaffRole = ["staff", "employee", "team_member"].includes(currentRoleKey);
  isStaffRoleRef.current = isStaffRole;
  const isFloorManagerRole = ["floor_manager", "floor-manager", "floor manager"].includes(currentRoleKey);
  const isBranchManagerRole = ["branch_manager", "branch-manager", "branch manager"].includes(currentRoleKey);
  const isHrRole = ["hr", "human_resources", "human-resource", "human resource", "hr_manager"].includes(currentRoleKey);
  const isSuperAdminRole = ["super_admin", "super-admin", "super admin"].includes(currentRoleKey);
  const isAdminRole = isSuperAdminRole || isHrRole || isBranchManagerRole || isFloorManagerRole;
  const currentPermissions = useMemo(() => {
    const matchedRole = roleMetadata.find((roleItem) =>
      normalizeRoleKey(roleItem.value) === currentRoleKey ||
      normalizeRoleKey(roleItem.label) === currentRoleKey,
    );
    const rawPermissions = matchedRole?.permissions || [];
    return new Set(
      Array.isArray(rawPermissions)
        ? rawPermissions.map((permission) => normalizePermissionKey(permission))
        : [],
    );
  }, [roleMetadata, currentRoleKey]);
  const hasPermission = (permission) =>
    currentRoleKey === "super_admin" ||
    currentPermissions.has(normalizePermissionKey(permission));
  const canManageUsers =
    hasPermission("manage_staff") ||
    hasPermission("manage_users") ||
    hasPermission("view_all_staff") ||
    hasPermission("access_hr_portal") ||
    isSuperAdminRole ||
    isHrRole ||
    isBranchManagerRole ||
    isFloorManagerRole;
  const visibleTabs = useMemo(() => {
    let allowed = tabs;
    if (isSuperAdminRole || isHrRole) {
      const shiftsIndex = allowed.indexOf("Shifts");
      allowed = [...allowed];
      allowed.splice(shiftsIndex + 1, 0, "Departments", "Designations");
    }
    if (isSuperAdminRole) allowed = [...allowed, "Kiosk Accounts"];
    if (isBranchManagerRole || isFloorManagerRole) allowed = allowed.filter((tab) => tab !== "Branches");
    return allowed;
  }, [isBranchManagerRole, isFloorManagerRole, isHrRole, isSuperAdminRole]);

  useEffect(() => {
    if (activeTab !== "Dashboard" && activeTab !== "Settings" && !visibleTabs.includes(activeTab)) {
      setActiveTab("Attendance");
    }
  }, [activeTab, visibleTabs]);

  useEffect(() => {
    if ((isBranchManagerRole || isFloorManagerRole) && activeTab === "Branches") {
      setActiveTab("Attendance");
    }
  }, [activeTab, isBranchManagerRole, isFloorManagerRole]);

  useEffect(() => {
    if (isStaffRole && !["Dashboard", "Attendance", "Requests", "Announcements", "Settings"].includes(activeTab)) {
      setActiveNav("Home");
      setActiveTab("Dashboard");
    }
  }, [activeTab, isStaffRole]);

  const currentUserInitials = currentUser
    ? (currentUser.full_name || "")
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "AD";
  const currentUserName = currentUser?.full_name || "Admin";
  const globalSearchResults = useMemo(() => {
    const query = globalSearch.trim().toLowerCase();
    if (!query) return [];
    const pages = visibleTabs
      .filter((tab) => tab.toLowerCase().includes(query))
      .map((label) => ({ type: "Page", label, tab: label }));
    const people = staff
      .filter((person) =>
        `${person.full_name || ""} ${person.employee_code || ""}`
          .toLowerCase()
          .includes(query),
      )
      .slice(0, 5)
      .map((person) => ({
        type: "Employee",
        label: person.full_name || person.employee_code,
        detail: person.employee_code,
        tab: "Employees",
      }));
    const locations = branches
      .filter((branch) =>
        `${branch.name || ""} ${branch.branch_code || ""} ${branch.city || ""}`
          .toLowerCase()
          .includes(query),
      )
      .slice(0, 5)
      .map((branch) => ({
        type: "Branch",
        label: branch.name || branch.branch_code,
        detail: branch.city || branch.branch_code,
        tab: "Branches",
      }));
    return [...pages, ...people, ...locations].slice(0, 8);
  }, [globalSearch, staff, branches]);
  const selectGlobalSearchResult = (result) => {
    setActiveNav("HR PORTAL");
    setActiveTab(result.tab);
    setGlobalSearch("");
    setGlobalSearchOpen(false);
  };
  const handleNavigation = (label) => {
    setActiveNav(label);
    setActiveTab("Dashboard");
    setQuickAction("");
    setMobileOpen(false);
  };
  const handleTabNavigation = (tab) => {
    setActiveNav(tab === "Dashboard" ? "Home" : "HR PORTAL");
    setActiveTab(tab);
    setQuickAction("");
    setMobileOpen(false);
  };
  const handleInternalPageLink = (event, navigate) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate();
  };
  const mobileShortcutTabs = [
    { key: "Dashboard", label: "Home", icon: House },
    { key: "Attendance", label: "Attendance", icon: Clock3 },
    { key: "Requests", label: "Requests", icon: Send },
    { key: "Announcements", label: "Announcements", icon: Megaphone },
    { key: "Settings", label: "Profile", icon: UserRound },
  ];

  if (!token) {
    return (
      <LoginScreen
        code={loginCode}
        password={loginPassword}
        setCode={setLoginCode}
        setPassword={setLoginPassword}
        onSubmit={handleLogin}
        error={loginError}
        busy={loginBusy}
        showForgotPassword={showForgotPassword}
        setShowForgotPassword={setShowForgotPassword}
        forgotPasswordStep={forgotPasswordStep}
        forgotPasswordEmail={forgotPasswordEmail}
        setForgotPasswordEmail={setForgotPasswordEmail}
        forgotPasswordOtp={forgotPasswordOtp}
        setForgotPasswordOtp={setForgotPasswordOtp}
        forgotPasswordNewPassword={forgotPasswordNewPassword}
        setForgotPasswordNewPassword={setForgotPasswordNewPassword}
        forgotPasswordConfirmPassword={forgotPasswordConfirmPassword}
        setForgotPasswordConfirmPassword={setForgotPasswordConfirmPassword}
        forgotPasswordMessage={forgotPasswordMessage}
        setForgotPasswordMessage={setForgotPasswordMessage}
        onForgotPasswordSubmit={handleForgotPassword}
        onVerifyOtp={handleVerifyOtp}
        onResetPassword={handleResetPassword}
        forgotPasswordBusy={forgotPasswordBusy}
      />
    );
  }

  if (!sessionChecked) {
    return <div className="session-loading">Checking your session...</div>;
  }

  if (isStaffRole) {
    return (
      <StaffDashboard
        token={token}
        employeeId={localStorage.getItem("hiwaga_employee_id")}
        onLogout={handleLogout}
        onNavigate={(section) => {
          if (section === "Home") {
            setActiveNav("Home");
            setActiveTab("Dashboard");
          } else {
            setActiveNav("HR PORTAL");
            setActiveTab(section === "My profile" ? "Settings" : section);
          }
        }}
      />
    );
  }

  return (
    <div className="crm-shell">
      <aside className={`crm-sidebar ${mobileOpen ? "is-open" : ""}`} style={mobileOpen ? {zIndex: 1100, position: 'fixed'} : {}}>
        <div className="logo-row">
          <img 
            src="/hiwaga-logo.png" 
            alt="Hiwaga" 
            className="brand-logo" 
            onClick={() => handleNavigation("Home")}
            style={{ cursor: "pointer" }}
          />
          <button className="close-mobile" onClick={() => setMobileOpen(false)}>
            <X size={16} />
          </button>
        </div>
        <nav className="side-nav">
          {navItems.map(([Icon, label]) => (
            <a
              key={label}
              className={`side-item ${activeTab !== "Settings" && activeNav === label ? "selected" : ""}`}
              href={label === "Home" ? "/" : PAGE_PATH_BY_TAB[label] || "/"}
              onClick={(event) => handleInternalPageLink(event, () => handleNavigation(label))}
            >
              <Icon size={15} />
              {label}
            </a>
          ))}
          <div className="side-tabs" aria-label="Application sections">
            {visibleTabs.map((tab) => (
              <a
                key={tab}
                className={`side-item side-tab-item ${activeTab === tab ? "selected" : ""}`}
                href={PAGE_PATH_BY_TAB[tab] || "/"}
                onClick={(event) => handleInternalPageLink(event, () => handleTabNavigation(tab))}
              >
                {(() => {
                  const Icon = tabIcons[tab];
                  return <Icon size={15} />;
                })()}
                {tab}
              </a>
            ))}
          </div>
        </nav>
        <div className="side-bottom">
          <a
            className={`side-item ${activeTab === "Settings" ? "selected" : ""}`}
            href={PAGE_PATH_BY_TAB.Settings}
            onClick={(event) => handleInternalPageLink(event, handleProfileClick)}
          >
            <UserRound size={15} />
            My profile
          </a>
        </div>
      </aside>
      {mobileOpen && (
        <button
          className="screen-cover"
          onClick={() => setMobileOpen(false)}
          style={{zIndex: 1099, position: 'fixed', inset: 0}}
          aria-label="Close menu"
        />
      )}
      <main className="crm-main">
        <header className="crm-header">
          <button className="mobile-menu" onClick={() => setMobileOpen(true)}>
            <Menu size={19} />
          </button>
          <div className="search-box">
            <Activity size={15} />
            <input
              aria-label="Search anything"
              value={globalSearch}
              onChange={(event) => {
                setGlobalSearch(event.target.value);
                setGlobalSearchOpen(true);
              }}
              onFocus={() => setGlobalSearchOpen(true)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && globalSearchResults[0])
                  selectGlobalSearchResult(globalSearchResults[0]);
                if (event.key === "Escape") setGlobalSearchOpen(false);
              }}
              placeholder="Search employees, branches, pages..."
            />
            {globalSearchOpen && globalSearch.trim() && (
              <div className="global-search-results">
                {globalSearchResults.length ? (
                  globalSearchResults.map((result, index) => (
                    <button
                      type="button"
                      key={`${result.type}-${result.label}-${index}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => selectGlobalSearchResult(result)}
                    >
                      <span>{result.type}</span>
                      <strong>{result.label}</strong>
                      {result.detail && <small>{result.detail}</small>}
                    </button>
                  ))
                ) : (
                  <p>No matching employees, branches, or pages.</p>
                )}
              </div>
            )}
          </div>
          <div className="header-spacer" />
          <div className="account" ref={accountRef}>
            <button
              ref={accountTriggerRef}
              className="account-trigger"
              type="button"
              aria-label={`Open account menu for ${currentUserName}`}
              aria-haspopup="menu"
              aria-expanded={accountMenuOpen}
              onClick={() => {
                if (!accountMenuOpen) {
                  const anchor = accountTriggerRef.current?.getBoundingClientRect();
                  if (anchor) {
                    setAccountMenuPosition({
                      top: anchor.bottom + 8,
                      right: Math.max(8, window.innerWidth - anchor.right),
                    });
                  }
                }
                setAccountMenuOpen((open) => !open);
              }}
            >
              <b aria-hidden="true">{currentUser?.photo_url ? <img src={currentUser.photo_url} alt="" /> : currentUserInitials}</b>
              <span className="account-copy">
                <strong>{currentUserName}</strong>
                <small>{currentUser?.designation || "Team member"}</small>
              </span>
            </button>
            {accountMenuOpen && createPortal(
              <div
                className="account-menu"
                ref={accountMenuRef}
                style={{ position: "fixed", top: accountMenuPosition.top, right: accountMenuPosition.right, zIndex: 10000 }}
              >
                <button
                  className="account-menu-item"
                  type="button"
                  onClick={handleProfileClick}
                >
                  <UserRound size={14} />
                  Profile
                </button>
                <button
                  className="account-menu-item"
                  type="button"
                  onClick={handleChangePassword}
                >
                  <ShieldCheck size={14} />
                  Change password
                </button>
                <button
                  className="account-menu-item header-logout"
                  type="button"
                  onClick={handleLogout}
                >
                  <LogOut size={14} />
                  Sign out
                </button>
              </div>,
              document.body,
            )}
          </div>
        </header>
        <div className="page-content mobile-page-scroll">
          <div
            className={`page-heading ${activeTab === "Dashboard" && activeNav === "Home" ? "home-page-heading" : ""}`}
          >
            <div>
              <h1>
                {activeNav === "HR PORTAL"
                  ? activeTab === "Settings" ? "MY Profile" : activeTab
                  : `${activeNav} Dashboard`}
              </h1>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {activeNav === "HR PORTAL" && activeTab === "Branches" && !isBranchManagerRole && (
                <button
                  className="branches-add-btn"
                  onClick={() => {
                    setQuickAction("branch");
                    setTimeout(() => setQuickAction(""), 100);
                  }}
                >
                  <Plus size={16} />
                  Add branch
                </button>
              )}
              {!(activeNav === "HR PORTAL" && activeTab === "Branches") && (
                <button
                  className="sync"
                  type="button"
                  aria-label="Refresh dashboard"
                  title="Refresh dashboard"
                  onClick={() => {
                    setRefreshing(true);
                    window.location.reload();
                  }}
                  disabled={refreshing}
                />
              )}
            </div>
          </div>
          {activeTab !== "Dashboard" ? (
            <AdminView
              tab={activeTab}
              branches={branches}
              staff={staff}
              attendance={attendance}
              departments={departments}
              designations={designations}
              token={token}
              onLogout={handleLogout}
              onChangePassword={handleChangePassword}
              quickAction={quickAction}
              currentUser={currentUser}
              onAttendanceRefresh={async () => {
                const records = await Promise.all(
                  branches.map((branch) =>
                    getJson(`/attendance/branch/${branch.branch_id}`, token).catch(() => []),
                  ),
                );
                setAttendance(records.flat());
              }}
              currentUserName={currentUserName}
              onProfileUpdate={setAuthenticatedProfile}
              onDepartmentsChange={setDepartments}
              onDesignationsChange={setDesignations}
              onBranchesChange={(updatedBranch) => {
                setBranches((current) => {
                  const exists = current.some((branch) => branch.branch_id === updatedBranch.branch_id);
                  return exists
                    ? current.map((branch) => branch.branch_id === updatedBranch.branch_id ? updatedBranch : branch)
                    : [...current, updatedBranch];
                });
              }}
            />
          ) : activeNav === "Home" ? (
            <HomeDashboard
              currentUser={currentUser}
              staff={staff}
              branches={branches}
              attendance={attendance}
              token={token}
              onAddStaff={() => {
                setActiveNav("HR PORTAL");
                setActiveTab("Employees");
                setQuickAction("");
              }}
              onAddBranch={() => {
                setActiveNav("HR PORTAL");
                setActiveTab("Branches");
                setQuickAction("");
              }}
              onOpenRequest={(requestId) => {
                setActiveNav("HR PORTAL");
                setActiveTab("Requests");
                setQuickAction("");
              }}
            />
          ) : (
            <AttendanceDashboard
              staff={staff}
              branches={branches}
              liveMetrics={liveMetrics}
              liveSegments={liveSegments}
              isLive={isLive}
              checkedIn={checkedIn}
            />
          )}
        </div>
        <nav className="mobile-bottom-nav" aria-label="Mobile shortcuts">
          {mobileShortcutTabs.map(({ key, label, icon: Icon }) => {
            const isSelected =
              key === "Dashboard"
                ? activeTab === "Dashboard" && activeNav === "Home"
                : activeTab === key;
            const handleClick = () => {
              if (key === "Dashboard") {
                setActiveNav("Home");
                setActiveTab("Dashboard");
              } else {
                handleTabNavigation(key);
              }
              setQuickAction("");
            };
            return (
              <a
                key={key}
                className={`mobile-bottom-item ${isSelected ? "selected" : ""}`}
                href={PAGE_PATH_BY_TAB[key] || "/"}
                onClick={(event) => handleInternalPageLink(event, handleClick)}
                aria-current={isSelected ? "page" : undefined}
              >
                <Icon size={16} />
                <span>{label}</span>
              </a>
            );
          })}
        </nav>
      </main>
      {showForgotPassword && (
        <div className="modal-backdrop settings-password-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setShowForgotPassword(false); }}>
          <form className="settings-password-modal" onSubmit={submitChangePassword}>
            <div className="settings-password-heading"><div><span className="eyebrow">ACCOUNT SECURITY</span><h2>Change password</h2><p>Enter your current password, then choose a new one.</p></div><button type="button" className="modal-close" aria-label="Close" onClick={() => setShowForgotPassword(false)}><X size={18} /></button></div>
            <label className="settings-password-field">Current password<input type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required /></label>
            <label className="settings-password-field">New password<input type="password" minLength={8} autoComplete="new-password" value={changePasswordValue} onChange={(event) => setChangePasswordValue(event.target.value)} required /></label>
            <label className="settings-password-field">Confirm new password<input type="password" minLength={8} autoComplete="new-password" value={confirmChangePassword} onChange={(event) => setConfirmChangePassword(event.target.value)} required /></label>
            {changePasswordMessage && <p className="settings-password-message" role="status">{changePasswordMessage}</p>}
            <div className="settings-password-actions"><button type="button" className="outline-action" onClick={() => setShowForgotPassword(false)}>Cancel</button><button className="primary-action" disabled={changePasswordBusy}>{changePasswordBusy ? "Updating-" : "Update password"}</button></div>
          </form>
        </div>
      )}
    </div>
  );
}

function LoginScreen({
  code,
  password,
  setCode,
  setPassword,
  onSubmit,
  error,
  busy,
  showForgotPassword,
  setShowForgotPassword,
  forgotPasswordStep,
  forgotPasswordEmail,
  setForgotPasswordEmail,
  forgotPasswordOtp,
  setForgotPasswordOtp,
  forgotPasswordNewPassword,
  setForgotPasswordNewPassword,
  forgotPasswordConfirmPassword,
  setForgotPasswordConfirmPassword,
  forgotPasswordMessage,
  setForgotPasswordMessage,
  onForgotPasswordSubmit,
  onVerifyOtp,
  onResetPassword,
  forgotPasswordBusy,
}) {
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  if (showForgotPassword) {
    const stepTitle =
      forgotPasswordStep === "otp"
        ? "Verify OTP"
        : forgotPasswordStep === "reset"
          ? "Set new password"
          : "Reset your password";

    const stepCopy =
      forgotPasswordStep === "otp"
        ? "Enter the 6-digit code sent to your email to continue."
        : forgotPasswordStep === "reset"
          ? "Create a new password for your account."
          : "Enter your email address and we will send you a password reset OTP.";

    const closeForgotPassword = () => {
      setShowForgotPassword(false);
      setForgotPasswordEmail("");
      setForgotPasswordOtp("");
      setForgotPasswordNewPassword("");
      setForgotPasswordConfirmPassword("");
      setForgotPasswordMessage("");
    };

    return (
      <div className="login-screen">
        <div className="login-card-simple">
          <div className="simple-logo-wrap">
            <img src="/hiwaga-logo.png" alt="Hiwaga logo" className="simple-logo" />
          </div>

          {forgotPasswordStep !== "reset" && (
            <>
              <h1 className="login-title-simple">{stepTitle}</h1>
              <p className="login-copy-simple">{stepCopy}</p>
            </>
          )}

          {forgotPasswordStep === "request" && (
            <form onSubmit={onForgotPasswordSubmit} className="login-form-simple forgot-password-form">
              <label className="login-field-simple forgot-password-field">
                <span>Email Address</span>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(event) => setForgotPasswordEmail(event.target.value)}
                  placeholder="your.email@example.com"
                  required
                />
              </label>
              {forgotPasswordMessage && (
                <p className={forgotPasswordMessage.toLowerCase().includes("sent") || forgotPasswordMessage.toLowerCase().includes("otp") ? "login-success" : "login-error"}>
                  {forgotPasswordMessage}
                </p>
              )}
              <button className="login-submit" disabled={forgotPasswordBusy}>
                {forgotPasswordBusy ? "Sending..." : "Send OTP"}
              </button>
            </form>
          )}

          {forgotPasswordStep === "otp" && (
            <form onSubmit={onVerifyOtp} className="login-form-simple forgot-password-form">
              <label className="login-field-simple forgot-password-field">
                <span>Email Address</span>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(event) => setForgotPasswordEmail(event.target.value)}
                  placeholder="your.email@example.com"
                  required
                />
              </label>
              <label className="login-field-simple forgot-password-field">
                <span>Enter 6-digit OTP</span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={forgotPasswordOtp}
                  onChange={(event) => setForgotPasswordOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  required
                />
              </label>
              {forgotPasswordMessage && (
                <p className={forgotPasswordMessage.toLowerCase().includes("verified") || forgotPasswordMessage.toLowerCase().includes("success") ? "login-success" : "login-error"}>
                  {forgotPasswordMessage}
                </p>
              )}
              <button className="login-submit" disabled={forgotPasswordBusy}>
                {forgotPasswordBusy ? "Verifying..." : "Verify OTP"}
              </button>
              <button
                type="button"
                className="forgot-password-link"
                onClick={(event) => {
                  event.preventDefault();
                  onForgotPasswordSubmit(event);
                }}
                disabled={forgotPasswordBusy}
              >
                Resend OTP
              </button>
            </form>
          )}

          {forgotPasswordStep === "reset" && (
            <form onSubmit={onResetPassword} className="login-form-simple forgot-password-form">
              <label className="login-field-simple forgot-password-field">
                <span>New Password</span>
                <input
                  type="password"
                  value={forgotPasswordNewPassword}
                  onChange={(event) => setForgotPasswordNewPassword(event.target.value)}
                  placeholder="At least 8 characters"
                  required
                />
              </label>
              <label className="login-field-simple forgot-password-field">
                <span>Confirm Password</span>
                <input
                  type="password"
                  value={forgotPasswordConfirmPassword}
                  onChange={(event) => setForgotPasswordConfirmPassword(event.target.value)}
                  placeholder="Re-enter password"
                  required
                />
              </label>
              {forgotPasswordMessage && (
                <p className={forgotPasswordMessage.toLowerCase().includes("success") ? "login-success" : "login-error"}>
                  {forgotPasswordMessage}
                </p>
              )}
              <button className="login-submit" disabled={forgotPasswordBusy}>
                {forgotPasswordBusy ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          )}

          <button
            type="button"
            className="forgot-password-link back-to-signin"
            onClick={closeForgotPassword}
          >
            ? Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-screen">
      <div className="login-card-simple">
        <div className="simple-logo-wrap">
          <img src="/hiwaga-logo.png" alt="Hiwaga logo" className="simple-logo" />
        </div>

        <form onSubmit={onSubmit} className="login-form-simple">
          <label className="login-field-simple">
            <span>Email or mobile</span>
            <input
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="Enter your email or mobile"
              inputMode="email"
              autoCapitalize="none"
              autoComplete="username"
              required
            />
          </label>

          <label className="login-field-simple">
            <span>Password</span>
            <div className="login-password-wrap">
              <input
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                type={showLoginPassword ? "text" : "password"}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowLoginPassword((visible) => !visible)}
                aria-label={showLoginPassword ? "Hide password" : "Show password"}
                aria-pressed={showLoginPassword}
              >
                {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </label>

          <div className="login-meta-row single-action">
            <button
              type="button"
              className="forgot-password-link"
              onClick={() => setShowForgotPassword(true)}
            >
              Forgot password?
            </button>
          </div>

          {error && <p className="login-error">{error}</p>}

          <button className="login-submit" disabled={busy} type="submit">
            {busy ? "Logging in..." : "Login"}
          </button>
        </form>
        <a className="kiosk-login-entry-link" href="/kiosk">Kiosk sign in</a>
      </div>
    </div>
  );
}

function UserDashboard({ token, employeeId, onLogout }) {
  const [records, setRecords] = useState([]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [selfie, setSelfie] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [checkType, setCheckType] = useState("in");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [selectedShiftId, setSelectedShiftId] = useState("");
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStreamRef = useRef(null);

  useEffect(() => {
    getJson(`/attendance/employee/${employeeId}/history`, token)
      .then(setRecords)
      .catch(() => setMessage("Unable to load attendance history."));
    getJson(`/staff/${employeeId}`, token)
      .then(setEmployeeProfile)
      .catch(() => setEmployeeProfile(null));
  }, [employeeId, token]);
  useEffect(() => {
    const branchId = employeeProfile?.branch_id;
    if (!branchId) {
      setShifts([]);
      setSelectedShiftId("");
      return undefined;
    }
    let ignore = false;
    getJson(`/shifts/branch/${branchId}`, token)
      .then((data) => {
        if (ignore) return;
        const activeShifts = Array.isArray(data) ? data : [];
        setShifts(activeShifts);
        setSelectedShiftId((current) =>
          activeShifts.some((shift) => String(shift.shift_id) === String(current))
            ? current
            : "",
        );
      })
      .catch(() => {
        if (!ignore) {
          setShifts([]);
          setSelectedShiftId("");
        }
      });
    return () => {
      ignore = true;
    };
  }, [employeeProfile?.branch_id, token]);
  useEffect(() => {
    const video = videoRef.current;
    const stream = cameraStreamRef.current;
    if (cameraOpen && video && stream) {
      video.srcObject = stream;
      video.muted = true;
      video.setAttribute("playsinline", "true");
      video.play().catch(() => {});
    }
  }, [cameraOpen]);

  useEffect(() => {
    return () => {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const getEffectiveAttendanceStart = (person) => {
    if (!person) return null;
    const candidate =
      person.date_of_joining ||
      person.created_at ||
      person.createdAt ||
      person.created_date ||
      person.joining_date;
    if (!candidate) return null;
    const parsed = new Date(candidate);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  };

  const attendanceStartDate = getEffectiveAttendanceStart(employeeProfile);
  const today = records.filter(
    (record) =>
      parseUTC(record.timestamp).toDateString() === new Date().toDateString(),
  );
  const checkedInToday = today.some((record) => record.check_type === "in");
  const checkedOutToday = today.some((record) => record.check_type === "out");
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const canMarkAttendance = !attendanceStartDate || todayStart >= attendanceStartDate;

  const stopCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraOpen(false);
  };

  const openCamera = async () => {
    stopCamera();

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setMessage(
        "Camera access is not supported here. Open this page on localhost or HTTPS and allow camera access.",
      );
      return;
    }

    const tryStream = async (constraints) => {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      cameraStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.setAttribute("playsinline", "true");
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().catch(() => {});
        };
      }
      setCameraOpen(true);
      setMessage("");
      return stream;
    };

    try {
      await tryStream({ video: { facingMode: "user" }, audio: false });
    } catch (error) {
      try {
        await tryStream({ video: true, audio: false });
      } catch (fallbackError) {
        setMessage(
          "Camera permission was denied or the camera is unavailable. Allow camera access in the browser and try again.",
        );
      }
    }
  };

  const captureCamera = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `selfie-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        if (selfiePreview) URL.revokeObjectURL(selfiePreview);
        const preview = URL.createObjectURL(blob);
        setSelfie(file);
        setSelfiePreview(preview);
        stopCamera();
        setMessage("");
      },
      "image/jpeg",
      0.92,
    );
  };

  const handleSelfieSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (selfiePreview) URL.revokeObjectURL(selfiePreview);
    const preview = URL.createObjectURL(file);
    setSelfie(file);
    setSelfiePreview(preview);
    setMessage("");
  };

  const submitCheck = async (event) => {
    event.preventDefault();
    if (!selfie) {
      setMessage("Please capture a selfie before checking in or out.");
      return;
    }
    if (checkType === "in" && !selectedShiftId) {
      setMessage("Please select a shift before checking in.");
      return;
    }
    if (attendanceStartDate && todayStart < attendanceStartDate) {
      setMessage(
        `Attendance marking starts from ${attendanceStartDate.toLocaleDateString()}.`,
      );
      return;
    }
    setBusy(true);
    setMessage("Getting your location...");
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const body = new FormData();
          body.append("latitude", position.coords.latitude.toFixed(2));
          body.append("longitude", position.coords.longitude.toFixed(2));
          body.append("device_info", navigator.userAgent);
          if (checkType === "in") body.append("shift_id", selectedShiftId);
          body.append("selfie", selfie);
          const response = await fetch(
            `${API_URL}/attendance/${checkType === "in" ? "checkin" : "checkout"}`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
              body,
            },
          );
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            setMessage(
              data.detail ||
                `Attendance failed (${response.status}). Check backend logs.`,
            );
          } else {
            setMessage(data.message || "Attendance recorded successfully.");
            setRecords((current) => [data, ...current]);
            setSelfie(null);
            setSelfiePreview("");
            if (fileInputRef.current) fileInputRef.current.value = "";
          }
        } catch (error) {
          setMessage(
            `Cannot reach attendance API at ${API_URL}. Start FastAPI and check CORS.`,
          );
        } finally {
          setBusy(false);
        }
      },
      () => {
        setMessage("Location permission is required for attendance.");
        setBusy(false);
      },
    );
  };
  const previewSelfie = async (recordId) => {
    const response = await fetch(`${API_URL}/attendance/${recordId}/selfie`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json().catch(() => ({}));
    setMessage(response.ok ? "" : data.detail || "Selfie preview unavailable");
    if (response.ok) setPreviewUrl(data.preview_url);
  };
  return (
    <div className="user-shell">
      <header className="user-header">
        <div className="login-brand">
          <img src="/hiwaga-logo.png" alt="Hiwaga" className="brand-logo" />
        </div>
        <div className="user-header-actions">
          <span>My attendance</span>
          <button className="outline-action" onClick={onLogout}>
            Sign out
          </button>
        </div>
      </header>
      <main className="user-main">
        <div className="user-welcome">
          <p className="eyebrow">EMPLOYEE PORTAL</p>
          <h1>My attendance</h1>
          <p>
            Record your attendance with a location check and selfie
            verification.
          </p>
        </div>
        <div className="user-grid">
          <section className="user-card user-check-card">
            <div className="user-card-title">
              <div>
                <p className="eyebrow">TODAY</p>
                <h2>Mark attendance</h2>
              </div>
              <span className="user-date">
                {new Date().toLocaleDateString()}
              </span>
            </div>
            <div className="check-toggle">
              <button
                className={checkType === "in" ? "active" : ""}
                onClick={() => setCheckType("in")}
                disabled={!canMarkAttendance}
              >
                Check in
              </button>
              <button
                className={checkType === "out" ? "active" : ""}
                onClick={() => setCheckType("out")}
                disabled={!canMarkAttendance}
              >
                Check out
              </button>
            </div>
            {!canMarkAttendance && attendanceStartDate && (
              <p className="form-message">
                Attendance marking is available from {attendanceStartDate.toLocaleDateString()}.
              </p>
            )}
            <form onSubmit={submitCheck} className="check-form">
              {checkType === "in" && (
                <label className="shift-selection-field">
                  <span>Work shift</span>
                  <select
                    value={selectedShiftId}
                    onChange={(event) => setSelectedShiftId(event.target.value)}
                    disabled={!canMarkAttendance || busy || !shifts.length}
                    required
                  >
                    <option value="">
                      {shifts.length ? "Select your shift" : "No active shifts available"}
                    </option>
                    {shifts.map((shift) => (
                      <option key={shift.shift_id} value={shift.shift_id}>
                        {shift.name} ({String(shift.start_time).slice(0, 5)} - {String(shift.end_time).slice(0, 5)})
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <div className="selfie-capture">
                <label className="selfie-label">Verification selfie</label>
                <button
                  type="button"
                  className="camera-trigger"
                  onClick={openCamera}
                  disabled={!canMarkAttendance}
                >
                  Open camera
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleSelfieSelect}
                  hidden
                />
              </div>
              {selfiePreview && (
                <div className="selfie-preview-box">
                  <img src={selfiePreview} alt="Selfie preview" />
                  <div className="selfie-preview-actions">
                    <button
                      type="button"
                      className="outline-action"
                      onClick={() => {
                        if (selfiePreview) URL.revokeObjectURL(selfiePreview);
                        setSelfiePreview("");
                        setSelfie(null);
                        if (fileInputRef.current)
                          fileInputRef.current.value = "";
                        stopCamera();
                        openCamera();
                      }}
                    >
                      Retake
                    </button>
                    <button
                      type="submit"
                      className="primary-action"
                      disabled={busy || !canMarkAttendance}
                    >
                      {busy
                        ? "Submitting..."
                        : checkType === "in"
                          ? "Check in now"
                          : "Check out now"}
                    </button>
                  </div>
                </div>
              )}
              {!selfiePreview && (
                <button
                  className="primary-action"
                  type="submit"
                  disabled={busy || !canMarkAttendance}
                >
                  {busy
                    ? "Submitting..."
                    : checkType === "in"
                      ? "Check in now"
                      : "Check out now"}
                </button>
              )}
            </form>
            {message && <p className="form-message">{message}</p>}
            {cameraOpen && (
              <div className="camera-modal">
                <div className="camera-panel">
                  <div className="camera-header">
                    <strong>Take selfie</strong>
                    <button
                      type="button"
                      className="modal-close"
                      onClick={stopCamera}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <video ref={videoRef} autoPlay playsInline muted />
                  <canvas ref={canvasRef} style={{ display: "none" }} />
                  <div className="camera-actions">
                    <button
                      type="button"
                      className="outline-action"
                      onClick={stopCamera}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="primary-action"
                      onClick={captureCamera}
                    >
                      Capture
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>
          <section className="user-card">
            <div className="user-card-title">
              <div>
                <p className="eyebrow">TODAY'S ACTIVITY</p>
                <h2>
                  {today.length
                    ? `${today.length} attendance record${today.length > 1 ? "s" : ""}`
                    : "No attendance yet"}
                </h2>
              </div>
              <Clock3 size={20} color="#3978b8" />
            </div>
            <div className="user-status-list">
              {today.length ? (
                today.map((record) => (
                  <div className="user-status" key={record.id}>
                    <span
                      className={
                        record.geofence_status === "pass"
                          ? "status-good"
                          : "status-warning"
                      }
                    >
                      {record.check_type === "in"
                        ? "Checked in"
                        : "Checked out"}
                    </span>
                    <span>
                      {parseUTC(record.timestamp).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    <small>
                      {record.geofence_status === "pass"
                        ? "Within branch geofence"
                        : "Outside geofence - flagged"}
                    </small>
                  </div>
                ))
              ) : (
                <p className="empty-user">
                  Your check-in and check-out activity will appear here.
                </p>
              )}
            </div>
          </section>
        </div>
        <section className="user-card history-card">
          <div className="user-card-title">
            <div>
              <p className="eyebrow">HISTORY</p>
              <h2>Recent attendance</h2>
            </div>
          </div>
          <div className="history-list">
            {records.slice(0, 10).map((record) => (
              <div className="history-row" key={record.id}>
                <strong>
                  {parseUTC(record.timestamp).toLocaleDateString()}
                </strong>
                <span>
                  {record.check_type === "in" ? "Check in" : "Check out"}
                </span>
                <span>{record.geofence_status}</span>
                <small>
                  {record.distance_from_branch_m
                    ? `${Math.round(record.distance_from_branch_m)}m from branch`
                    : "Location recorded"}
                </small>
                <button
                  className="preview-button"
                  onClick={() => previewSelfie(record.id)}
                  disabled={!record.selfie_available}
                >
                  Preview selfie
                </button>
              </div>
            ))}
          </div>
        </section>
        {previewUrl && (
          <div className="selfie-preview">
            <button onClick={() => setPreviewUrl("")}>Close</button>
            <img src={previewUrl} alt="Attendance selfie" />
          </div>
        )}
      </main>
    </div>
  );
}

function ReportsCenter({ branches, staff, token }) {
  const [branchId, setBranchId] = useState(() => branches[0]?.branch_id || "");
  const [employeeId, setEmployeeId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!branches.some((branch) => String(branch.branch_id) === String(branchId))) {
      setBranchId(branches[0]?.branch_id || "");
    }
  }, [branches, branchId]);

  async function exportAttendance() {
    if (!token || !branchId || exporting) return;
    setExporting(true);
    setMessage("");
    try {
      const query = new URLSearchParams();
      if (fromDate) query.set("from_date", new Date(fromDate).toISOString());
      if (toDate) query.set("to_date", new Date(toDate).toISOString());
      if (employeeId) query.set("employee_id", employeeId);
      const queryString = query.toString();
      const response = await apiFetch(
        `/attendance/export/${branchId}${queryString ? `?${queryString}` : ""}`,
        {},
        token,
      );
      if (!response.ok) {
        const detail = await response.text().catch(() => "");
        throw new Error(detail || `Export failed (${response.status})`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const branch = branches.find((item) => String(item.branch_id) === String(branchId));
      const employee = staff.find((item) => String(item.employee_id) === String(employeeId));
      const safeName = (branch?.name || "branch").trim().replace(/[^a-z0-9]+/gi, "_");
      link.href = url;
      const employeeName = employee ? `_${(employee.full_name || "employee").trim().replace(/[^a-z0-9]+/gi, "_")}` : "";
      link.download = `attendance_${safeName}${employeeName}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setMessage("Your attendance report has been downloaded.");
    } catch (error) {
      setMessage(error.message || "Unable to export the attendance report.");
    } finally {
      setExporting(false);
    }
  }

  return (
    <section className="admin-view panel reports-center">
      <div className="reports-intro">
        <div>
          <h2>Attendance reports</h2>
          <p className="reports-description">Choose a branch and optional date range for your attendance spreadsheet.</p>
        </div>
      </div>
      <div className="report-row reports-export-row">
        <div className="report-icon"><FileText size={21} /></div>
        <div className="report-copy">
          <strong>Branch attendance</strong>
          <small>Export detailed employee attendance records with check-in and check-out times for the selected employees and dates.</small>
        </div>
        <div className="report-branch-picker">
          <span>Branch</span>
          <SelectFilter
            value={String(branchId)}
            onChange={(value) => { setBranchId(value); setEmployeeId(""); }}
            placeholder="Select branch"
            options={branches.map((branch) => ({
              value: String(branch.branch_id),
              label: branch.name || `Branch ${branch.branch_id}`,
              icon: <MapPin size={15} />,
            }))}
          />
        </div>
        <div className="report-branch-picker">
          <span>Employee <em>Optional</em></span>
          <SelectFilter
            value={employeeId}
            onChange={setEmployeeId}
            placeholder="All employees"
            options={[
              { value: "", label: "All employees", icon: <Users size={15} /> },
              ...staff
                .filter((person) => String(person.branch_id) === String(branchId))
                .map((person) => ({
                  value: String(person.employee_id),
                  label: person.full_name || person.employee_id,
                  icon: <UserRound size={15} />,
                })),
            ]}
          />
        </div>
        <div className="report-date-picker">
          <CustomDateField
            label="From date"
            value={fromDate}
            onChange={setFromDate}
            maxDate={toDate || new Date().toISOString().split('T')[0]}
            placeholder="dd/mm/yyyy"
          />
        </div>
        <div className="report-date-picker">
          <CustomDateField
            label="To date"
            value={toDate}
            onChange={setToDate}
            maxDate={new Date().toISOString().split('T')[0]}
            placeholder="dd/mm/yyyy"
          />
        </div>
      </div>
      <div className="report-actions">
        <button className="primary-action report-download" onClick={exportAttendance} disabled={!branchId || exporting}>
          <Download size={16} /> {exporting ? "Preparing-" : "Download XLSX"}
        </button>
      </div>
      {message && <p className={`report-feedback ${message.startsWith("Your") ? "success" : "error"}`} role="status">{message}</p>}
      <div className="reports-note"><ShieldCheck size={17} /><span>Date and employee filters are optional. Choose an employee to export only their attendance, or leave it on All employees for the whole branch.</span></div>
    </section>
  );
}

function RequestsPage({ token, currentUserName: propCurrentUserName, branches = [] }) {
  const role = normalizeRoleKey(localStorage.getItem("hiwaga_role") || "");
  const canReview = ["super_admin", "branch_manager", "floor_manager", "hr"].includes(role);
  const canApplyRequests = ["super_admin", "staff", "employee", "team_member", "branch_manager", "floor_manager", "hr"].includes(role);
  const [requests, setRequests] = useState([]);
  const [requestScope, setRequestScope] = useState(canReview ? "team" : "self");
  const [requestSearch, setRequestSearch] = useState("");
  const [requestStatus, setRequestStatus] = useState("pending");
  const [requestPeriod, setRequestPeriod] = useState("this_month");
  const [requestTypeFilter, setRequestTypeFilter] = useState("all");
  const [requestBranchFilter, setRequestBranchFilter] = useState("");
  const [requestCustomFrom, setRequestCustomFrom] = useState("");
  const [requestCustomTo, setRequestCustomTo] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectingRequest, setRejectingRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionError, setRejectionError] = useState("");
  const [requestStaff, setRequestStaff] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(12);
  const [reportingToId, setReportingToId] = useState("");
  const [requestType, setRequestType] = useState("leave");
  const [leaveType, setLeaveType] = useState("");
  const [permissionType, setPermissionType] = useState("early_logout");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [permissionDate, setPermissionDate] = useState("");
  const [arrivalTime, setArrivalTime] = useState("");
  const [fromTimePeriod, setFromTimePeriod] = useState("AM");
  const [toTimePeriod, setToTimePeriod] = useState("AM");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const closeRequestDetails = () => {
    setSelectedRequest(null);
    if (/^\/requests\/[^/]+\/?$/i.test(window.location.pathname)) navigateToPath("/requests");
  };
  useEffect(() => {
    const syncRequestRoute = () => {
      const match = window.location.pathname.match(/^\/requests\/([^/]+)\/?$/i);
      const requestId = match ? decodeURIComponent(match[1]) : "";
      setSelectedRequest(requestId ? requests.find((request) => String(request.id) === requestId) || null : null);
    };
    syncRequestRoute();
    window.addEventListener("popstate", syncRequestRoute);
    return () => window.removeEventListener("popstate", syncRequestRoute);
  }, [requests]);
  
  // Time formatting helper
  const formatTimeInput = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Auto-format with colon
    if (digits.length === 0) return '';
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  };
  const convertPermissionTime = (value, period) => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours < 1 || hours > 12 || minutes > 59) return null;
    const hours24 = (hours % 12) + (period === "PM" ? 12 : 0);
    return `${String(hours24).padStart(2, "0")}:${match[2]}`;
  };
  
  // Get current user info from localStorage (same logic as main app)
  const employeeId = localStorage.getItem("hiwaga_employee_id");
  const currentUser = requestStaff.find(
    (person) =>
      String(person.employee_id) === String(employeeId) ||
      String(person.user_id) === String(employeeId) ||
      String(person.employee_code) === String(employeeId)
  );
  // Use the name passed from parent (top-right profile) or fallback to local lookup
  const currentUserName = propCurrentUserName || currentUser?.full_name || "User";

  const loadRequests = async () => {
    const response = await apiFetch(requestScope === "team" && canReview ? "/requests" : "/requests/me", {}, token);
    const data = await response.json().catch(() => []);
    if (!response.ok) throw new Error(normalizeErrorMessage(data, "Unable to load requests."));
    const requestList = Array.isArray(data) ? data : [];
    
    console.log('Raw requests from API:', requestList.length, 'requests');
    console.log('Staff list for enrichment:', requestStaff.length, 'staff members');
    
    // Enrich each request with employee details from staff
    const enrichedRequests = requestList.map(req => {
      // Start with API data (it already has full_name)
      let employeeFullName = req.full_name;
      let employeeDesignation = req.designation || null;
      let employeeBranch = req.branch_name || null;
      
      console.log('Enriching request:', req.id, 'employee_id:', req.employee_id, 'has full_name from API:', !!req.full_name);
      
      // If API doesn't have full_name, try to find it in staff array
      if (!employeeFullName) {
        // Check if this is the current user's request
        const isCurrentUser = currentUser && (
          String(currentUser.employee_id) === String(req.employee_id) ||
          String(currentUser.user_id) === String(req.employee_id) ||
          String(currentUser.employee_code) === String(req.employee_id) ||
          String(currentUser.employee_code) === String(req.employee_code)
        );
        
        if (isCurrentUser) {
          employeeFullName = currentUserName;
          employeeDesignation = currentUser.designation;
          employeeBranch = currentUser.branch_name;
          console.log('  -> Matched current user:', employeeFullName);
        } else {
          // Find other employee in staff array
          const employee = requestStaff.find(staff => 
            staff.employee_id === req.employee_id || 
            staff.employee_code === req.employee_id ||
            staff.employee_code === req.employee_code
          );
          
          if (employee) {
            employeeFullName = employee.full_name;
            employeeDesignation = employee.designation;
            employeeBranch = employee.branch_name;
            console.log('  -> Found in staff:', employeeFullName);
          } else {
            console.log('  -> NOT FOUND in staff array');
          }
        }
      }
      
      // Return request with employee details merged in
      return {
        ...req,
        employee_full_name: employeeFullName,
        employee_designation: employeeDesignation,
        employee_branch: employeeBranch,
        employee_branch_id: req.branch_id || null,
      };
    });
    
    setRequests(enrichedRequests);
  };

  useEffect(() => {
    loadRequests().catch((loadError) => setError(loadError.message));
  }, [token, canReview, requestScope, requestStaff]);

  useEffect(() => {
    let ignore = false;
    if (!token) return undefined;
    getJson("/staff", token)
      .then((data) => {
        if (!ignore) setRequestStaff(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!ignore) setRequestStaff([]);
      });
    return () => { ignore = true; };
  }, [token]);

  const reportingPeople = requestStaff.filter((person) => ["branch_manager", "floor_manager", "hr", "super_admin"].includes(normalizeRoleKey(person.role_code || person.role || person.user_role)));
  const personLabel = (person) => person.full_name || person.employee_code || person.employee_id || "Employee";

  const getRequestTime = (request) => {
    const timestamp = new Date(request.created_at || request.permission_date || request.from_date || 0).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  };
  const filteredRequests = requests.filter((request) => {
    const type = request.request_type === "leave" ? "leave" : "permission";
    const employeeName = request.full_name || request.employee_name || request.name || request.employee_id || "";
    const requestDate = new Date(request.created_at || request.from_date || request.permission_date || 0);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const requestDay = new Date(requestDate.getFullYear(), requestDate.getMonth(), requestDate.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - 6);
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(thisMonthStart);
    lastMonthEnd.setDate(lastMonthEnd.getDate() - 1);
    const thisYearStart = new Date(now.getFullYear(), 0, 1);
    const lastYearStart = new Date(now.getFullYear() - 1, 0, 1);
    const lastYearEnd = new Date(thisYearStart);
    lastYearEnd.setDate(lastYearEnd.getDate() - 1);
    const customFrom = requestCustomFrom ? new Date(`${requestCustomFrom}T00:00:00`) : null;
    const customTo = requestCustomTo ? new Date(`${requestCustomTo}T23:59:59`) : null;
    const matchesStatus = requestStatus === "all" || String(request.status || "").toLowerCase() === requestStatus;
    const matchesSearch = !requestSearch.trim() || employeeName.toLowerCase().includes(requestSearch.trim().toLowerCase());
    const matchesType = requestTypeFilter === "all" 
      || (requestTypeFilter === "leave" && type === "leave")
      || (requestTypeFilter === "permission" && type === "permission");
    const matchesBranch = !requestBranchFilter || String(request.branch_id || request.employee_branch_id || "") === requestBranchFilter;
    const matchesPeriod = requestPeriod === "all"
      || (requestPeriod === "today" && requestDay.getTime() === today.getTime())
      || (requestPeriod === "yesterday" && requestDay.getTime() === yesterday.getTime())
      || (requestPeriod === "last_7_days" && requestDay >= weekStart && requestDay <= today)
      || (requestPeriod === "this_month" && requestDate >= thisMonthStart)
      || (requestPeriod === "last_month" && requestDate >= lastMonthStart && requestDate <= lastMonthEnd)
      || (requestPeriod === "this_year" && requestDate >= thisYearStart)
      || (requestPeriod === "last_year" && requestDate >= lastYearStart && requestDate <= lastYearEnd)
      || (requestPeriod === "custom" && customFrom && customTo && requestDate >= customFrom && requestDate <= customTo);
    return matchesStatus && matchesSearch && matchesType && matchesBranch && matchesPeriod;
  }).sort((first, second) => getRequestTime(second) - getRequestTime(first));
  
  // Pagination logic
  const totalRequests = filteredRequests.length;
  const totalPages = Math.ceil(totalRequests / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedRequests = filteredRequests.slice(startIndex, endIndex);
  
  // Reset to page 1 when filters change
  const filterKey = `${requestStatus}-${requestSearch}-${requestTypeFilter}-${requestBranchFilter}-${requestPeriod}-${requestCustomFrom}-${requestCustomTo}`;
  const prevFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKeyRef.current !== filterKey) {
      setCurrentPage(1);
      prevFilterKeyRef.current = filterKey;
    }
  }, [filterKey]);

  const renderRequestCard = (request) => {
    // Always show employee name for team view, show "My Request" only for self view
    const employeeName = requestScope === "self" 
      ? "My Request" 
      : (request.employee_full_name || request.full_name || request.employee_code || request.employee_id || "Unknown");
    const designation = request.employee_designation || '';
    const branch = request.employee_branch || '';
    
    // Debug: log request data to see what's available
    console.log('Request data:', {
      id: request.id,
      requestScope,
      employee_full_name: request.employee_full_name,
      full_name: request.full_name,
      employee_code: request.employee_code,
      employee_id: request.employee_id,
      computed_employeeName: employeeName
    });
    
    return (
    <article className="request-card" key={request.id} onClick={() => { setSelectedRequest(request); navigateToPath(`/requests/${encodeURIComponent(request.id)}`); }}>
      <div className="request-card-head">
      <strong>{employeeName}</strong>
        <span className={`request-status-badge ${request.status}`}><i />{request.status}</span>
      </div>
      {requestScope !== "self" && (designation || branch) && (
        <small className="request-card-employee-info">
          {designation}{designation && branch ? ' - ' : ''}{branch}
        </small>
      )}
      <small className="request-card-type">{request.request_type === "leave" ? "Leave" : request.request_type === "early_logout" ? "Early logout" : request.request_type === "late_permission" ? "Late login" : "Permission"}</small>
      <small className="request-card-meta">{request.from_date && `From ${request.from_date} to ${request.to_date}`}{request.permission_date && `Date ${request.permission_date}${request.expected_departure_time ? ` - Leave by ${request.expected_departure_time}` : request.expected_arrival_time ? ` - Arrive by ${request.expected_arrival_time}` : ""}`}</small>
      <span className="request-card-reason">{request.reason}</span>
      {["approved", "rejected"].includes(String(request.status).toLowerCase()) && request.reviewed_by_name && <small className="request-card-reviewer">{String(request.status).toLowerCase() === "approved" ? "Approved" : "Rejected"} by {request.reviewed_by_name}{request.reviewed_by_designation ? ` (${request.reviewed_by_designation.replace(/_/g, "-")})` : ""}</small>}
      {canReview && request.status === "pending" && (
        <div className="request-review-box">
          <div className="request-review-actions">
            <button className="primary-action" type="button" disabled={busy} onClick={(event) => { event.stopPropagation(); decideRequest(request.id, "approved"); }}>Approve</button>
            <button className="outline-action" type="button" disabled={busy} onClick={(event) => { event.stopPropagation(); setRejectingRequest(request); setRejectionReason(""); setRejectionError(""); }}>Reject</button>
          </div>
        </div>
      )}
    </article>
  )};
  

  const submitRequest = async (event) => {
    event.preventDefault();
    if (requestType === "leave" && fromDate > toDate) {
      setError("The leave end date must be on or after the start date.");
      return;
    }
    if (requestType === "leave" && (!fromDate || !toDate)) {
      setError("Select both leave dates.");
      return;
    }
    if (requestType === "leave" && !leaveType) {
      setError("Select a leave type.");
      return;
    }
    if (requestType === "late_permission" && !permissionDate) {
      setError("Select the permission date.");
      return;
    }
    const permissionTime = convertPermissionTime(fromDate, fromTimePeriod);
    const permissionEndTime = convertPermissionTime(toDate, toTimePeriod);
    if (requestType === "late_permission" && !permissionTime) {
      setError("Enter a valid time in HH:MM format and select AM or PM.");
      return;
    }
    if (requestType === "late_permission" && permissionType === "others" && !permissionEndTime) {
      setError("Enter a valid end time in HH:MM format and select AM or PM.");
      return;
    }
    setBusy(true);
    setMessage("");
    setError("");

    const originalRole = localStorage.getItem("hiwaga_role");
    const shouldUseStaffRole = normalizeRoleKey(originalRole) === "super_admin";
    if (shouldUseStaffRole) localStorage.setItem("hiwaga_role", "staff");

    const isEarlyLogout = requestType === "late_permission" && permissionType === "early_logout";
    const payload = requestType === "leave"
      ? { from_date: fromDate, to_date: toDate, leave_type: leaveType, reason }
      : isEarlyLogout
        ? { permission_date: permissionDate, expected_departure_time: permissionTime, reason }
      : {
          permission_date: permissionDate,
          expected_arrival_time: permissionTime,
          reason,
        };
    payload.to_employee_id = reportingToId || null;
    try {
      const requestRoute = requestType === "leave" ? "leave" : isEarlyLogout ? "early-logout" : "late-permission";
      const response = await apiFetch(`/requests/${requestRoute}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }, token);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(normalizeErrorMessage(data, "Unable to submit request."));
      setMessage("Request submitted successfully.");
      setReason("");
      setFromDate("");
      setToDate("");
      setPermissionDate("");
      setArrivalTime("");
      setFromTimePeriod("AM");
      setToTimePeriod("AM");
      setLeaveType("");
      setPermissionType("early_logout");
      setReportingToId("");
      setComposerOpen(false);
      await loadRequests();
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      if (shouldUseStaffRole) {
        if (originalRole) localStorage.setItem("hiwaga_role", originalRole);
        else localStorage.removeItem("hiwaga_role");
      }
      setBusy(false);
    }
  };

  const decideRequest = async (requestId, decision, decisionReason = null) => {
    if (decision === "rejected" && !decisionReason?.trim()) {
      setRejectionError("Enter a reason for rejecting this request.");
      return false;
    }
    setBusy(true);
    setMessage("");
    setError("");
    try {
      const response = await apiFetch(`/requests/${requestId}/decision`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision, reason: decision === "rejected" ? decisionReason.trim() : null }),
      }, token);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(normalizeErrorMessage(data, "Unable to update request."));
      setMessage(`Request ${decision}.`);
      await loadRequests();
      if (decision === "rejected") {
        setRejectingRequest(null);
        setRejectionReason("");
        setRejectionError("");
        closeRequestDetails();
      }
      return true;
    } catch (decisionError) {
      setError(decisionError.message);
      if (decision === "rejected") setRejectionError(decisionError.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="requests-page-layout">
      <div className="requests-toolbar">
        <div className="attendance-search emp-search-box requests-search"><Search size={15} /><input value={requestSearch} onChange={(event) => setRequestSearch(event.target.value)} placeholder="Search employee" />{requestSearch && <button type="button" className="filter-search-clear" aria-label="Clear request search" onClick={() => setRequestSearch("")}><X size={14} /></button>}</div>
        <div className={`emp-status-filter-wrap ${requestScope !== "self" ? "has-filter" : ""}`}>
          <SelectFilter
            value={requestScope}
            onChange={(value) => setRequestScope(value)}
            clearable={canReview}
            defaultValue={canReview ? "team" : "self"}
            options={[
              { value: "self", label: "Self", icon: <UserRound size={15} /> },
              ...(canReview ? [{ value: "team", label: "All", icon: <Users size={15} /> }] : []),
            ]}
          />
        </div>
        <div className={`emp-status-filter-wrap ${requestBranchFilter ? "has-filter" : ""}`}>
          <SelectFilter
            value={requestBranchFilter}
            onChange={setRequestBranchFilter}
            clearable
            defaultValue=""
            placeholder="All Branches"
            options={[
              { value: "", label: "All Branches" },
              ...branches.map((branch) => ({ value: String(branch.branch_id), label: branch.name || branch.branch_name || "Branch" })),
            ]}
          />
        </div>
        <div className={`emp-status-filter-wrap ${requestTypeFilter !== "all" ? "has-filter" : ""}`}>
          <SelectFilter 
            value={requestTypeFilter} 
            onChange={setRequestTypeFilter} 
            clearable
            defaultValue="all"
            options={[
              { value: "all", label: "All Types" }, 
              { value: "leave", label: "Leaves" }, 
              { value: "permission", label: "Permissions" }
            ]} 
          />
        </div>
        <div className={`emp-status-filter-wrap ${requestStatus !== "pending" ? "has-filter" : ""}`}>
          <SelectFilter value={requestStatus} onChange={setRequestStatus} clearable defaultValue="pending" options={[{ value: "pending", label: "Pending" }, { value: "approved", label: "Approved" }, { value: "rejected", label: "Rejected" }, { value: "all", label: "All statuses" }]} />
        </div>
        <div className={`emp-status-filter-wrap ${requestPeriod !== "this_month" ? "has-filter" : ""}`}>
          <SelectFilter
            value={requestPeriod}
            onChange={setRequestPeriod}
            clearable
            defaultValue="this_month"
            options={[
              { value: "today", label: "Today" },
              { value: "yesterday", label: "Yesterday" },
              { value: "last_7_days", label: "Last 7 Days" },
              { value: "this_month", label: "This Month" },
              { value: "last_month", label: "Last Month" },
              { value: "this_year", label: "This Year" },
              { value: "last_year", label: "Last Year" },
              { value: "all", label: "All" },
              { value: "custom", label: "Custom" },
            ]}
          />
        </div>
        <button className="primary-action request-new-button" type="button" onClick={() => setComposerOpen((current) => !current)}><Plus size={15} /> {composerOpen ? "Close" : "Request"}</button>
      </div>
      {requestPeriod === "custom" && (
        <div className="requests-custom-period">
          <CustomDateField
            label="From"
            value={requestCustomFrom}
            onChange={setRequestCustomFrom}
            maxDate={requestCustomTo || null}
            placeholder="dd/mm/yyyy"
          />
          <CustomDateField
            label="To"
            value={requestCustomTo}
            onChange={setRequestCustomTo}
            maxDate={formatLocalDate(new Date())}
            placeholder="dd/mm/yyyy"
          />
        </div>
      )}
      <div className="requests-content-container">
      {canApplyRequests && composerOpen && (
        <div className="modal-backdrop request-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setComposerOpen(false); }}>
          <form className="request-compose request-modal" onSubmit={submitRequest}>
            <div className="request-modal-head">
              <strong>REQUEST</strong>
              <button type="button" className="modal-close" aria-label="Close request form" onClick={() => setComposerOpen(false)}><X size={18} /></button>
            </div>
          <div className="request-compose-tabs" role="tablist" aria-label="Request type">
            <button className={requestType === "leave" ? "active" : ""} type="button" role="tab" aria-selected={requestType === "leave"} onClick={() => setRequestType("leave")}>
              <CalendarDays size={16} /> Leave
            </button>
            <button className={requestType === "late_permission" ? "active" : ""} type="button" role="tab" aria-selected={requestType === "late_permission"} onClick={() => setRequestType("late_permission")}>
              <Clock3 size={16} /> Permission
            </button>
          </div>
          <div className="request-compose-body">
            {requestType === "leave" ? (
              <>
                <div className="request-reference-panel">
                  <label className="field-label">Apply
                    <SelectFilter
                      value={leaveType}
                      onChange={setLeaveType}
                      placeholder="Leave Type"
                      options={[
                        { value: "Casual leave", label: "Casual leave" },
                        { value: "Sick leave", label: "Sick leave" },
                      ]}
                    />
                  </label>
                  <label className="field-label request-reason-field">Reason
                    <textarea value={reason} onChange={(event) => setReason(event.target.value)} minLength={3} maxLength={2000} placeholder="Write here..." required rows={3} />
                  </label>
                </div>
                <div className="request-duration-row">
                  <strong>Duration</strong>
                  <CustomDateField
                    label=""
                    value={fromDate}
                    onChange={setFromDate}
                    maxDate={toDate || null}
                    placeholder="Start date"
                  />
                  <CustomDateField
                    label=""
                    value={toDate}
                    onChange={setToDate}
                    minDate={fromDate || null}
                    placeholder="End date"
                  />
                </div>
              </>
            ) : (
              <div className="request-reference-panel">
                <div className="field-label">
                  <CustomDateField
                    label="Permission date"
                    value={permissionDate}
                    onChange={setPermissionDate}
                    placeholder="dd/mm/yyyy"
                  />
                </div>
                {permissionType === "others" ? (
                  <>
                    <label className="field-label">Start Time<div className="request-time-control"><input type="text" value={fromDate} onChange={(event) => setFromDate(formatTimeInput(event.target.value))} placeholder="HH:MM" inputMode="numeric" maxLength="5" required /><select aria-label="Start time AM or PM" value={fromTimePeriod} onChange={(event) => setFromTimePeriod(event.target.value)}><option value="AM">AM</option><option value="PM">PM</option></select></div></label>
                    <label className="field-label">End Time<div className="request-time-control"><input type="text" value={toDate} onChange={(event) => setToDate(formatTimeInput(event.target.value))} placeholder="HH:MM" inputMode="numeric" maxLength="5" required /><select aria-label="End time AM or PM" value={toTimePeriod} onChange={(event) => setToTimePeriod(event.target.value)}><option value="AM">AM</option><option value="PM">PM</option></select></div></label>
                  </>
                ) : (
                <label className="field-label">{permissionType === "early_logout" ? "Expected departure time" : "Expected arrival time"}<div className="request-time-control"><input type="text" value={fromDate} onChange={(event) => setFromDate(formatTimeInput(event.target.value))} placeholder="HH:MM" inputMode="numeric" maxLength="5" required /><select aria-label="Permission time AM or PM" value={fromTimePeriod} onChange={(event) => setFromTimePeriod(event.target.value)}><option value="AM">AM</option><option value="PM">PM</option></select></div></label>
                )}
                <label className="field-label request-reason-field">Reason
                  <textarea value={reason} onChange={(event) => setReason(event.target.value)} minLength={3} maxLength={2000} placeholder="Write here..." required rows={3} />
                </label>
                <div style={{display: 'flex', gap: '24px', marginTop: '12px'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap'}}>
                    <input type="radio" name="permissionType" value="early_logout" checked={permissionType === "early_logout"} onChange={(event) => setPermissionType(event.target.value)} required />
                    <span style={{fontSize: '13px', color: '#263b59'}}>Early Logout</span>
                  </label>
                  <label style={{display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap'}}>
                    <input type="radio" name="permissionType" value="late_login" checked={permissionType === "late_login"} onChange={(event) => setPermissionType(event.target.value)} />
                    <span style={{fontSize: '13px', color: '#263b59'}}>Late Login</span>
                  </label>
                  <label style={{display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap'}}>
                    <input type="radio" name="permissionType" value="others" checked={permissionType === "others"} onChange={(event) => setPermissionType(event.target.value)} />
                    <span style={{fontSize: '13px', color: '#263b59'}}>Others</span>
                  </label>
                </div>
              </div>
            )}
            <div className="request-compose-footer">
              <span>Your request will be sent to your manager for review.</span>
              <button className="primary-action" type="submit" disabled={busy}>{busy ? "Submitting..." : "REQUEST"}</button>
            </div>
          </div>
          </form>
        </div>
      )}
      {message && <p className="success-message" style={{ margin: "14px 20px 0" }}>{message}</p>}
      {error && <p className="login-error" style={{ margin: "14px 20px 0" }}>{error}</p>}
      <div className="request-sections">
        <section className="request-section">
          <div className="request-section-list">
            {paginatedRequests.length ? paginatedRequests.map(renderRequestCard) : <p className="request-section-empty">No requests match these filters.</p>}
          </div>
        </section>
      </div>
      {totalPages > 1 && (
        <Pagination 
          page={currentPage}
          totalPages={totalPages}
          totalItems={totalRequests}
          pageSize={recordsPerPage}
          onPageChange={setCurrentPage}
        />
      )}
      {selectedRequest && (
        <div className="modal-backdrop request-detail-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeRequestDetails(); }}>
          <section className="request-detail-modal" role="dialog" aria-modal="true" aria-label="Request details">
            <div className="request-detail-head">
              <div>
                <span className="eyebrow">REQUEST DETAILS</span>
                <h2>{requestScope === "self" ? "My Request" : (selectedRequest.employee_full_name || selectedRequest.full_name || selectedRequest.employee_id || "Employee request")}</h2>
                {requestScope !== "self" && (selectedRequest.employee_designation || selectedRequest.employee_branch) && (
                  <small style={{color: '#8492a6', fontSize: '13px'}}>
                    {selectedRequest.employee_designation}{selectedRequest.employee_designation && selectedRequest.employee_branch ? ' - ' : ''}{selectedRequest.employee_branch}
                  </small>
                )}
              </div>
              <button type="button" className="modal-close" aria-label="Close request details" onClick={closeRequestDetails}><X size={18} /></button>
            </div>
            <div className="request-detail-body">
              <span className="request-detail-type">{selectedRequest.request_type === "leave" ? (selectedRequest.leave_type || "Leave") : selectedRequest.request_type === "early_logout" ? "Early logout" : selectedRequest.request_type === "late_permission" ? "Late login" : "Permission"}</span>
              <strong>{selectedRequest.from_date && `From ${selectedRequest.from_date} to ${selectedRequest.to_date}`}{selectedRequest.permission_date && `Date ${selectedRequest.permission_date}${selectedRequest.expected_departure_time ? ` - Leave by ${selectedRequest.expected_departure_time}` : selectedRequest.expected_arrival_time ? ` - Arrive by ${selectedRequest.expected_arrival_time}` : ""}`}</strong>
              <p>{selectedRequest.reason || "No reason provided."}</p>
              {selectedRequest.decision_reason && <small>Decision note: {selectedRequest.decision_reason}</small>}
              {["approved", "rejected"].includes(String(selectedRequest.status).toLowerCase()) && selectedRequest.reviewed_by_name && <small>{String(selectedRequest.status).toLowerCase() === "approved" ? "Approved" : "Rejected"} by {selectedRequest.reviewed_by_name}{selectedRequest.reviewed_by_designation ? ` (${selectedRequest.reviewed_by_designation.replace(/_/g, "-")})` : ""}</small>}
              <span className={`request-status-badge ${selectedRequest.status}`}><i />{selectedRequest.status}</span>
              {canReview && selectedRequest.status === "pending" && (
                <div className="request-review-actions">
                  <button className="primary-action" type="button" disabled={busy} onClick={() => { decideRequest(selectedRequest.id, "approved"); closeRequestDetails(); }}>Approve</button>
                  <button className="outline-action" type="button" disabled={busy} onClick={() => { setRejectingRequest(selectedRequest); setRejectionReason(""); setRejectionError(""); }}>Reject</button>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
      {rejectingRequest && (
        <div className="modal-backdrop request-reject-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) setRejectingRequest(null); }}>
          <form className="request-reject-modal" role="dialog" aria-modal="true" aria-labelledby="request-reject-title" onSubmit={async (event) => { event.preventDefault(); await decideRequest(rejectingRequest.id, "rejected", rejectionReason); }}>
            <div className="request-reject-head"><div><span className="eyebrow">REQUEST REVIEW</span><h2 id="request-reject-title">Reject request</h2></div><button type="button" className="modal-close" aria-label="Close rejection dialog" disabled={busy} onClick={() => setRejectingRequest(null)}><X size={18}/></button></div>
            <p>Add a reason so the employee knows why this request was rejected.</p>
            <label htmlFor="request-rejection-reason">Reason <span aria-hidden="true">*</span></label>
            <textarea id="request-rejection-reason" value={rejectionReason} onChange={(event) => { setRejectionReason(event.target.value); setRejectionError(""); }} minLength={3} maxLength={2000} rows={4} placeholder="Explain why you are rejecting this request" required autoFocus />
            {rejectionError && <p className="request-reject-error" role="alert">{rejectionError}</p>}
            <div className="request-reject-actions"><button type="button" className="outline-action" disabled={busy} onClick={() => setRejectingRequest(null)}>Cancel</button><button type="submit" className="primary-action" disabled={busy || rejectionReason.trim().length < 3}>{busy ? "Submitting-" : "Reject request"}</button></div>
          </form>
        </div>
      )}
      </div>
    </section>
  );
}

function ShiftsPage({ branches, token }) {
  const [shifts, setShifts] = useState([]);
  const [branchId, setBranchId] = useState(() => branches[0]?.branch_id || "");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    name: "",
    start_time: "09:00",
    end_time: "18:00",
    late_grace_minutes: "15",
  });

  const loadShifts = async () => {
    if (!token || !branchId) {
      setShifts([]);
      return;
    }
    const data = await getJson(`/shifts/branch/${branchId}?include_inactive=true`, token);
    setShifts(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (!branches.some((branch) => String(branch.branch_id) === String(branchId))) {
      setBranchId(branches[0]?.branch_id || "");
    }
  }, [branches, branchId]);

  useEffect(() => {
    loadShifts().catch((error) => setMessage(error.message || "Unable to load shifts."));
  }, [branchId, token]);

  const resetForm = () => {
    setEditing(null);
    setForm({ name: "", start_time: "09:00", end_time: "18:00", late_grace_minutes: "15" });
    setMessage("");
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (shift) => {
    setEditing(shift);
    setForm({
      name: shift.name || "",
      start_time: String(shift.start_time || "").slice(0, 5),
      end_time: String(shift.end_time || "").slice(0, 5),
      late_grace_minutes: String(shift.late_grace_minutes ?? 15),
    });
    setMessage("");
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();
    if (!branchId) {
      setMessage("Select a branch first.");
      return;
    }
    if (form.start_time === form.end_time) {
      setMessage("Shift end time must differ from start time.");
      return;
    }
    setBusy(true);
    setMessage("");
    const payload = {
      name: form.name.trim(),
      start_time: form.start_time,
      end_time: form.end_time,
      late_grace_minutes: Number(form.late_grace_minutes),
    };
    try {
      const response = await apiFetch(
        editing ? `/shifts/${editing.shift_id}` : `/shifts/branch/${branchId}`,
        {
          method: editing ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        token,
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(normalizeErrorMessage(data, "Unable to save shift."));
      setOpen(false);
      resetForm();
      await loadShifts();
    } catch (error) {
      setMessage(error.message || "Unable to save shift.");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (shift) => {
    setBusy(true);
    setMessage("");
    try {
      const response = await apiFetch(
        `/shifts/${shift.shift_id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_active: !shift.is_active }),
        },
        token,
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(normalizeErrorMessage(data, "Unable to update shift."));
      await loadShifts();
    } catch (error) {
      setMessage(error.message || "Unable to update shift.");
    } finally {
      setBusy(false);
    }
  };

  const deleteShift = async (shift) => {
    if (!window.confirm(`Delete the ${shift.name} shift? This cannot be undone.`)) return;
    setBusy(true);
    setMessage("");
    try {
      const response = await apiFetch(
        `/shifts/${shift.shift_id}`,
        { method: "DELETE" },
        token,
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(normalizeErrorMessage(data, "Unable to delete shift."));
      await loadShifts();
    } catch (error) {
      setMessage(error.message || "Unable to delete shift.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="admin-view panel shifts-page">
      <div className="shifts-card-head">
        <div>
          <span>Schedule</span>
          <h2>Shifts</h2>
        </div>
        <button className="primary-action" type="button" onClick={openCreate} disabled={!branchId || busy}>
          <Plus size={15} /> Create shift
        </button>
      </div>
      <div className="shifts-toolbar">
        <label className="field-label">
          Branch
          <SelectFilter
            value={branchId}
            onChange={setBranchId}
            placeholder="No branches available"
            options={branches.map((branch) => ({
              value: String(branch.branch_id),
              label: branch.name || `Branch ${branch.branch_id}`,
            }))}
          />
        </label>
        <span className="shifts-count">{shifts.length} {shifts.length === 1 ? "shift" : "shifts"}</span>
      </div>
      {message && <p className="form-message">{message}</p>}
      <div className="shifts-table-wrap">
        <table className="attendance-table shifts-table">
          <thead>
            <tr><th>Name</th><th>Start</th><th>End</th><th>Late grace</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {shifts.length ? shifts.map((shift) => (
              <tr key={shift.shift_id}>
                <td><strong>{shift.name}</strong></td>
                <td>{String(shift.start_time).slice(0, 5)}</td>
                <td>{String(shift.end_time).slice(0, 5)}</td>
                <td>{shift.late_grace_minutes} min</td>
                <td><span className={`attendance-status ${shift.is_active ? "present" : "absent"}`}>{shift.is_active ? "Active" : "Inactive"}</span></td>
                <td className="emp-actions">
                  <button className="emp-action-btn" type="button" onClick={() => openEdit(shift)}>Edit</button>
                  <button className="emp-action-btn" type="button" disabled={busy} onClick={() => toggleActive(shift)}>{shift.is_active ? "Deactivate" : "Activate"}</button>
                  <button className="emp-action-btn shift-delete-btn" type="button" disabled={busy} onClick={() => deleteShift(shift)}>Delete</button>
                </td>
              </tr>
            )) : <tr><td colSpan="6" className="attendance-no-results">No shifts found for this branch.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && (
        <div className="modal-backdrop">
          <form className="user-modal" onSubmit={save}>
            <div className="modal-head">
              <h2>{editing ? "Update shift" : "Create shift"}</h2>
              <button type="button" className="modal-close" onClick={() => setOpen(false)}><X size={16} /></button>
            </div>
            <div className="form-grid">
              <label>Shift name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} minLength={2} maxLength={100} required /></label>
              <label>Start time<input type="time" value={form.start_time} onChange={(event) => setForm({ ...form, start_time: event.target.value })} required /></label>
              <label>End time<input type="time" value={form.end_time} onChange={(event) => setForm({ ...form, end_time: event.target.value })} required /></label>
              <label>Late grace (minutes)<input type="number" min="0" max="120" value={form.late_grace_minutes} onChange={(event) => setForm({ ...form, late_grace_minutes: event.target.value })} required /></label>
            </div>
            {message && <p className="form-message">{message}</p>}
            <div className="modal-actions"><button type="button" className="outline-action" onClick={() => setOpen(false)}>Cancel</button><button className="primary-action" disabled={busy}>{busy ? "Saving..." : editing ? "Save changes" : "Create shift"}</button></div>
          </form>
        </div>
      )}
    </section>
  );
}

const announcementMediaItems = (item) => Array.isArray(item?.media) && item.media.length
  ? item.media
  : item?.media_url ? [{ media_url: item.media_url, media_content_type: item.media_content_type, media_filename: item.media_filename }] : [];

function AnnouncementsCenter({ branches = [], staff = [], token, currentEmployeeId }) {
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [announcementReadBusy, setAnnouncementReadBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [announcementSoundEnabled, setAnnouncementSoundEnabled] = useState(
    () => localStorage.getItem(`hiwaga_announcement_sound_${currentEmployeeId || "admin"}`) !== "false",
  );
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [targetType, setTargetType] = useState("all");
  const [branchIds, setBranchIds] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [removeCurrentMedia, setRemoveCurrentMedia] = useState(false);
  const [mediaViewer, setMediaViewer] = useState(null); // For image/video pagination viewer
  const announcementIdsRef = useRef(null);
  const announcementAudioRef = useRef(null);
  
  // Use the standard pagination hook with PAGE_SIZE (10) items per page
  const { page, setPage, totalPages, totalItems, pageData: items, pageSize } = usePagination(allItems, PAGE_SIZE);
  
  const playAnnouncementChime = useCallback(() => {
    if (!announcementSoundEnabled) return;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    try {
      const context = announcementAudioRef.current || new AudioContextClass();
      announcementAudioRef.current = context;
      if (context.state === "suspended") context.resume().catch(() => {});
      [659.25, 783.99, 987.77].forEach((frequency, index) => {
        const start = context.currentTime + index * 0.17;
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = "sine";
        oscillator.frequency.value = frequency;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.12, start + 0.025);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.48);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(start);
        oscillator.stop(start + 0.5);
      });
    } catch { /* Audio may be unavailable or blocked until the user enables it. */ }
  }, [announcementSoundEnabled]);
  const load = useCallback(async ({ showLoading = true } = {}) => {
    if (showLoading) setLoading(true);
    try {
      // Load all announcements for client-side pagination - this improves UX
      // by allowing instant pagination without server requests
      const listResponse = await apiFetch(`/announcements/me?page_size=1000`, {}, token);
      const data = await listResponse.json().catch(() => ({}));
      if (!listResponse.ok) throw new Error(data.detail || "Could not load announcements.");
      const announcements = Array.isArray(data) ? data : Array.isArray(data.items) ? data.items : [];
      const nextIds = new Set(announcements.map((item) => String(item.id)));
      if (announcementIdsRef.current && announcements.some((item) =>
        !item.is_read && !announcementIdsRef.current.has(String(item.id)),
      )) playAnnouncementChime();
      announcementIdsRef.current = nextIds;
      // Sort announcements by creation date (newest first)
      announcements.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setAllItems(announcements);
      setError("");
    } catch (err) { setError(err.message || "Could not load announcements."); }
    finally { setLoading(false); }
  }, [token, playAnnouncementChime]);
  useEffect(() => {
    load();
    const timer = window.setInterval(() => load({ showLoading: false }), 30_000);
    return () => window.clearInterval(timer);
  }, [load]);
  useEffect(() => () => { announcementAudioRef.current?.close().catch(() => {}); }, []);
  const toggleAnnouncementSound = async () => {
    const enabled = !announcementSoundEnabled;
    setAnnouncementSoundEnabled(enabled);
    localStorage.setItem(`hiwaga_announcement_sound_${currentEmployeeId || "admin"}`, String(enabled));
    if (!enabled) return;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) { setError("This browser does not support announcement sounds."); return; }
    try {
      const context = announcementAudioRef.current || new AudioContextClass();
      announcementAudioRef.current = context;
      await context.resume();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = 783.99;
      gain.gain.setValueAtTime(0.0001, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.1, context.currentTime + 0.025);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.3);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.32);
    } catch { setError("Could not enable announcement sound in this browser."); }
  };
  const markAllAnnouncementsRead = async () => {
    const unread = allItems.filter((item) => !item.is_read);
    if (!unread.length || announcementReadBusy) return;
    setAnnouncementReadBusy(true);
    setError("");
    setNotice("");
    const results = await Promise.allSettled(unread.map(async (item) => {
      const response = await apiFetch(`/announcements/${encodeURIComponent(item.id)}/read`, { method: "POST" }, token);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(normalizeErrorMessage(data, "Could not mark announcement as read."));
      }
      return item.id;
    }));
    const readIds = new Set(results
      .filter((result) => result.status === "fulfilled")
      .map((result) => result.value));
    setAllItems((current) => current.map((item) => readIds.has(item.id) ? { ...item, is_read: true } : item));
    const failureCount = results.filter((result) => result.status === "rejected").length;
    if (failureCount) setError(`Could not mark ${failureCount} announcement${failureCount === 1 ? "" : "s"} as read.`);
    else setNotice("All announcements marked as read.");
    setAnnouncementReadBusy(false);
  };
  const saveAnnouncement = async (event) => {
    event.preventDefault(); setBusy(true); setError(""); setNotice("");
    try {
      const form = new FormData();
      form.append("title", title.trim());
      form.append("body", body.trim());
      if (editingAnnouncement) {
        form.append("remove_media", String(removeCurrentMedia && mediaFiles.length === 0));
      } else {
        form.append("target_type", targetType);
        if (targetType === "branches") form.append("branch_ids", JSON.stringify(branchIds));
        if (targetType === "employee") form.append("employee_id", employeeId);
      }
      mediaFiles.forEach((file) => form.append("media", file));
      const response = await apiFetch(
        editingAnnouncement ? `/announcements/${encodeURIComponent(editingAnnouncement.id)}` : "/announcements",
        { method: editingAnnouncement ? "PATCH" : "POST", body: form },
        token,
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(normalizeErrorMessage(data, "Could not save announcement."));
      const wasEditing = Boolean(editingAnnouncement);
      setTitle(""); setBody(""); setMediaFiles([]); setRemoveCurrentMedia(false); setEditingAnnouncement(null);
      setNotice(wasEditing ? "Announcement updated." : "Announcement published."); setComposerOpen(false);
      event.target.reset(); 
      // Reload all announcements to reflect changes
      await load();
    } catch (err) { setError(err.message || "Could not save announcement."); }
    finally { setBusy(false); }
  };
  const editAnnouncement = (item) => {
    setEditingAnnouncement(item);
    setTitle(item.title || "");
    setBody(item.body || "");
    setMediaFiles([]);
    setRemoveCurrentMedia(false);
    setError(""); setNotice(""); setComposerOpen(true);
  };
  const deleteAnnouncement = async (item) => {
    if (!window.confirm(`Delete “${item.title}”? This cannot be undone.`)) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const response = await apiFetch(`/announcements/${encodeURIComponent(item.id)}`, { method: "DELETE" }, token);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(normalizeErrorMessage(data, "Could not delete announcement."));
      }
      setNotice("Announcement deleted.");
      // Reload all announcements to reflect changes
      await load();
    } catch (err) { setError(err.message || "Could not delete announcement."); }
    finally { setBusy(false); }
  };
  const chooseMediaFiles = (fileList) => {
    const selectedFiles = Array.from(fileList || []);
    if (selectedFiles.length > 10) { setError("Choose no more than 10 images or videos."); return; }
    if (selectedFiles.reduce((sum, file) => sum + file.size, 0) > 100 * 1024 * 1024) { setError("The total attachment size must be 100 MB or less."); return; }
    setMediaFiles(selectedFiles);
    setRemoveCurrentMedia(false);
    setError("");
  };
  const pageCount = totalPages;
  const visibleItems = items;
  return <section className="admin-view panel announcements-center">
    <header className="announcement-page-header"><div><h2><span>Hiwaga</span> Announcements</h2><p>Stay updated with important notifications and updates</p></div><div className="announcement-page-actions"><div className="announcement-inbox-actions"><button type="button" className={`announcement-sound-toggle ${announcementSoundEnabled ? "sound-enabled" : ""}`} onClick={toggleAnnouncementSound} aria-label={announcementSoundEnabled ? "Turn announcement sound off" : "Turn announcement sound on"} title={announcementSoundEnabled ? "Turn announcement sound off" : "Turn announcement sound on"}>{announcementSoundEnabled ? <Volume2 size={18}/> : <VolumeX size={18}/>}</button><button type="button" className="announcement-refresh-button" disabled={loading} onClick={() => load()} aria-label="Refresh announcements" title="Refresh announcements"><RefreshCw size={17}/></button><button type="button" className="announcement-mark-read-button" disabled={announcementReadBusy || !allItems.some((item) => !item.is_read)} onClick={markAllAnnouncementsRead}><CheckCircle2 size={16}/>{announcementReadBusy ? "Marking…" : "Mark all as read"}</button></div><button className={`announcement-send-button ${composerOpen ? "is-close" : ""}`} type="button" onClick={() => { if (composerOpen) { setEditingAnnouncement(null); setTitle(""); setBody(""); setMediaFiles([]); setRemoveCurrentMedia(false); setError(""); } setComposerOpen((open) => !open); }}><Plus size={16}/>{composerOpen ? "Close" : "Send New"}</button></div></header>
    {composerOpen && <form className="announcement-compose" onSubmit={saveAnnouncement}>
      <div className="form-grid">
        <label>Title<input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} required /></label>
        {!editingAnnouncement && <label>Audience<select value={targetType} onChange={(event) => setTargetType(event.target.value)}><option value="all">All employees</option><option value="branches">Selected branches</option><option value="employee">One employee</option></select></label>}
        {!editingAnnouncement && targetType === "branches" && <label>Branches<select multiple value={branchIds} onChange={(event) => setBranchIds(Array.from(event.target.selectedOptions, (option) => option.value))} required>{branches.map((branch) => <option key={branch.branch_id} value={branch.branch_id}>{branch.name || `Branch ${branch.branch_id}`}</option>)}</select></label>}
        {!editingAnnouncement && targetType === "employee" && <label>Employee<select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)} required><option value="">Select employee</option>{staff.map((person) => <option key={person.employee_id} value={person.employee_id}>{person.full_name || person.employee_id}</option>)}</select></label>}
        <label className="announcement-body-field">Message<textarea value={body} onChange={(event) => setBody(event.target.value)} maxLength={10000} rows="4" placeholder="Write your announcement" required={!mediaFiles.length && !(announcementMediaItems(editingAnnouncement).length && !removeCurrentMedia)} /></label>
        <label>{editingAnnouncement ? "Replace images or videos" : "Images or videos"}<input type="file" accept="image/*,video/*" multiple onChange={(event) => chooseMediaFiles(event.target.files)} /></label>
        {mediaFiles.length > 0 && <small className="announcement-selected-media">Selected: {mediaFiles.map((file) => file.name).join(", ")}</small>}
        {editingAnnouncement && announcementMediaItems(editingAnnouncement).length > 0 && <label className="announcement-remove-media"><input type="checkbox" checked={removeCurrentMedia} onChange={(event) => setRemoveCurrentMedia(event.target.checked)} />Remove current attachments</label>}
      </div>
      <div className="announcement-compose-footer"><small>{editingAnnouncement ? "Update the announcement details or replace/remove its attachments." : "Provide a message or attach up to 10 images/videos (100 MB total)."}</small><button className="primary-action" disabled={busy || (!editingAnnouncement && targetType === "branches" && !branchIds.length) || (!editingAnnouncement && targetType === "employee" && !employeeId)}>{busy ? "Saving…" : editingAnnouncement ? "Save changes" : "Publish announcement"}</button></div>
      {error && <p className="form-message error-message">{error}</p>}{notice && <p className="form-message">{notice}</p>}
    </form>}
    {error && !composerOpen && <p className="form-message error-message">{error}</p>}{notice && <p className="form-message">{notice}</p>}
    {loading ? <p className="announcement-empty">Loading announcements…</p> : items.length ? <div className="announcement-list">{items.map((item) => {
      const sender = staff.find((person) => String(person.employee_id) === String(item.created_by_employee_id));
      const senderName = item.name || sender?.full_name || item.created_by_name || "Team member";
      const senderDesignation = (item.designation || sender?.designation || sender?.role_code || "").replace(/_/g, "-");
      const mediaItems = announcementMediaItems(item);
      
      return <article className="announcement-card" key={item.id}>
        <div className="announcement-card-top">
          <div><h3>{item.title}</h3></div>
          <div className="announcement-card-byline">
            <span>{senderName}{senderDesignation ? ` (${senderDesignation})` : ""}</span>
            <time>{parseUTC(item.created_at)?.toLocaleDateString("en-GB") || item.created_at}</time>
          </div>
        </div>
        {item.body && <p>{item.body}</p>}
        {mediaItems.length > 0 && (
          <div className={`announcement-media-gallery${mediaItems.length === 1 ? " is-single" : ""}`} aria-label={`${mediaItems.length} attachments`}>
            {mediaItems.slice(0, 4).map((mediaItem, index) => (
              <button 
                key={mediaItem.id || mediaItem.media_url || index}
                className="announcement-media-tile" 
                type="button" 
                onClick={() => setMediaViewer({ title: item.title, items: mediaItems, index })}
                aria-label={`View attachment ${index + 1} of ${mediaItems.length}`}
              >
                {mediaItem.media_content_type?.startsWith("video/") ? (
                  <video src={mediaItem.media_url} />
                ) : (
                  <img src={mediaItem.media_url} alt={mediaItem.media_filename || item.title} />
                )}
                {index === 3 && mediaItems.length > 4 && (
                  <div className="announcement-media-more">+{mediaItems.length - 4}</div>
                )}
              </button>
            ))}
          </div>
        )}
        <div className="announcement-card-bottom">
          <div className="announcement-card-actions">
            <button type="button" disabled={busy} onClick={() => editAnnouncement(item)}>
              <Pencil size={14}/>Edit
            </button>
            <button type="button" disabled={busy} onClick={() => deleteAnnouncement(item)}>
              <Trash2 size={14}/>Delete
            </button>
          </div>
          <span aria-label={item.is_read ? "Read" : "Unread"} title={item.is_read ? "Read" : "Unread"}>
            <Bell size={17}/>
          </span>
        </div>
      </article>;
    })}</div> : <p className="announcement-empty">No announcements have been sent to you yet.</p>}
    {totalItems > 0 && (
      <div className="announcements-pagination">
        <Pagination 
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      </div>
    )}
    
    {/* Media Viewer for paginating through announcement images/videos */}
    {mediaViewer && (
      <div className="announcement-media-viewer" role="dialog" aria-modal="true" aria-label={`${mediaViewer.title} attachments`} 
           onClick={(event) => { if (event.target === event.currentTarget) setMediaViewer(null); }}>
        <section className="announcement-media-viewer-panel">
          <header>
            <span>{mediaViewer.title} · {mediaViewer.index + 1} of {mediaViewer.items.length}</span>
            <button type="button" onClick={() => setMediaViewer(null)} aria-label="Close attachment viewer">
              <X size={20}/>
            </button>
          </header>
          <div className="announcement-media-viewer-stage">
            {mediaViewer.index > 0 && (
              <button 
                type="button" 
                className="announcement-media-viewer-nav previous" 
                onClick={() => setMediaViewer((current) => ({ ...current, index: current.index - 1 }))} 
                aria-label="Previous attachment"
              >
                <ChevronLeft size={22}/>
              </button>
            )}
            {mediaViewer.items[mediaViewer.index].media_content_type?.startsWith("video/") ? (
              <video controls autoPlay src={mediaViewer.items[mediaViewer.index].media_url}/>
            ) : (
              <img 
                src={mediaViewer.items[mediaViewer.index].media_url} 
                alt={mediaViewer.items[mediaViewer.index].media_filename || mediaViewer.title}
              />
            )}
            {mediaViewer.index < mediaViewer.items.length - 1 && (
              <button 
                type="button" 
                className="announcement-media-viewer-nav next" 
                onClick={() => setMediaViewer((current) => ({ ...current, index: current.index + 1 }))} 
                aria-label="Next attachment"
              >
                <ChevronRight size={22}/>
              </button>
            )}
          </div>
        </section>
      </div>
    )}
  </section>;
}
function NotifyPage({ token }) {
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      title: "Client Appointment",
      date: "28/09/2026",
      sender: "Rajesh",
      senderRole: "Manager",
      message: "Tomorrow you have a client appointment at 10:30 AM.",
      sentTo: "Rajesh",
      timestamp: new Date("2026-09-28"),
      comments: [],
    },
    {
      id: 2,
      title: "Branch Meeting",
      date: "27/09/2026",
      sender: "Priya",
      senderRole: "Manager",
      message: "Please report to the Hyderabad branch by 9:30 AM tomorrow.",
      sentTo: "Hyderabad Branch",
      timestamp: new Date("2026-09-27"),
      comments: [],
    },
    {
      id: 3,
      title: "Staff Meeting",
      date: "26/09/2026",
      sender: "Admin",
      senderRole: "",
      message: "Staff meeting is scheduled for Monday at 5:00 PM.",
      sentTo: "All Employees",
      timestamp: new Date("2026-09-26"),
      comments: [],
    },
    {
      id: 4,
      title: "Training Session",
      date: "25/09/2026",
      sender: "HR Team",
      senderRole: "",
      message: "Mandatory training session on new software tools this Friday.",
      sentTo: "All Employees",
      timestamp: new Date("2026-09-25"),
      comments: [],
    },
    {
      id: 5,
      title: "Holiday Notice",
      date: "24/09/2026",
      sender: "Admin",
      senderRole: "",
      message: "Office will be closed on October 2nd for national holiday.",
      sentTo: "All Employees",
      timestamp: new Date("2026-09-24"),
      comments: [],
    },
  ]);
  const [composerOpen, setComposerOpen] = useState(false);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const notificationsPerPage = 3;
  const [form, setForm] = useState({
    title: "",
    message: "",
    sentTo: "",
  });

  // Pagination calculations
  const indexOfLastNotification = currentPage * notificationsPerPage;
  const indexOfFirstNotification = indexOfLastNotification - notificationsPerPage;
  const currentNotifications = notifications.slice(indexOfFirstNotification, indexOfLastNotification);
  const totalPages = Math.ceil(notifications.length / notificationsPerPage);

  const openComposer = () => {
    setForm({ title: "", message: "", sentTo: "" });
    setComposerOpen(true);
  };

  const closeComposer = () => {
    setComposerOpen(false);
  };

  const openCommentModal = (notification) => {
    setSelectedNotification(notification);
    setCommentText("");
    setCommentModalOpen(true);
  };

  const closeCommentModal = () => {
    setCommentModalOpen(false);
    setSelectedNotification(null);
    setCommentText("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const newNotification = {
      id: notifications.length + 1,
      title: form.title,
      date: new Date().toLocaleDateString("en-GB"),
      sender: "You",
      senderRole: "",
      message: form.message,
      sentTo: form.sentTo,
      timestamp: new Date(),
      comments: [],
    };
    setNotifications([newNotification, ...notifications]);
    setComposerOpen(false);
    setCurrentPage(1); // Reset to first page when new notification is added
  };

  const handleAddComment = (event) => {
    event.preventDefault();
    if (!commentText.trim()) return;

    const updatedNotifications = notifications.map((notif) => {
      if (notif.id === selectedNotification.id) {
        return {
          ...notif,
          comments: [
            ...notif.comments,
            {
              id: notif.comments.length + 1,
              text: commentText,
              author: "You",
              timestamp: new Date().toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }),
            },
          ],
        };
      }
      return notif;
    });

    setNotifications(updatedNotifications);
    setCommentText("");
    closeCommentModal();
  };

  return (
    <section className="admin-view panel notify-page">
      <div className="notify-header">
        <div>
          <h2><span style={{ color: "#10b981" }}>Hiwaga</span> Announcements</h2>
          <p style={{ fontSize: "13px", color: "#64748b", margin: "4px 0 0" }}>
            Stay updated with important notifications and updates
          </p>
        </div>
        <button className="notify-send-btn" type="button" onClick={openComposer}>
          <Plus size={15} /> Send New
        </button>
      </div>

      <div className="notify-list">
        {currentNotifications.length > 0 ? (
          currentNotifications.map((notification) => (
            <div key={notification.id} className="notify-card">
              <div className="notify-card-header">
                <h3>{notification.title}</h3>
                <span className="notify-date">{notification.date}</span>
              </div>
              <div className="notify-sender">
                {notification.sender}
                {notification.senderRole && ` (${notification.senderRole})`}
              </div>
              <div className="notify-message">{notification.message}</div>
              <div className="notify-footer">Sent to: {notification.sentTo}</div>
              <button
                className="notify-comment-btn"
                onClick={() => openCommentModal(notification)}
                title="Add comment"
              >
                <MessageCircle size={16} />
                {notification.comments.length > 0 && (
                  <span className="comment-count">{notification.comments.length}</span>
                )}
              </button>
            </div>
          ))
        ) : (
          <div className="notify-empty-state">
            <Bell size={48} />
            <h3>No announcements yet</h3>
            <p>New announcements will appear here once they are published.</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          totalItems={notifications.length}
          pageSize={notificationsPerPage}
          onPageChange={setCurrentPage}
        />
      )}

      {composerOpen && (
        <div className="modal-backdrop">
          <form className="user-modal notify-composer" onSubmit={handleSubmit}>
            <div className="modal-head">
              <div>
                <h2>Send Announcement</h2>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={closeComposer}
              >
                <X size={16} />
              </button>
            </div>
            <div className="form-grid">
              <label>
                Title
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  placeholder="e.g., Client Appointment"
                />
              </label>
              <label>
                Send to
                <input
                  value={form.sentTo}
                  onChange={(e) => setForm({ ...form, sentTo: e.target.value })}
                  required
                  placeholder="e.g., All Employees, Branch Name"
                />
              </label>
              <label style={{ gridColumn: "1 / -1" }}>
                Message
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  required
                  rows={4}
                  placeholder="Write your announcement message..."
                />
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="outline-action" onClick={closeComposer}>
                Cancel
              </button>
              <button type="submit" className="primary-action">
                <Send size={15} /> Send Announcement
              </button>
            </div>
          </form>
        </div>
      )}

      {commentModalOpen && selectedNotification && (
        <div className="modal-backdrop">
          <div className="user-modal notify-comment-modal">
            <div className="modal-head">
              <div>
                <h2>Comments</h2>
                <p style={{ fontSize: "13px", color: "#64748b", margin: "4px 0 0" }}>
                  {selectedNotification.title}
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={closeCommentModal}
              >
                <X size={16} />
              </button>
            </div>
            <div className="notify-comments-list">
              {selectedNotification.comments.length > 0 ? (
                selectedNotification.comments.map((comment) => (
                  <div key={comment.id} className="notify-comment-item">
                    <div className="notify-comment-header">
                      <strong>{comment.author}</strong>
                      <span className="notify-comment-time">{comment.timestamp}</span>
                    </div>
                    <div className="notify-comment-text">{comment.text}</div>
                  </div>
                ))
              ) : (
                <div className="notify-no-comments">
                  <MessageCircle size={32} />
                  <p>No comments yet. Be the first to comment!</p>
                </div>
              )}
            </div>
            <form className="notify-comment-form" onSubmit={handleAddComment}>
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Write a comment..."
                rows={3}
                required
              />
              <div className="modal-actions">
                <button type="button" className="outline-action" onClick={closeCommentModal}>
                  Cancel
                </button>
                <button type="submit" className="primary-action">
                  <MessageCircle size={15} /> Add Comment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

function AdminView({
  tab,
  branches,
  staff,
  attendance,
  departments = [],
  designations = [],
  token,
  onLogout,
  onChangePassword,
  quickAction,
  currentUser,
  onAttendanceRefresh,
  currentUserName,
  onProfileUpdate,
  onBranchesChange,
  onDepartmentsChange,
  onDesignationsChange,
}) {
  const profileFileInputRef = useRef(null);
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const [showProfilePhoto, setShowProfilePhoto] = useState(false);
  const [profilePhotoBusy, setProfilePhotoBusy] = useState(false);
  const [profilePhotoMessage, setProfilePhotoMessage] = useState("");
  const [profileTab, setProfileTab] = useState("personal");
  useEffect(() => {
    if (tab !== "Settings") return undefined;
    const syncProfileRoute = () => {
      const match = window.location.pathname.match(/^\/profile\/(personal|employment|payroll)\/?$/i);
      setProfileTab(match ? match[1].toLowerCase() : "personal");
    };
    syncProfileRoute();
    window.addEventListener("popstate", syncProfileRoute);
    return () => window.removeEventListener("popstate", syncProfileRoute);
  }, [tab]);
  if (tab === "Kiosk Accounts") return <KioskAccountsPage branches={branches} staff={staff} token={token} />;
  if (tab === "Attendance")
    return (
      <AttendancePage
        staff={staff}
        branches={branches}
        attendance={attendance}
        currentUser={currentUser}
        onAttendanceRefresh={onAttendanceRefresh}
        departments={departments}
        designations={designations}
        token={token}
      />
    );
  if (tab === "Announcements")
    return <AnnouncementsCenter branches={branches} staff={staff} token={token} currentEmployeeId={currentUser?.employee_id || currentUser?.user_id || localStorage.getItem("hiwaga_employee_id")} />;
  if (tab === "Reports")
    return <ReportsCenter branches={branches} staff={staff} token={token} />;
  if (tab === "Shifts")
    return <ShiftsPage branches={branches} token={token} />;
  if (tab === "Settings") {
    const profileInitials = (currentUser?.full_name || "AD")
      .split(" ")
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "AD";
    const profileGroups = [
      {
        id: "personal",
        label: "Personal",
        fields: [
          ["Full name", currentUser?.full_name],
          ["Father / husband name", currentUser?.father_husband_name],
          ["Email", currentUser?.email],
          ["Phone", currentUser?.phone],
          ["Gender", currentUser?.gender],
          ["Date of birth", currentUser?.date_of_birth],
          ["Category", currentUser?.category],
          ["Marital status", currentUser?.marital_status],
          ["Aadhaar number", currentUser?.aadhaar_number],
          ["Address", currentUser?.address],
        ],
      },
      {
        id: "employment",
        label: "Employment",
        fields: [
          ["Employee Code", currentUser?.employee_code],
          ["Designation", currentUser?.designation],
          ["Department", currentUser?.department],
          ["Date of joining", currentUser?.date_of_joining],
          ["Last working day (LWD)", currentUser?.last_working_day],
          ["Role", currentUser?.role_code || currentUser?.role || localStorage.getItem("hiwaga_role")],
          ["Branch", currentUser?.branch_name || branches.find((branch) => String(branch.branch_id) === String(currentUser?.branch_id))?.name || currentUser?.branch_id],
          ["Employment status", currentUser?.employment_status],
          ["Previous employment joining date", currentUser?.previous_employment_joining_date],
        ],
      },
      {
        id: "payroll",
        label: "Payroll & statutory",
        fields: [
          ["PAN number", currentUser?.pan_number],
          ["UAN number", currentUser?.uan_number],
          ["EPF number", currentUser?.epf_number],
          ["ESIC number", currentUser?.esic_number],
          ["Bank name", currentUser?.bank_name],
          ["Bank account number", currentUser?.bank_account_number],
          ["IFSC", currentUser?.bank_ifsc],
          ["Bank branch", currentUser?.bank_branch],
          ["Pay scale", currentUser?.pay_scale],
          ["Basic salary", currentUser?.basic_salary],
          ["HRA", currentUser?.hra],
          ["Other allowances", currentUser?.other_allowances],
        ],
      },
    ];
    const activeProfileGroup = profileGroups.find((group) => group.id === profileTab) || profileGroups[0];

    const handleProfileImageUpload = async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        setProfilePhotoMessage("Choose an image file for your profile photo.");
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setProfilePhotoMessage("Choose a profile photo smaller than 2 MB.");
        return;
      }

      setProfilePhotoBusy(true);
      setProfilePhotoMessage("");
      try {
        const formData = new FormData();
        formData.append("photo", file, file.name);
        const response = await apiFetch("/auth/me/photo", { method: "POST", body: formData }, token);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(normalizeErrorMessage(data, "Unable to upload profile photo."));
        onProfileUpdate(await getJson("/auth/me", token));
        setProfilePhotoMessage("Profile photo updated.");
      } catch (error) {
        setProfilePhotoMessage(error.message || "Unable to upload profile photo.");
      } finally {
        setProfilePhotoBusy(false);
      }
    };

    return (
      <section className="admin-view panel settings-page">
        <header className="settings-header">
          <div><h2>Profile information</h2><p>View your personal details, profile photo, and account security.</p></div>
          <div className="settings-header-actions">
            <button className="outline-action" onClick={onChangePassword}>Change password</button>
            <button className="settings-logout" onClick={onLogout}><LogOut size={15} /> Logout</button>
          </div>
        </header>

        <div className="settings-profile-card">
          <div className="settings-identity">
            <div className="settings-avatar-wrap">
            <button type="button" className="settings-avatar" onClick={() => setShowPhotoMenu((open) => !open)} aria-label="Profile photo options" aria-expanded={showPhotoMenu}>
                {currentUser?.photo_url ? (
                    <img src={currentUser.photo_url} alt="Profile" />
                ) : (
                <span className="settings-avatar-initials">{profileInitials}</span>
                )}
              <span className="settings-avatar-camera" aria-hidden="true"><Camera size={13} /></span>
            </button>
            {showPhotoMenu && <div className="settings-photo-menu" role="menu">
              {currentUser?.photo_url && <button type="button" role="menuitem" onClick={() => { setShowPhotoMenu(false); setShowProfilePhoto(true); }}><Eye size={15} /> View photo</button>}
              <button type="button" role="menuitem" disabled={profilePhotoBusy} onClick={() => { setShowPhotoMenu(false); profileFileInputRef.current?.click(); }}><Camera size={15} /> {profilePhotoBusy ? "Uploading..." : currentUser?.photo_url ? "Change photo" : "Add photo"}</button>
            </div>}
            </div>
            <div className="settings-identity-copy">
              <span className="settings-identity-label">Signed in as</span>
              <h3>{currentUser?.full_name || "Account profile"}</h3>
              <span className="settings-identity-label">{currentUser?.designation || "Team member"}{currentUser?.department ? ` · ${currentUser.department}` : ""}</span>
              <span className="settings-role-pill">{currentUser?.role_code || currentUser?.role || localStorage.getItem("hiwaga_role") || "Team member"}</span>
              <input
                ref={profileFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfileImageUpload}
                style={{ display: "none" }}
              />
              {profilePhotoMessage && <small className="settings-photo-message" role="status">{profilePhotoMessage}</small>}
            </div>
          </div>
          <div className="settings-info-section settings-profile-sections">
            <div className="staff-profile-tabs" role="tablist" aria-label="Profile information">
              {profileGroups.map((group) => (
                <button
                  key={group.id}
                  id={`admin-profile-tab-${group.id}`}
                  type="button"
                  role="tab"
                  aria-selected={profileTab === group.id}
                  aria-controls="admin-profile-panel"
                  className={profileTab === group.id ? "active" : ""}
                  onClick={() => { setProfileTab(group.id); navigateToPath(`/profile/${group.id}`); }}
                >
                  {group.label}
                </button>
              ))}
            </div>
            <section
              className="staff-profile-tabpanel"
              id="admin-profile-panel"
              role="tabpanel"
              aria-labelledby={`admin-profile-tab-${activeProfileGroup.id}`}
            >
              <div className="staff-profile-fields">
                {activeProfileGroup.fields.map(([label, value]) => (
                  <div key={label}>
                    <span>{label}</span>
                    <b>{value === undefined || value === null || String(value).trim() === "" ? "Not provided" : String(value)}</b>
                  </div>
                ))}
              </div>
            </section>
          </div>
          
          <ProfileDataTables 
            employeeData={currentUser}
            showMobileFooter={false}
            token={token}
            onDataChange={() => window.location.reload()}
            isEditable={true}
          />
        </div>
        
        {showProfilePhoto && (
          <div className="settings-photo-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setShowProfilePhoto(false); }}>
            <div className="settings-photo-preview" role="dialog" aria-modal="true" aria-label="Profile photo">
              <button type="button" className="settings-photo-close" onClick={() => setShowProfilePhoto(false)} aria-label="Close photo preview"><X size={18} /></button>
              {currentUser?.photo_url ? <img src={currentUser.photo_url} alt={`${currentUser?.full_name || "User"} profile`} /> : <div className="settings-photo-empty"><span>{profileInitials}</span><p>No profile photo uploaded</p></div>}
            </div>
          </div>
        )}
      </section>
    );
  }
  if (tab === "Employees")
    return (
      <UserManagement
        staff={staff}
        branches={branches}
        attendance={attendance}
        departments={departments}
        designations={designations}
        token={token}
        openCreateRequested={quickAction === "staff"}
      />
    );
  if (tab === "Permissions")
    return (
      <PermissionManagement staff={staff} branches={branches} token={token} />
    );
  if (tab === "Leave approvals")
    return <RequestsPage token={token} currentUserName={currentUserName} branches={branches} />;
  if (tab === "Requests")
    return (
      <RequestsPage token={token} currentUserName={currentUserName} branches={branches} />
    );
  if (tab === "Branches")
    return (
      <BranchManagement
        branches={branches}
        staff={staff}
        attendance={attendance}
        token={token}
        onBranchesChange={onBranchesChange}
        openCreateRequested={quickAction === "branch"}
      />
    );
  if (tab === "Departments" || tab === "Designations") {
    const isDepartment = tab === "Departments";
    return <OrganizationCatalog
      title={tab}
      endpoint={isDepartment ? "/departments" : "/designations"}
      idKey={isDepartment ? "department_id" : "designation_id"}
      items={isDepartment ? departments : designations}
      onItemsChange={isDepartment ? onDepartmentsChange : onDesignationsChange}
      token={token}
    />;
  }
  return (
    <AdminTable
      title="Live attendance"
      eyebrow="MONITORING"
      columns={["Employee", "Type", "Timestamp", "Geofence"]}
      rows={attendance
        .slice(0, 30)
        .map((record) => [
          record.employee_id,
          record.check_type,
          parseUTC(record.timestamp).toLocaleString(),
          record.geofence_status,
        ])}
    />
  );
}

function OrganizationCatalog({ title, endpoint, idKey, items = [], onItemsChange, token }) {
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
    setMessage("");
    setName("");
    setEditingId("");
    setOpen(false);
  }, [title]);
  const openCreate = () => {
    setEditingId("");
    setName("");
    setMessage("");
    setOpen(true);
  };
  const save = async (event) => {
    event.preventDefault();
    const cleanName = name.trim();
    if (!cleanName) return;
    setBusy(true);
    setMessage("");
    try {
      const response = await apiFetch(editingId ? `${endpoint}/${encodeURIComponent(editingId)}` : endpoint, {
        method: editingId ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: cleanName }),
      }, token);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(normalizeErrorMessage(data, `Unable to save ${title.toLowerCase()}.`));
      const key = idKey;
      onItemsChange((current) => editingId
        ? current.map((item) => String(item[key]) === String(editingId) ? data : item)
        : [...current, data]);
      setName("");
      setEditingId("");
      setOpen(false);
      setMessage(`${title.slice(0, -1)} saved.`);
    } catch (error) {
      setMessage(error.message || `Unable to save ${title.toLowerCase()}.`);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (item) => {
    try {
      const response = await apiFetch(`${endpoint}/${encodeURIComponent(item[idKey])}`, { method: "DELETE" }, token);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(normalizeErrorMessage(data, `Unable to delete ${title.toLowerCase()}.`));
      }
      onItemsChange((current) => current.filter((row) => String(row[idKey]) !== String(item[idKey])));
      if (String(editingId) === String(item[idKey])) { setEditingId(""); setName(""); }
      setMessage(`${title.slice(0, -1)} deleted.`);
    } catch (error) {
      setMessage(error.message || `Unable to delete ${title.toLowerCase()}.`);
    }
  };
  return <section className="admin-view panel shifts-page organization-catalog">
    <div className="shifts-card-head"><div><h2>{title}</h2></div><button className="primary-action" type="button" onClick={openCreate} disabled={busy}><Plus size={15} /> Add {title.slice(0, -1).toLowerCase()}</button></div>
    {message && <p className="form-message" role="status">{message}</p>}
    <div className="shifts-table-wrap"><table className="attendance-table shifts-table"><thead><tr><th>Name</th><th>Actions</th></tr></thead><tbody>
      {items.length ? [...items].sort((a, b) => a.name.localeCompare(b.name)).map((item) => <tr key={item[idKey]}><td><strong>{item.name}</strong></td><td className="emp-actions"><button type="button" className="emp-action-btn" onClick={() => { setEditingId(item[idKey]); setName(item.name); setMessage(""); setOpen(true); }}><Pencil size={14} /> Edit</button><button type="button" className="emp-action-btn shift-delete-btn" disabled={busy} onClick={() => remove(item)}><Trash2 size={14} /> Delete</button></td></tr>) : <tr><td colSpan="2" className="attendance-no-results">No {title.toLowerCase()} found.</td></tr>}
    </tbody></table></div>
    {open && <div className="modal-backdrop"><form className="user-modal" onSubmit={save}><div className="modal-head"><h2>{editingId ? `Edit ${title.slice(0, -1).toLowerCase()}` : `Add ${title.slice(0, -1).toLowerCase()}`}</h2><button type="button" className="modal-close" onClick={() => setOpen(false)} aria-label="Close"><X size={16} /></button></div><div className="form-grid"><label>{title.slice(0, -1)} name<input autoFocus value={name} onChange={(event) => setName(event.target.value)} maxLength={100} required placeholder={`Enter ${title.slice(0, -1).toLowerCase()} name`} /></label></div>{message && <p className="form-message" role="status">{message}</p>}<div className="modal-actions"><button type="button" className="outline-action" onClick={() => setOpen(false)}>Cancel</button><button className="primary-action" disabled={busy}>{busy ? "Saving…" : editingId ? "Save changes" : `Add ${title.slice(0, -1).toLowerCase()}`}</button></div></form></div>}
  </section>;
}

export function KioskSignInScreen() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [kiosk, setKiosk] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [searchMode, setSearchMode] = useState("mobile");
  const [clockNow, setClockNow] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [searchingEmployees, setSearchingEmployees] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeStatus, setEmployeeStatus] = useState(null);
  const [shiftId, setShiftId] = useState("");
  const [shiftDropdownOpen, setShiftDropdownOpen] = useState(false);
  const [selfie, setSelfie] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [kioskProfileMenuOpen, setKioskProfileMenuOpen] = useState(false);
  const [locationResult, setLocationResult] = useState(null);
  const [message, setMessage] = useState("");
  const [attendanceFeedback, setAttendanceFeedback] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const feedbackTimerRef = useRef(null);
  const kioskProfileMenuRef = useRef(null);

  useEffect(() => {
    const timer = window.setInterval(() => setClockNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!kioskProfileMenuOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!kioskProfileMenuRef.current?.contains(event.target)) setKioskProfileMenuOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    return () => document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, [kioskProfileMenuOpen]);

  const kioskFetch = async (path, options = {}) => {
    const savedToken = localStorage.getItem("hiwaga_kiosk_access_token");
    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: { ...(options.headers || {}), Authorization: `Bearer ${savedToken}` },
    });
    const raw = response.status === 204 ? "" : await response.text();
    let data = null;
    try { data = raw ? JSON.parse(raw) : null; } catch { data = null; }
    if (!response.ok) throw new Error(data?.detail || raw || `Kiosk request failed (${response.status}).`);
    return data;
  };

  const stopKioskCamera = () => {
    cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
    cameraStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOpen(false);
  };

  const cancelKioskAttendance = () => {
    window.clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = null;
    stopKioskCamera();
    setSelectedEmployee(null);
    setEmployeeStatus(null);
    setSearch("");
    setShiftId("");
    setShiftDropdownOpen(false);
    setSelfie(null);
    if (selfiePreview) URL.revokeObjectURL(selfiePreview);
    setSelfiePreview("");
    setLocationResult(null);
    setMessage("");
    setAttendanceFeedback(null);
  };

  const showAttendanceFeedback = (type, text, completed = false) => {
    window.clearTimeout(feedbackTimerRef.current);
    setAttendanceFeedback({ type, text, completed });
    feedbackTimerRef.current = window.setTimeout(() => {
      feedbackTimerRef.current = null;
      cancelKioskAttendance();
    }, 3000);
  };

  const openKioskCamera = async () => {
    setMessage("");
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Camera access requires HTTPS or localhost and a supported browser.");
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      cameraStreamRef.current = stream;
      setCameraOpen(true);
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true;
          videoRef.current.setAttribute("playsinline", "true");
          videoRef.current.play().catch(() => {});
        }
      });
    } catch (cameraError) { setMessage(cameraError.message || "Allow camera access to capture a selfie."); }
  };

  const captureKioskSelfie = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (context) {
      context.save();
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      context.restore();
    }
    canvas.toBlob((blob) => {
      if (!blob) return;
      if (selfiePreview) URL.revokeObjectURL(selfiePreview);
      setSelfie(new File([blob], `kiosk-selfie-${Date.now()}.jpg`, { type: "image/jpeg" }));
      setSelfiePreview(URL.createObjectURL(blob));
      stopKioskCamera();
      setMessage("");
    }, "image/jpeg", 0.9);
  };

  const locateKiosk = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Location is not supported by this browser."));
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      try {
        const body = new FormData();
        body.append("latitude", coords.latitude);
        body.append("longitude", coords.longitude);
        const result = await kioskFetch("/kiosk-attendance/geofence", { method: "POST", body });
        setLocationResult({ ...result, latitude: coords.latitude, longitude: coords.longitude });
        resolve({ ...result, latitude: coords.latitude, longitude: coords.longitude });
      } catch (locationError) { reject(locationError); }
    }, () => reject(new Error("Allow location access to verify that you are inside the branch attendance area.")), {
      enableHighAccuracy: true, timeout: 15000, maximumAge: 0,
    });
  });

  const selectKioskEmployee = async (employee) => {
    setSelectedEmployee(employee);
    setEmployees([]);
    setSearch(employee.full_name);
    setEmployeeStatus(null);
    setShiftId("");
    setSelfie(null);
    if (selfiePreview) URL.revokeObjectURL(selfiePreview);
    setSelfiePreview("");
    setLocationResult(null);
    setMessage("");
    try {
      const status = await kioskFetch(`/kiosk-attendance/employees/${employee.employee_id}/status`);
      setEmployeeStatus(status);
      if (status.next_action === "completed") {
        showAttendanceFeedback("success", "Attendance completed for today.", true);
      }
    } catch (statusError) { setMessage(statusError.message); }
  };

  useEffect(() => {
    const query = search.trim();
    const minimumLength = searchMode === "mobile" ? 3 : 2;
    if (!kiosk || selectedEmployee || query.length < minimumLength) { setEmployees([]); setSearchingEmployees(false); return undefined; }
    const timer = setTimeout(async () => {
      setSearchingEmployees(true);
      try { setEmployees(await kioskFetch(`/kiosk-attendance/employees?q=${encodeURIComponent(query)}`)); }
      catch (searchError) { setMessage(searchError.message); }
      finally { setSearchingEmployees(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [kiosk, search, selectedEmployee, searchMode]);

  useEffect(() => () => {
    cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
    if (selfiePreview) URL.revokeObjectURL(selfiePreview);
  }, [selfiePreview]);

  useEffect(() => () => window.clearTimeout(feedbackTimerRef.current), []);

  useEffect(() => {
    let cancelled = false;
    const savedToken = localStorage.getItem("hiwaga_kiosk_access_token");
    if (!savedToken) {
      setCheckingSession(false);
      return () => { cancelled = true; };
    }
    fetch(`${API_URL}/kiosk-auth/me`, { headers: { Authorization: `Bearer ${savedToken}` } })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.detail || "Kiosk session expired. Sign in again.");
        if (!cancelled) setKiosk(data);
      })
      .catch((sessionError) => {
        localStorage.removeItem("hiwaga_kiosk_access_token");
        if (!cancelled) setError(sessionError.message || "Kiosk session expired. Sign in again.");
      })
      .finally(() => { if (!cancelled) setCheckingSession(false); });
    return () => { cancelled = true; };
  }, []);

  const signIn = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/kiosk-auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: identifier.trim(), password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "Unable to sign in to this kiosk.");
      localStorage.setItem("hiwaga_kiosk_access_token", data.access_token);
      setKiosk(data.kiosk);
      setPassword("");
    } catch (loginError) {
      setError(loginError.message || "Unable to sign in to this kiosk.");
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    const kioskToken = localStorage.getItem("hiwaga_kiosk_access_token");
    try {
      if (kioskToken) await fetch(`${API_URL}/kiosk-auth/logout`, { method: "POST", headers: { Authorization: `Bearer ${kioskToken}` } });
    } catch {
      // Always clear this browser's kiosk token if the server cannot be reached.
    } finally {
      localStorage.removeItem("hiwaga_kiosk_access_token");
      setKiosk(null);
      setIdentifier("");
      setPassword("");
      setError("");
      setSelectedEmployee(null);
      setEmployeeStatus(null);
      setSearch("");
      setSelfie(null);
      if (selfiePreview) URL.revokeObjectURL(selfiePreview);
      setSelfiePreview("");
      setLocationResult(null);
    }
  };

  const submitKioskAttendance = async () => {
    if (!selectedEmployee || !employeeStatus) return;
    if (employeeStatus.next_action === "check_in" && !shiftId) { setMessage("Select a shift before checking in."); return; }
    if (!selfie) { setMessage("Capture a selfie before submitting attendance."); return; }
    const submittedAction = employeeStatus.next_action;
    setIsBusy(true);
    setMessage("Verifying your location...");
    try {
      const location = await locateKiosk();
      if (!location.inside_geofence) throw new Error(location.message);
      const body = new FormData();
      body.append("employee_id", selectedEmployee.employee_id);
      body.append("latitude", location.latitude);
      body.append("longitude", location.longitude);
      body.append("device_info", navigator.userAgent);
      body.append("selfie", selfie);
      if (employeeStatus.next_action === "check_in") body.append("shift_id", shiftId);
      const result = await kioskFetch("/kiosk-attendance/submit", { method: "POST", body });
      setMessage("");
      showAttendanceFeedback(
        "success",
        submittedAction === "check_out" ? "Attendance completed for today." : result?.message || "Check-in recorded successfully.",
        submittedAction === "check_out",
      );
    } catch (submitError) {
      const failureMessage = submitError.message || "Unable to submit attendance.";
      // A response can be lost after the API commits. Refresh state so a retry
      // cannot accidentally perform the opposite action.
      try {
        const freshStatus = await kioskFetch(`/kiosk-attendance/employees/${selectedEmployee.employee_id}/status`);
        const attendanceWasRecorded = submittedAction === "check_in"
          ? freshStatus.next_action === "check_out"
          : freshStatus.next_action === "completed";
        if (attendanceWasRecorded) {
          setMessage("");
          showAttendanceFeedback(
            "success",
            submittedAction === "check_out" ? "Attendance completed for today." : "Check-in recorded successfully.",
            submittedAction === "check_out",
          );
          return;
        }
        setEmployeeStatus(freshStatus);
      } catch { /* Keep the original submission error visible. */ }
      setMessage("");
      showAttendanceFeedback("error", failureMessage);
    }
    finally { setIsBusy(false); }
  };

  const appendKioskDigit = (digit) => {
    setSearch((current) => `${current}${digit}`.slice(0, 15));
    setEmployees([]);
    setMessage("");
  };
  const clearKioskDigits = () => { setSearch(""); setEmployees([]); setSearchingEmployees(false); setMessage(""); };
  const removeKioskDigit = () => { setSearch((current) => current.slice(0, -1)); setEmployees([]); };

  return (
    <main className={kiosk ? "kiosk-login-shell" : "login-screen"}>
      <section className={kiosk ? "kiosk-login-card" : "login-card-simple"}>
        {kiosk ? <img src="/hiwaga-logo.png" alt="Hiwaga" className="kiosk-login-logo" /> : <div className="simple-logo-wrap"><img src="/hiwaga-logo.png" alt="Hiwaga logo" className="simple-logo" /></div>}
        {checkingSession ? <p className="kiosk-login-status">Checking kiosk session…</p> : kiosk ? (
          <div className={`kiosk-signed-in ${selectedEmployee ? "kiosk-selected-flow" : ""} ${cameraOpen ? "kiosk-camera-active" : ""}`}>
            <div className="kiosk-terminal-topline"><time>{clockNow.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}<small>{clockNow.toLocaleDateString([], { weekday: "long" }).toUpperCase()}, {clockNow.toLocaleDateString([], { month: "long" })} {clockNow.toLocaleDateString([], { day: "2-digit" })}</small></time><div className="kiosk-profile-menu-wrap" ref={kioskProfileMenuRef}><button type="button" className="kiosk-profile-button" aria-label="Kiosk account menu" aria-expanded={kioskProfileMenuOpen} onClick={() => setKioskProfileMenuOpen((open) => !open)}><UserRound size={21} /></button>{kioskProfileMenuOpen && <div className="kiosk-profile-menu"><button type="button" onClick={() => { setKioskProfileMenuOpen(false); signOut(); }}>Log out</button></div>}</div></div>
            <div className="kiosk-attendance-flow">
              {!selectedEmployee && <div className="kiosk-picker">
                <label className={`kiosk-terminal-input ${search ? "has-value" : ""}`}><input aria-label={searchMode === "mobile" ? "Employee ID or mobile number" : "Employee name"} value={search} readOnly={searchMode === "mobile"} inputMode={searchMode === "mobile" ? "none" : "text"} onChange={(event) => { setSearch(event.target.value); setEmployees([]); }} placeholder={searchMode === "mobile" ? "Mobile / Name" : "Search employee name"} autoComplete="off" />{search ? <button type="button" onClick={clearKioskDigits} aria-label="Clear search"><X size={17} /></button> : <Search className="kiosk-search-icon" size={19} aria-hidden="true" />}</label>
                {searchMode === "name" && <button type="button" className="kiosk-name-search" onClick={() => { setSearchMode("mobile"); setSearch(""); setEmployees([]); }}>123 · Search by number</button>}
                {employees.length > 0 && <div className="kiosk-employee-results">{employees.map((employee) => <button type="button" key={employee.employee_id} onClick={() => selectKioskEmployee(employee)}><span className="kiosk-result-avatar"><UserRound size={17} /></span><span className="kiosk-result-info"><strong>{employee.full_name}</strong><small>{employee.employee_code}</small></span></button>)}</div>}
                {searchMode === "mobile" && <div className="kiosk-number-pad" aria-label="Employee ID or mobile number keypad">{["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((digit) => <button type="button" key={digit} onClick={() => appendKioskDigit(digit)}>{digit}</button>)}<span aria-hidden="true" /><button type="button" onClick={() => appendKioskDigit("0")}>0</button><span aria-hidden="true" /><button type="button" className="kiosk-pad-action kiosk-pad-delete" onClick={removeKioskDigit} aria-label="Backspace" title="Backspace">⌫</button><button type="button" className="kiosk-pad-action kiosk-pad-abc" onClick={() => { setSearchMode("name"); setSearch(""); setEmployees([]); }}>ABC</button><button type="button" className="kiosk-pad-action kiosk-pad-refresh" onClick={clearKioskDigits} aria-label="Clear search" title="Clear search"><RefreshCw size={22} /></button></div>}
                {searchingEmployees && <p className="kiosk-search-hint">Searching employees…</p>}
                {!searchingEmployees && ((searchMode === "mobile" && search.length >= 3) || (searchMode === "name" && search.trim().length >= 2)) && employees.length === 0 && <p className="kiosk-search-hint">No matching active employees found for this branch.</p>}
              </div>}
              {attendanceFeedback ? <div className={`kiosk-attendance-feedback ${attendanceFeedback.type} ${attendanceFeedback.type === "success" && selectedEmployee ? "completed" : ""}`} role={attendanceFeedback.type === "error" ? "alert" : "status"}>{attendanceFeedback.type === "success" && selectedEmployee && <div className="kiosk-completed-employee"><div className="kiosk-employee-profile-icon"><UserRound size={38} /></div><strong>{employeeStatus?.full_name || selectedEmployee.full_name}</strong><span>{employeeStatus?.employee_code || selectedEmployee.employee_code}</span></div>}<div className="kiosk-attendance-feedback-message">{attendanceFeedback.type === "success" ? <CheckCircle2 size={22} /> : <X size={22} />}<span>{attendanceFeedback.text}</span></div></div> : selectedEmployee && employeeStatus && <>
                {!cameraOpen && <div className="kiosk-selected-employee kiosk-selected-employee-profile"><div className="kiosk-employee-profile-icon"><UserRound size={38} /></div><div className="kiosk-employee-profile-name"><strong>{employeeStatus.full_name}</strong><span>{employeeStatus.employee_code}</span></div></div>}
                {cameraOpen ? <div className="kiosk-camera-panel kiosk-camera-screen"><h2>Take employee selfie</h2><video ref={videoRef} autoPlay playsInline /><div><button type="button" className="outline-action" onClick={cancelKioskAttendance}>Cancel</button><button type="button" className="primary-action" onClick={captureKioskSelfie}><Camera size={16} /> Capture photo</button></div></div> : selfiePreview ? <>
                  <div className="kiosk-selfie-preview"><img src={selfiePreview} alt="Captured employee selfie" /><div className="kiosk-selfie-actions"><button type="button" className="outline-action" onClick={() => { setSelfie(null); URL.revokeObjectURL(selfiePreview); setSelfiePreview(""); openKioskCamera(); }}>Retake photo</button><button type="button" className="outline-action" disabled={isBusy} onClick={cancelKioskAttendance}>Cancel</button></div></div>
                  <button type="button" className={`primary-action kiosk-submit ${employeeStatus.next_action === "check_out" ? "kiosk-submit-checkout" : ""}`} disabled={isBusy} onClick={submitKioskAttendance}>{isBusy ? "Checking location…" : employeeStatus.next_action === "check_in" ? "Check in" : "Check out"}</button>
                </> : <>
                  {employeeStatus.next_action === "check_in" && <div className="kiosk-field"><span>Work shift</span>{employeeStatus.shifts.length ? <div className="kiosk-shift-select"><button type="button" className="kiosk-shift-select-trigger" aria-haspopup="listbox" aria-expanded={shiftDropdownOpen} onClick={() => setShiftDropdownOpen((open) => !open)}><span>{shiftId ? (() => { const chosenShift = employeeStatus.shifts.find((shift) => String(shift.shift_id) === String(shiftId)); return chosenShift ? `${chosenShift.name} (${chosenShift.start_time}–${chosenShift.end_time})` : "Select a shift"; })() : "Select a shift"}</span><ChevronDown size={16} /></button>{shiftDropdownOpen && <div className="kiosk-shift-options" role="listbox" aria-label="Work shifts">{employeeStatus.shifts.map((shift) => <button type="button" role="option" aria-selected={String(shiftId) === String(shift.shift_id)} key={shift.shift_id} onClick={() => { setShiftId(String(shift.shift_id)); setShiftDropdownOpen(false); }}>{shift.name} ({shift.start_time}–{shift.end_time})</button>)}</div>}</div> : <small>No active shifts are configured for this branch.</small>}</div>}
                  <button type="button" className="primary-action kiosk-submit kiosk-proceed" disabled={employeeStatus.next_action === "check_in" && !shiftId} onClick={openKioskCamera}>Proceed</button>
                </>}
              </>}
              <canvas ref={canvasRef} hidden />
              {message && <p className="kiosk-flow-message" role="status">{message}</p>}
            </div>
          </div>
        ) : (
          <>
            <h1 className="kiosk-auth-line">Kiosk sign in</h1>
            <form className="login-form-simple" onSubmit={signIn}>
              <label className="login-field-simple"><span>Mobile number or email</span><input autoFocus value={identifier} onChange={(event) => setIdentifier(event.target.value)} required autoComplete="username" placeholder="Kiosk mobile or email" /></label>
              <label className="login-field-simple"><span>Password</span><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required autoComplete="current-password" placeholder="Enter kiosk password" /></label>
              {error && <p className="kiosk-login-error" role="alert">{error}</p>}
              <button className="login-submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
            </form>
            <a className="kiosk-login-admin-link" href="/">Management dashboard</a>
          </>
        )}
      </section>
    </main>
  );
}

function KioskAccountsPage({ branches = [], staff = [], token = "" }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pageError, setPageError] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: "", branch_id: "", mobile_number: "", email: "", password: "" });
  const [formError, setFormError] = useState("");

  const loadAccounts = async () => {
    setLoading(true);
    setPageError("");
    try {
      setAccounts(await getJson("/kiosk-accounts", token));
    } catch (error) {
      setPageError(error.message || "Unable to load kiosk accounts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAccounts(); }, [token]);

  const openCreate = () => {
    setForm({ name: "", branch_id: "", mobile_number: "", email: "", password: "" });
    setFormError("");
    setModal({ mode: "create" });
  };
  const openEdit = (account) => {
    setForm({ name: account.name, branch_id: account.branch_id, mobile_number: account.mobile_number || "", email: account.email || "", password: "" });
    setFormError("");
    setModal({ mode: "edit", account });
  };
  const openPasswordReset = (account) => {
    setForm({ name: "", branch_id: "", mobile_number: "", email: "", password: "" });
    setFormError("");
    setModal({ mode: "reset", account });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (modal.mode !== "reset" && !form.branch_id) {
      setFormError("Select a branch for this kiosk account.");
      return;
    }
    setBusy(true);
    setFormError("");
    const isCreate = modal.mode === "create";
    const isReset = modal.mode === "reset";
    const path = isCreate
      ? "/kiosk-accounts"
      : isReset
        ? `/kiosk-accounts/${modal.account.kiosk_account_id}/reset-password`
        : `/kiosk-accounts/${modal.account.kiosk_account_id}`;
    const payload = isReset
      ? { password: form.password }
      : isCreate
        ? { ...form, email: form.email.trim() || null }
        : { name: form.name, branch_id: form.branch_id, mobile_number: form.mobile_number, email: form.email.trim() || null };
    try {
      const response = await apiFetch(path, {
        method: isCreate ? "POST" : isReset ? "POST" : "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }, token);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(normalizeErrorMessage(data, "Unable to save kiosk account."));
      setModal(null);
      await loadAccounts();
    } catch (error) {
      setFormError(error.message || "Unable to save kiosk account.");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (account) => {
    const verb = account.is_active ? "deactivate" : "activate";
    if (!window.confirm(`Are you sure you want to ${verb} kiosk account “${account.name}”?`)) return;
    setPageError("");
    try {
      const response = await apiFetch(`/kiosk-accounts/${account.kiosk_account_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: !account.is_active }),
      }, token);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(normalizeErrorMessage(data, `Unable to ${verb} kiosk account.`));
      await loadAccounts();
    } catch (error) {
      setPageError(error.message || `Unable to ${verb} kiosk account.`);
    }
  };

  const modalTitle = modal?.mode === "create" ? "Create kiosk account" : modal?.mode === "edit" ? "Edit kiosk account" : "Reset kiosk password";
  return (
    <section className="admin-view panel kiosk-accounts-page">
      <header className="kiosk-accounts-header">
        <div><h2>Kiosk accounts</h2><p>Create and manage branch-specific credentials for attendance kiosks.</p></div>
        <div className="kiosk-accounts-header-actions">
          <a className="outline-action" href="/kiosk" target="_blank" rel="noreferrer">Open kiosk sign-in</a>
          <button type="button" className="primary-action" onClick={openCreate}><Plus size={15} /> Create kiosk account</button>
        </div>
      </header>
      <p className="kiosk-accounts-note">These credentials are separate from employee dashboard accounts. Kiosk sign-in is available at /kiosk; employee attendance submission will be added next.</p>
      {pageError && <p className="kiosk-accounts-error" role="alert">{pageError}</p>}
      <div className="attendance-table-wrap">
        <table className="attendance-table emp-table">
          <thead><tr><th>Kiosk name</th><th>Mobile number</th><th>Email</th><th>Branch</th><th>Status</th><th>Created by</th><th>Created</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="8" className="attendance-no-results">Loading kiosk accounts…</td></tr>
              : accounts.length ? accounts.map((account) => {
                const branchName = branches.find((branch) => String(branch.branch_id) === String(account.branch_id))?.name || "Unknown branch";
                const creator = staff.find((person) => String(person.employee_id) === String(account.created_by_employee_id));
                return <tr key={account.kiosk_account_id}>
                  <td className="emp-name">{account.name}</td>
                  <td>{account.mobile_number || "—"}</td>
                  <td>{account.email || "—"}</td>
                  <td>{branchName}</td>
                  <td><span className={`attendance-status ${account.is_active ? "present" : "absent"}`}>{account.is_active ? "Active" : "Inactive"}</span></td>
                  <td>{creator?.full_name || creator?.employee_code || "Super Admin"}</td>
                  <td>{parseUTC(account.created_at)?.toLocaleString() || "—"}</td>
                  <td className="kiosk-account-actions">
                    <button type="button" className="emp-action-btn" onClick={() => openEdit(account)}>Edit</button>
                    <button type="button" className="emp-action-btn" onClick={() => openPasswordReset(account)}>Reset password</button>
                    <button type="button" className={`emp-action-btn ${account.is_active ? "kiosk-deactivate" : "kiosk-activate"}`} onClick={() => toggleActive(account)}>{account.is_active ? "Deactivate" : "Activate"}</button>
                  </td>
                </tr>;
              }) : <tr><td colSpan="8" className="attendance-no-results">No kiosk accounts yet. Create one to get started.</td></tr>}
          </tbody>
        </table>
      </div>
      {modal && <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) setModal(null); }}>
        <form className="user-modal kiosk-account-modal" onSubmit={submit}>
          <div className="modal-head"><h2>{modalTitle}</h2><button type="button" className="modal-close" onClick={() => setModal(null)} aria-label="Close" disabled={busy}><X size={16} /></button></div>
          {modal.mode !== "reset" ? <div className="form-grid">
            <label>Kiosk name<input autoFocus value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} maxLength={100} required placeholder="e.g. Reception kiosk" /></label>
            <label>Branch<SelectFilter value={String(form.branch_id || "")} onChange={(value) => { setForm((current) => ({ ...current, branch_id: value })); setFormError(""); }} placeholder="Select branch" options={branches.filter((branch) => branch.is_active !== false).map((branch) => ({ value: String(branch.branch_id), label: branch.name || `Branch ${branch.branch_id}` }))} /></label>
            <label>Mobile number<input type="tel" inputMode="tel" value={form.mobile_number} onChange={(event) => setForm((current) => ({ ...current, mobile_number: event.target.value }))} minLength={7} maxLength={16} pattern="\+?[0-9]{7,15}" required autoComplete="tel" placeholder="e.g. +919876543210" /></label>
            <label>Email (optional)<input type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} maxLength={254} autoComplete="email" placeholder="kiosk@example.com" /></label>
            {modal.mode === "create" && <label>Password<input type="password" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} minLength={12} maxLength={128} required autoComplete="new-password" /><small>Use at least 12 characters. The password is stored securely and cannot be viewed later.</small></label>}
          </div> : <label className="kiosk-reset-password">New password<input autoFocus type="password" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} minLength={12} maxLength={128} required autoComplete="new-password" /><small>Use at least 12 characters. The password is stored securely and cannot be viewed later.</small></label>}
          {formError && <p className="kiosk-accounts-error" role="alert">{formError}</p>}
          <div className="modal-actions"><button type="button" className="outline-action" onClick={() => setModal(null)} disabled={busy}>Cancel</button><button className="primary-action" disabled={busy}>{busy ? "Saving…" : modal.mode === "reset" ? "Reset password" : modal.mode === "edit" ? "Save changes" : "Create account"}</button></div>
        </form>
      </div>}
    </section>
  );
}

function BranchManagement({ branches, staff, attendance, token, onBranchesChange, openCreateRequested }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // "grid" | "list"
  const [branchSearchQuery, setBranchSearchQuery] = useState("");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    name: "",
    address_line: "",
    city: "",
    state: "",
    pincode: "",
    latitude: "",
    longitude: "",
    geofence_radius_m: "100",
    phone: "",
  });
  const update = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));
  
  const handleDeactivate = async (branch) => {
    const action = branch.is_active ? 'deactivate' : 'activate';
    const confirmMessage = branch.is_active 
      ? `Are you sure you want to deactivate ${branch.name}?`
      : `Are you sure you want to activate ${branch.name}?`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      const response = await apiFetch(
        `/branches/${branch.branch_id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_active: !branch.is_active }),
        },
        token,
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        alert(normalizeErrorMessage(error, `Unable to ${action} branch`));
        return;
      }

      const updatedBranch = await response.json().catch(() => null);
      if (updatedBranch) {
        onBranchesChange?.(updatedBranch);
      }
    } catch (error) {
      alert(`Failed to ${action} branch. Please try again.`);
    }
  };
  
  useEffect(() => {
    if (openCreateRequested) {
      setEditing(null);
      setForm({
        name: "",
        address_line: "",
        city: "",
        state: "",
        pincode: "",
        latitude: "",
        longitude: "",
        geofence_radius_m: "100",
        phone: "",
      });
      setMessage("");
      setOpen(true);
    }
  }, [openCreateRequested]);
  const openCreate = () => {
    setEditing(null);
    setForm({
      name: "",
      address_line: "",
      city: "",
        state: "",
      pincode: "",
      latitude: "",
      longitude: "",
      geofence_radius_m: "100",
      phone: "",
    });
    setMessage("");
    setOpen(true);
  };
  const openEdit = (branch) => {
    setEditing(branch);
    setForm({
      name: branch.name || "",
      address_line: branch.address_line || "",
      city: branch.city || "",
      state: branch.state || "",
      pincode: branch.pincode || "",
      latitude: branch.latitude ?? "",
      longitude: branch.longitude ?? "",
      geofence_radius_m: branch.geofence_radius_m ?? "100",
      phone: branch.phone || "",
    });
    setMessage("");
    setOpen(true);
  };
  const save = async (event) => {
    event.preventDefault();
    const latitude = Number.parseFloat(form.latitude);
    const longitude = Number.parseFloat(form.longitude);
    const geofenceRadius = Number.parseInt(form.geofence_radius_m, 10);
    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
      setMessage("Latitude must be a number between -90 and 90.");
      return;
    }
    if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
      setMessage("Longitude must be a number between -180 and 180.");
      return;
    }
    if (!Number.isFinite(geofenceRadius) || geofenceRadius < 1) {
      setMessage("Geofence radius must be at least 1 metre.");
      return;
    }
    setMessage(editing ? "Updating branch..." : "Creating branch...");
    const payload = {
      name: form.name.trim(),
      address_line: form.address_line.trim() || null,
      city: form.city.trim() || null,
      state: form.state.trim() || null,
      pincode: form.pincode.trim() || null,
      phone: form.phone.trim() || null,
      latitude: form.latitude.trim(),
      longitude: form.longitude.trim(),
      geofence_radius_m: geofenceRadius,
    };
    const response = await apiFetch(
      editing ? `/branches/${editing.branch_id}` : "/branches",
      {
        method: editing ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
      token,
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      setMessage(normalizeErrorMessage(error, "Unable to save branch"));
      return;
    }
    const savedBranch = await response.json().catch(() => null);
    if (savedBranch) onBranchesChange?.(savedBranch);
    setOpen(false);
    setEditing(null);
    setMessage("Branch saved successfully.");
  };
  const {
    page: branchPage,
    setPage: setBranchPage,
    totalPages: branchTotalPages,
    totalItems: branchTotalItems,
    pageData: pagedBranches,
    pageSize: branchPageSize,
  } = usePagination(
    branches.filter((branch) => {
      const query = branchSearchQuery.toLowerCase().trim();
      if (!query) return true;
      return (
        branch.name?.toLowerCase().includes(query) ||
        branch.city?.toLowerCase().includes(query) ||
        branch.state?.toLowerCase().includes(query) ||
        branch.address_line?.toLowerCase().includes(query)
      );
    }),
    5
  );

  const branchesListHeaderRef = useRef(null);
  const branchesListBodyRef = useRef(null);

  useEffect(() => {
    const header = branchesListHeaderRef.current;
    const body = branchesListBodyRef.current;
    if (!header || !body) return undefined;

    const syncHeaderToBody = () => {
      header.scrollLeft = body.scrollLeft;
    };
    const syncBodyToHeader = () => {
      body.scrollLeft = header.scrollLeft;
    };

    body.addEventListener("scroll", syncHeaderToBody, { passive: true });
    header.addEventListener("scroll", syncBodyToHeader, { passive: true });

    return () => {
      body.removeEventListener("scroll", syncHeaderToBody);
      header.removeEventListener("scroll", syncBodyToHeader);
    };
  }, [viewMode]);

  // Calculate staff and attendance stats per branch
  const getBranchStats = (branchId) => {
    const branchStaff = staff.filter(person => String(person.branch_id) === String(branchId));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const branchAttendance = attendance.filter(record => {
      const recordDate = new Date(record.timestamp);
      recordDate.setHours(0, 0, 0, 0);
      return recordDate.getTime() === today.getTime() && 
             branchStaff.some(person => String(person.employee_id) === String(record.employee_id));
    });
    const checkedInIds = new Set(
      branchAttendance
        .filter(record => record.check_type === "in")
        .map(record => String(record.employee_id))
    );
    const checkedInCount = checkedInIds.size;
    const totalStaff = branchStaff.length;
    const percentage = totalStaff > 0 ? Math.round((checkedInCount / totalStaff) * 100) : 0;
    return { totalStaff, checkedInCount, percentage };
  };

  return (
    <>
      <section className="branches-modern-page">
        <header className="branches-modern-header">
          <p>
            {branches.length} {branches.length === 1 ? "Branch" : "Branches"} - {branches.filter(b => b.is_active).length} checked in
          </p>
        </header>

        {/* Map View */}
        <div className="branches-map-container">
          <div className="branches-map-placeholder">
            {(() => {
              const located = pagedBranches.filter(b => b.latitude && b.longitude);
              if (!located.length) return null;
              // Compute bounding box with padding
              const lats = located.map(b => Number(b.latitude));
              const lngs = located.map(b => Number(b.longitude));
              const minLat = Math.min(...lats), maxLat = Math.max(...lats);
              const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
              // Add 10% padding so pins don't sit right at the edges
              const padLat = (maxLat - minLat) * 0.15 || 2;
              const padLng = (maxLng - minLng) * 0.15 || 2;
              const bMinLat = minLat - padLat, bMaxLat = maxLat + padLat;
              const bMinLng = minLng - padLng, bMaxLng = maxLng + padLng;
              return located.map((branch) => {
                // lat increases upward ? invert for CSS top
                const x = ((Number(branch.longitude) - bMinLng) / (bMaxLng - bMinLng)) * 100;
                const y = ((bMaxLat - Number(branch.latitude)) / (bMaxLat - bMinLat)) * 100;
                return (
                  <div
                    key={branch.branch_id}
                    className="branches-map-pin"
                    style={{ left: `${x}%`, top: `${y}%` }}
                  >
                    <div className="branches-map-pin-halo">
                      <div className="branches-map-pin-icon">
                        <MapPin size={14} />
                      </div>
                    </div>
                    <span className="branches-map-pin-label">{branch.city || branch.name}</span>
                  </div>
                );
              });
            })()}
          </div>
          <button className="branches-map-view-toggle">
            <Navigation size={14} />
            Geofence view
          </button>
        </div>

        {/* Search bar and view toggle */}
        <div className="branches-controls-row">
          <div className="branches-search-box">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search branches..."
              value={branchSearchQuery}
              onChange={(e) => setBranchSearchQuery(e.target.value)}
            />
          </div>
          <div className="branches-view-toggle">
            <button
              className={`branches-view-btn${viewMode === "grid" ? " active" : ""}`}
              onClick={() => setViewMode("grid")}
              title="Grid view"
            >
              <LayoutGrid size={15} />
            </button>
            <button
              className={`branches-view-btn${viewMode === "list" ? " active" : ""}`}
              onClick={() => setViewMode("list")}
              title="List view"
            >
              <List size={15} />
            </button>
          </div>
        </div>

        {/* Branch Cards Grid / List */}
        {viewMode === "grid" ? (
          <div className="branches-view-panel branches-grid-panel">
            <div className="branches-cards-grid">
              {pagedBranches.map((branch) => {
                const stats = getBranchStats(branch.branch_id);
                return (
                <div key={branch.branch_id} className="branch-card">
                  <div className="branch-card-header">
                    <h3>{branch.name}</h3>
                    <button className="branch-card-menu" onClick={() => openEdit(branch)}>
                      <MoreHorizontal size={16} />
                    </button>
                  </div>
                  <div className="branch-card-location">
                    <MapPin size={12} />
                    <span>{branch.address_line || branch.city || ""}</span>
                  </div>
                  <div className="branch-card-stats">
                    <div className="branch-stat">
                      <Users size={14} />
                      <div>
                        <strong>{stats.totalStaff}</strong>
                        <span>Staff</span>
                      </div>
                    </div>
                    <div className="branch-stat">
                      <CheckCircle2 size={14} />
                      <div>
                        <strong>{stats.checkedInCount}</strong>
                        <span>Checked in</span>
                      </div>
                    </div>
                  </div>
                  <div className="branch-card-progress">
                    <div className="branch-progress-bar">
                      <div className="branch-progress-fill" style={{ width: `${stats.percentage}%` }} />
                    </div>
                    <span className="branch-progress-label">{stats.percentage}% present</span>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="branches-view-panel branches-list-panel">
            <div className="attendance-table-wrap">
              <table className="attendance-table emp-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Branch</th>
                    <th>Code</th>
                    <th>Location</th>
                    <th>Geofence</th>
                    <th>Staff</th>
                    <th>Checked In</th>
                    <th>Attendance</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedBranches.map((branch, index) => {
                    const stats = getBranchStats(branch.branch_id);
                    return (
                      <tr key={branch.branch_id}>
                        <td>{(branchPage - 1) * branchPageSize + index + 1}</td>
                        <td className="branch-name-cell">
                          {branch.name}
                        </td>
                        <td>
                          {branch.branch_code ? (
                            <span style={{color: '#64748b', fontSize: '12px', fontFamily: 'monospace'}}>{branch.branch_code.toUpperCase()}</span>
                          ) : (
                            <span style={{color: '#cbd5e1'}}>-</span>
                          )}
                        </td>
                        <td>{branch.city || branch.address_line || ""}</td>
                        <td>{branch.geofence_radius_m ? `${branch.geofence_radius_m} m` : "-"}</td>
                        <td>{stats.totalStaff}</td>
                        <td>{stats.checkedInCount}</td>
                        <td>
                          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <div className="branch-progress-bar" style={{flex: 1}}>
                              <div className="branch-progress-fill" style={{ width: `${stats.percentage}%` }} />
                            </div>
                            <span className="branch-pct">{stats.percentage}%</span>
                          </div>
                        </td>
                        <td>
                          <span className={`attendance-status ${branch.is_active ? "present" : "absent"}`}>
                            {branch.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div style={{display: 'flex', gap: '8px'}}>
                            <button className="emp-action-btn" onClick={() => openEdit(branch)}>Edit</button>
                            <button 
                              className="emp-action-btn" 
                              style={{
                                backgroundColor: branch.is_active ? '#fee2e2' : '#dcfce7',
                                color: branch.is_active ? '#dc2626' : '#16a34a'
                              }}
                              onClick={() => handleDeactivate(branch)}
                            >
                              {branch.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!pagedBranches.length && (
                    <tr>
                      <td colSpan="10" className="attendance-no-results">No branches available.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <Pagination
          page={branchPage}
          totalPages={branchTotalPages}
          totalItems={branchTotalItems}
          pageSize={branchPageSize}
          onPageChange={setBranchPage}
        />
      </section>
      {open && (
        <div className="modal-backdrop">
          <form className="user-modal" onSubmit={save}>
            <div className="modal-head">
              <div>
                <h2>{editing ? "Modify branch" : "Create Branch"}</h2>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={() => setOpen(false)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="form-grid">
              <label>
                Branch name
                <input
                  value={form.name}
                  onChange={(event) => update("name", event.target.value)}
                  required
                />
              </label>
              <label>
                Phone
                <input
                  value={form.phone}
                  onChange={(event) => update("phone", event.target.value)}
                />
              </label>
              <label>
                Address
                <input
                  value={form.address_line}
                  onChange={(event) =>
                    update("address_line", event.target.value)
                  }
                />
              </label>
              <label>
                City
                <input
                  value={form.city}
                  onChange={(event) => update("city", event.target.value)}
                />
              </label>
              <label>
                State
                <input
                  value={form.state}
                  onChange={(event) => update("state", event.target.value)}
                />
              </label>
              <label>
                Pincode
                <input
                  value={form.pincode}
                  onChange={(event) => update("pincode", event.target.value)}
                />
              </label>
              <label>
                Latitude
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="-?[0-9]+(\.[0-9]+)?"
                  value={form.latitude}
                  onChange={(event) => update("latitude", event.target.value)}
                  required
                />
              </label>
              <label>
                Longitude
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="-?[0-9]+(\.[0-9]+)?"
                  value={form.longitude}
                  onChange={(event) => update("longitude", event.target.value)}
                  required
                />
              </label>
              <label>
                Geofence radius (metres)
                <input
                  type="number"
                  min="1"
                  value={form.geofence_radius_m}
                  onChange={(event) =>
                    update("geofence_radius_m", event.target.value)
                  }
                />
              </label>
            </div>
            {message && <p className="form-message">{message}</p>}
            <div className="modal-actions">
              <button
                type="button"
                className="outline-action"
                onClick={() => setOpen(false)}
              >
                Cancel
              </button>
              <button className="primary-action">
                {editing ? "Save changes" : "Create branch"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

function EmployeeArrayEditor({ title, rows, fields, onChange, addLabel }) {
  const values = Array.isArray(rows) ? rows : [];
  const updateRow = (index, key, value) => onChange(values.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row));
  return <section className="employee-array-editor">
    <header><div><h3>{title}</h3><p>Add any number of records. Leave unused rows out.</p></div><button type="button" onClick={() => onChange([...values, Object.fromEntries(fields.map((field) => [field.key, ""]))])}>+ {addLabel}</button></header>
    {values.length === 0 ? <p className="employee-array-empty">No records added.</p> : values.map((row, index) => <div className="employee-array-row" key={row.id || `${title}-${index}`}>
      <span className="employee-array-index">{index + 1}</span>
      {fields.map((field) => <label key={field.key}>{field.label}<input type={field.type || "text"} min={field.min} max={field.max} step={field.step} value={row[field.key] ?? ""} onChange={(event) => updateRow(index, field.key, event.target.value)} /></label>)}
      <button className="employee-array-remove" type="button" aria-label={`Remove ${title.toLowerCase()} row ${index + 1}`} onClick={() => onChange(values.filter((_, rowIndex) => rowIndex !== index))}><Trash2 size={15}/></button>
    </div>)}
  </section>;
}

function EmployeeArrayDisplay({ title, rows, columns }) {
  const values = Array.isArray(rows) ? rows : [];
  return <section className="employee-array-display"><h3>{title}</h3>{values.length ? <div className="employee-array-table-wrap"><table><thead><tr>{columns.map(([label]) => <th key={label}>{label}</th>)}</tr></thead><tbody>{values.map((row, index) => <tr key={row.id || `${title}-${index}`}>{columns.map(([label, key]) => <td key={key}>{row[key] ?? "—"}</td>)}</tr>)}</tbody></table></div> : <p>No {title.toLowerCase()} records.</p>}</section>;
}

function UserManagement({ staff, branches, attendance = [], departments: departmentRecords = [], designations: designationRecords = [], token, openCreateRequested }) {
  const [open, setOpen] = useState(false);
  const [attendanceBranchesOpen, setAttendanceBranchesOpen] = useState(false);
  const attendanceBranchesRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkWarn, setBulkWarn] = useState(false);
  const [employeeHistory, setEmployeeHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [serviceHistoryError, setServiceHistoryError] = useState("");
  const [viewEmployee, setViewEmployee] = useState(null);
  const closeViewEmployee = () => {
    setViewEmployee(null);
    if (/^\/employees\/[^/]+\/?$/i.test(window.location.pathname)) navigateToPath("/employees");
  };
  const [employeeArraysAvailable, setEmployeeArraysAvailable] = useState(false);
  const [message, setMessage] = useState("");
  const [query, setQuery] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [roleList, setRoleList] = useState([]);
  const [mainFilter, setMainFilter] = useState("active");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // "table" or "grid"

  useEffect(() => {
    if (!attendanceBranchesOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (attendanceBranchesRef.current && !attendanceBranchesRef.current.contains(event.target)) {
        setAttendanceBranchesOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [attendanceBranchesOpen]);

  const currentRole = getEffectiveCurrentRole(staff, localStorage.getItem("hiwaga_role") || "");
  const canManageUsers =
    currentRole === "super_admin" ||
    currentRole === "branch_manager" ||
    currentRole === "floor_manager";
  const canViewEmployeeHistory = ["super_admin", "hr", "branch_manager", "floor_manager"].includes(currentRole);

  const createEmptyEmployeeForm = () => ({
    branch_id: branches[0]?.branch_id || "",
    attendance_branch_ids: [],
    role_code: "staff",
    full_name: "",
    father_husband_name: "",
    phone: "",
    email: "",
    gender: "",
    date_of_birth: "",
    category: "",
    marital_status: "",
    aadhaar_number: "",
    address: "",
    designation: "",
    department: "",
    date_of_joining: "",
    last_working_day: "",
    uan_number: "",
    epf_number: "",
    esic_number: "",
    previous_employment_joining_date: "",
    pay_scale: "",
    basic_salary: "",
    hra: "",
    other_allowances: "",
    pan_number: "",
    bank_account_number: "",
    bank_ifsc: "",
    bank_branch: "",
    bank_name: "",
    family_members: [],
    educations: [],
    previous_employments: [],
    initial_password: "",
  });

  const [form, setForm] = useState(createEmptyEmployeeForm);
  const update = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));
  const closeEmployeeModal = () => {
    setOpen(false);
    setEditing(null);
    setEmployeeArraysAvailable(false);
    setMessage("");
    setForm(createEmptyEmployeeForm());
  };
  useEffect(() => {
    if (openCreateRequested) {
      setEditing(null);
      setEmployeeArraysAvailable(true);
      setForm(createEmptyEmployeeForm());
      setMessage("");
      setOpen(true);
    }
  }, [openCreateRequested, branches]);
  useEffect(() => {
    if (!editing && !form.branch_id && branches[0]?.branch_id)
      update("branch_id", branches[0].branch_id);
  }, [branches, editing, form.branch_id]);
  const openCreate = () => {
    setEditing(null);
    setEmployeeArraysAvailable(true);
    setForm(createEmptyEmployeeForm());
    setMessage("");
    setOpen(true);
  };
  const employeeFormValues = (person) => ({
      branch_id: person.branch_id ?? "",
      attendance_branch_ids: Array.isArray(person.attendance_branch_ids) ? person.attendance_branch_ids.map(String) : [],
      role_code: person.role_code || "",
      full_name: person.full_name || "",
      father_husband_name: person.father_husband_name || "",
      phone: person.phone || "",
      email: person.email || "",
      gender: person.gender || "",
      date_of_birth: person.date_of_birth ? person.date_of_birth.slice(0, 10) : "",
      category: person.category || "",
      marital_status: person.marital_status || "",
      aadhaar_number: person.aadhaar_number || "",
      address: person.address || "",
      designation: person.designation || "",
      department: person.department || "",
      date_of_joining: person.date_of_joining ? person.date_of_joining.slice(0, 10) : "",
      last_working_day: person.last_working_day ? person.last_working_day.slice(0, 10) : "",
      uan_number: person.uan_number || "",
      epf_number: person.epf_number || "",
      esic_number: person.esic_number || "",
      previous_employment_joining_date: person.previous_employment_joining_date
        ? person.previous_employment_joining_date.slice(0, 10)
        : "",
      pay_scale: person.pay_scale || "",
      basic_salary: person.basic_salary ?? "",
      hra: person.hra ?? "",
      other_allowances: person.other_allowances ?? "",
      pan_number: person.pan_number || "",
      bank_account_number: person.bank_account_number || "",
      bank_ifsc: person.bank_ifsc || "",
      bank_branch: person.bank_branch || "",
      bank_name: person.bank_name || "",
      family_members: Array.isArray(person.family_members) ? person.family_members.map((row) => ({ ...row, date_of_birth: row.date_of_birth ? String(row.date_of_birth).slice(0, 10) : "" })) : [],
      educations: Array.isArray(person.educations) ? person.educations.map((row) => ({ ...row })) : [],
      previous_employments: Array.isArray(person.previous_employments) ? person.previous_employments.map((row) => ({ ...row, from_date: row.from_date ? String(row.from_date).slice(0, 10) : "", to_date: row.to_date ? String(row.to_date).slice(0, 10) : "" })) : [],
      initial_password: "",
    });
  const loadEmployeeDetails = async (person) => {
    const response = await apiFetch(`/staff/${encodeURIComponent(person.employee_id)}`, {}, token);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.detail || "Unable to load employee details.");
    return { ...person, ...data };
  };
  const openEdit = async (person) => {
    setEditing(person);
    setEmployeeArraysAvailable(false);
    setMessage("Loading employee details…");
    setForm(employeeFormValues(person));
    setOpen(true);
    try {
      const detail = await loadEmployeeDetails(person);
      setEditing(detail);
      setEmployeeArraysAvailable(["family_members", "educations", "previous_employments"].every((key) => Array.isArray(detail[key])));
      setForm(employeeFormValues(detail));
      setMessage("");
    } catch (error) {
      setMessage(error.message || "Unable to load employee details.");
    }
  };
  const openViewEmployee = async (person) => {
    setViewEmployee(person);
    navigateToPath(`/employees/${encodeURIComponent(person.employee_code || person.employee_id)}`);
    try {
      const detail = await loadEmployeeDetails(person);
      setViewEmployee(detail);
    } catch (error) {
      setMessage(error.message || "Unable to load employee details.");
    }
  };
  useEffect(() => {
    const syncEmployeeRoute = () => {
      const match = window.location.pathname.match(/^\/employees\/([^/]+)\/?$/i);
      const employeeCode = match ? decodeURIComponent(match[1]) : "";
      if (!employeeCode) {
        setViewEmployee(null);
        return;
      }
      const person = staff.find((candidate) =>
        String(candidate.employee_code || candidate.employee_id).toLowerCase() === employeeCode.toLowerCase(),
      );
      if (person && String(viewEmployee?.employee_id) !== String(person.employee_id)) {
        setViewEmployee(person);
        loadEmployeeDetails(person).then(setViewEmployee).catch((loadError) => setMessage(loadError.message || "Unable to load employee details."));
      }
    };
    syncEmployeeRoute();
    window.addEventListener("popstate", syncEmployeeRoute);
    return () => window.removeEventListener("popstate", syncEmployeeRoute);
  }, [staff]); // Route changes are handled by popstate; staff changes resolve deep links after loading.
  const save = async (event) => {
    event.preventDefault();

    if (!editing) {
      const requiredMessage =
        !form.branch_id
          ? "Branch is required."
          : !form.full_name?.trim()
            ? "Full name is required."
            : !form.phone?.trim()
              ? "Mobile number is required."
              : !form.role_code?.trim()
                ? "Role is required."
                : !form.initial_password?.trim()
                  ? "Password is required."
                  : "";

      if (requiredMessage) {
        setMessage(requiredMessage);
        return;
      }
    }

    const arraySpecs = [
      ["family_members", "name", "relationship", "Family members"],
      ["educations", "qualification", null, "Education"],
      ["previous_employments", "organization", null, "Previous employment"],
    ];
    for (const [key, requiredField, secondaryRequiredField, label] of arraySpecs) {
      const hasIncompleteRow = (form[key] || []).some((row) => {
        const hasData = Object.values(row).some((value) => value !== null && value !== undefined && String(value).trim() !== "");
        return hasData && (!String(row[requiredField] || "").trim() || (secondaryRequiredField && !String(row[secondaryRequiredField] || "").trim()));
      });
      if (hasIncompleteRow) {
        setMessage(`${label}: complete the required fields in each added row.`);
        return;
      }
    }

    setMessage("Saving...");
    const payload = {};

    const pushIfPresent = (key, value) => {
      if (value === undefined || value === null) return;
      const stringValue = typeof value === "string" ? value.trim() : value;
      if (stringValue === "") return;
      payload[key] = stringValue;
    };

    const pushNumericIfPresent = (key, value) => {
      if (value === undefined || value === null || value === "") return;
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) payload[key] = numeric;
    };

    if (editing) {
      pushIfPresent("branch_id", form.branch_id);
      payload.attendance_branch_ids = form.attendance_branch_ids || [];
      pushIfPresent("role_code", form.role_code);
      pushIfPresent("full_name", form.full_name);
      pushIfPresent("father_husband_name", form.father_husband_name);
      pushIfPresent("phone", form.phone);
      pushIfPresent("email", form.email);
      pushIfPresent("gender", form.gender);
      pushIfPresent("date_of_birth", form.date_of_birth);
      pushIfPresent("category", form.category);
      pushIfPresent("marital_status", form.marital_status);
      pushIfPresent("aadhaar_number", form.aadhaar_number);
      pushIfPresent("address", form.address);
      pushIfPresent("designation", form.designation);
      pushIfPresent("department", form.department);
      pushIfPresent("date_of_joining", form.date_of_joining);
      payload.last_working_day = form.last_working_day || null;
      pushIfPresent("uan_number", form.uan_number);
      pushIfPresent("epf_number", form.epf_number);
      pushIfPresent("esic_number", form.esic_number);
      pushIfPresent("previous_employment_joining_date", form.previous_employment_joining_date);
      pushIfPresent("pay_scale", form.pay_scale);
      pushNumericIfPresent("basic_salary", form.basic_salary);
      pushNumericIfPresent("hra", form.hra);
      pushNumericIfPresent("other_allowances", form.other_allowances);
      pushIfPresent("pan_number", form.pan_number);
      pushIfPresent("bank_account_number", form.bank_account_number);
      pushIfPresent("bank_ifsc", form.bank_ifsc);
      pushIfPresent("bank_branch", form.bank_branch);
      pushIfPresent("bank_name", form.bank_name);
      pushIfPresent("initial_password", form.initial_password);
      if (form.employment_status) payload.employment_status = form.employment_status;
    } else {
      pushIfPresent("branch_id", form.branch_id);
      pushIfPresent("role_code", form.role_code);
      pushIfPresent("full_name", form.full_name);
      pushIfPresent("father_husband_name", form.father_husband_name);
      pushIfPresent("phone", form.phone);
      pushIfPresent("email", form.email);
      pushIfPresent("gender", form.gender);
      pushIfPresent("date_of_birth", form.date_of_birth);
      pushIfPresent("category", form.category);
      pushIfPresent("marital_status", form.marital_status);
      pushIfPresent("aadhaar_number", form.aadhaar_number);
      pushIfPresent("address", form.address);
      pushIfPresent("designation", form.designation);
      pushIfPresent("department", form.department);
      pushIfPresent("date_of_joining", form.date_of_joining);
      payload.last_working_day = form.last_working_day || null;
      pushIfPresent("uan_number", form.uan_number);
      pushIfPresent("epf_number", form.epf_number);
      pushIfPresent("esic_number", form.esic_number);
      pushIfPresent("previous_employment_joining_date", form.previous_employment_joining_date);
      pushIfPresent("pay_scale", form.pay_scale);
      pushNumericIfPresent("basic_salary", form.basic_salary);
      pushNumericIfPresent("hra", form.hra);
      pushNumericIfPresent("other_allowances", form.other_allowances);
      pushIfPresent("pan_number", form.pan_number);
      pushIfPresent("bank_account_number", form.bank_account_number);
      pushIfPresent("bank_ifsc", form.bank_ifsc);
      pushIfPresent("bank_branch", form.bank_branch);
      pushIfPresent("bank_name", form.bank_name);
      pushIfPresent("initial_password", form.initial_password);
    }

    if (!editing || employeeArraysAvailable) {
      payload.family_members = (form.family_members || []).filter((row) => String(row.name || "").trim() && String(row.relationship || "").trim()).map((row) => ({
        name: row.name.trim(), relationship: row.relationship.trim(), date_of_birth: row.date_of_birth || null, aadhaar_number: row.aadhaar_number?.trim() || null,
      }));
      payload.educations = (form.educations || []).filter((row) => String(row.qualification || "").trim()).map((row) => ({
        qualification: row.qualification.trim(), year_of_passing: row.year_of_passing === "" || row.year_of_passing == null ? null : Number(row.year_of_passing),
        institute_university: row.institute_university?.trim() || null, marks_percentage: row.marks_percentage === "" || row.marks_percentage == null ? null : Number(row.marks_percentage),
      }));
      payload.previous_employments = (form.previous_employments || []).filter((row) => String(row.organization || "").trim()).map((row) => ({
        organization: row.organization.trim(), from_date: row.from_date || null, to_date: row.to_date || null, nature_of_work: row.nature_of_work?.trim() || null,
      }));
    }

    const response = await apiFetch(
      editing ? `/staff/${editing.employee_id}` : "/staff",
      {
        method: editing ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
      token,
    );
    const savedEmployee = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = savedEmployee || {};
      setMessage(normalizeErrorMessage(error, "Unable to save user"));
      return;
    }
    const expectedLastWorkingDay = form.last_working_day || null;
    const savedLastWorkingDay = savedEmployee?.last_working_day
      ? String(savedEmployee.last_working_day).slice(0, 10)
      : null;
    if (savedLastWorkingDay !== expectedLastWorkingDay) {
      setMessage("The server did not save the Last Working Day. Restart the backend so it loads the latest employee schema, then save again.");
      return;
    }
    const currentEmployeeId = localStorage.getItem("hiwaga_employee_id");
    if (editing && String(editing.employee_id) === String(currentEmployeeId) && form.role_code) {
      localStorage.setItem("hiwaga_role", form.role_code);
    }
    setMessage("Saved. Refreshing...");
    closeEmployeeModal();
    window.location.reload();
  };
  const viewEmployeeHistory = async (person) => {
    setEmployeeHistory({ employee: person, entries: [], services: [] });
    setHistoryLoading(true);
    setHistoryError("");
    setServiceHistoryError("");
    try {
      const query = new URLSearchParams({ entity_type: "employee", entity_id: person.employee_id });
      const [auditResponse, serviceResponse] = await Promise.all([
        apiFetch(`/audit-logs?${query.toString()}`, {}, token),
        apiFetch(`/staff/${person.employee_id}/service-history`, {}, token),
      ]);
      const [auditData, serviceData] = await Promise.all([
        auditResponse.json().catch(() => ({})),
        serviceResponse.json().catch(() => ({})),
      ]);
      if (!auditResponse.ok) {
        throw new Error(normalizeErrorMessage(auditData, "Unable to load employee activity history."));
      }
      setServiceHistoryError(
        serviceResponse.ok
          ? ""
          : normalizeErrorMessage(serviceData, "Unable to load employee service history."),
      );
      setEmployeeHistory({
        employee: person,
        entries: Array.isArray(auditData) ? auditData : [],
        services: serviceResponse.ok && Array.isArray(serviceData) ? serviceData : [],
      });
    } catch (error) {
      setHistoryError(error.message || "Unable to load employee history.");
    } finally {
      setHistoryLoading(false);
    }
  };
  const auditFieldLabels = {
    full_name: "Name",
    father_husband_name: "Father / spouse name",
    phone: "Mobile",
    email: "Email",
    gender: "Gender",
    date_of_birth: "Date of birth",
    category: "Category",
    marital_status: "Marital status",
    address: "Address",
    designation: "Designation",
    department: "Department",
    date_of_joining: "Date of joining",
    last_working_day: "Last working day",
    employment_status: "Employment status",
    branch_id: "Branch",
    role_code: "Role",
    employee_code: "Employee code",
    family_members: "Family members",
    educations: "Education",
    previous_employments: "Previous employment",
  };
  const getAuditChanges = (entry) => {
    const oldValues = entry.old_values || {};
    const newValues = entry.new_values || {};
    return [...new Set([...Object.keys(oldValues), ...Object.keys(newValues)])]
      .filter((key) => auditFieldLabels[key] && oldValues[key] !== newValues[key])
      .map((key) => ({ key, oldValue: oldValues[key], newValue: newValues[key] }));
  };
  const formatAuditValue = (key, value) => {
    if (value === null || value === undefined || value === "") return "—";
    if (key === "branch_id") return branches.find((branch) => String(branch.branch_id) === String(value))?.name || value;
    if (["date_of_birth", "date_of_joining", "last_working_day"].includes(key) || key.endsWith("_date")) {
      const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);
      return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
    }
    return String(value).replace(/_/g, " ");
  };
  const deactivate = async (person) => {
    if (!window.confirm(`Deactivate ${person.full_name}?`)) return;
    await apiFetch(
      `/staff/${person.employee_id}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employment_status: "inactive" }),
      },
      token,
    );
    window.location.reload();
  };
  const activate = async (person) => {
    if (!window.confirm(`Activate ${person.full_name}?`)) return;
    await apiFetch(
      `/staff/${person.employee_id}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employment_status: "active" }),
      },
      token,
    );
    window.location.reload();
  };
  const departments = departmentRecords.map((item) => item.name).filter(Boolean).sort();
  const designations = designationRecords.map((item) => item.name).filter(Boolean).sort();

  useEffect(() => {
    let ignore = false;
    async function loadRoles() {
      try {
        const response = await apiFetch("/roles", {}, token);
        if (!response.ok) return;
        const data = await response.json();
        if (ignore) return;
        const rolesFromApi = Array.isArray(data)
          ? data
          : Array.isArray(data.roles)
            ? data.roles
            : [];
        const normalized = rolesFromApi.map((role) => {
          if (typeof role === "string") return { value: role, label: role };
          const value = role.role_code || role.code || role.name || role.value || "";
          const label = role.label || role.role_name || role.name || role.role_code || value;
          return { value, label };
        }).filter((role) => role.value && role.value.trim());
        setRoleList(normalized);
      } catch {
        // ignore role fetch errors; fallback to staff-derived roles below
      }
    }
    if (token) loadRoles();
    return () => { ignore = true; };
  }, [token]);

  const extractedRoles = [...new Set(staff.map((p) => {
    const role = p.role_code || p.role || p.user_role;
    return role?.toString().trim();
  }).filter(Boolean))].sort();
  const roles = roleList.length > 0
    ? roleList.map((role) => ({ value: role.value, label: role.label }))
    : extractedRoles.length > 0
      ? extractedRoles.map((role) => ({ value: role, label: role.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') }))
      : [
          { value: "staff", label: "Staff" },
          { value: "floor_manager", label: "Floor Manager" },
          { value: "branch_manager", label: "Branch Manager" },
          { value: "super_admin", label: "Super Admin" },
        ];
  const availableRoles = currentRole === "branch_manager"
    ? roles.filter((role) => ["staff", "floor_manager"].includes(normalizeRoleKey(role.value)))
    : roles;
  const roleOptions = availableRoles.map((role) => ({
    value: role.value,
    label: role.label,
    icon:
      normalizeRoleKey(role.value) === "super_admin" ? <ShieldCheck size={16} /> :
      normalizeRoleKey(role.value) === "branch_manager" ? <Briefcase size={16} /> :
      normalizeRoleKey(role.value) === "floor_manager" ? <PersonStanding size={16} /> :
      <UserRound size={16} />,
  }));
  const visibleStaff = staff.filter((person) => {
    const q = query.trim().toLowerCase();
    const matchesQuery =
      !q ||
      (person.full_name || "").toLowerCase().includes(q) ||
      String(person.employee_id || "").toLowerCase().includes(q) ||
      (person.employee_code || "").toLowerCase().includes(q) ||
      (person.email || "").toLowerCase().includes(q) ||
      (person.phone || "").toLowerCase().includes(q);
    const matchesBranch = !branchFilter || String(person.branch_id) === branchFilter;
    const matchesDept = !deptFilter || person.department?.trim() === deptFilter;
    const matchesDesignation = !designationFilter || person.designation?.trim() === designationFilter;
    const personRole = (person.role_code || person.role || person.user_role)?.toString().trim();
    const matchesRole = !roleFilter || personRole === roleFilter;
    
    // Debug role filtering
    if (roleFilter) {
      console.log(`Person: ${person.full_name}, role_code: ${person.role_code}, personRole: ${personRole}, roleFilter: ${roleFilter}, matches: ${matchesRole}`);
    }
    
    const matchesStatus =
      !statusFilter ||
      (person.employment_type || person.employment_status || "").toLowerCase() === statusFilter;
    // Main filter: active, inactive, ex-employee, admin
    const empStatus = (person.employment_type || person.employment_status || "").toLowerCase();
    const roleCode = (person.role_code || "").toLowerCase();
    const matchesMain =
      mainFilter === "active"      ? empStatus === "active" || empStatus === "" :
      mainFilter === "inactive"    ? empStatus === "inactive" :
      mainFilter === "ex-employee" ? empStatus === "ex-employee" || empStatus === "terminated" || empStatus === "resigned" :
      mainFilter === "admin"       ? ["super_admin", "hr", "branch_manager", "floor_manager"].includes(roleCode) :
      true;
    return matchesQuery && matchesBranch && matchesDept && matchesDesignation && matchesRole && matchesStatus && matchesMain;
  });
  const hasFilters = branchFilter || deptFilter || designationFilter || roleFilter || statusFilter;
  
  // Responsive page size for mobile
  const [empPageSizeValue, setEmpPageSizeValue] = useState(
    window.innerWidth <= 768 ? 7 : 15
  );
  useEffect(() => {
    const handleResize = () => {
      setEmpPageSizeValue(window.innerWidth <= 768 ? 7 : 15);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const {
    page: empPage,
    setPage: setEmpPage,
    totalPages: empTotalPages,
    totalItems: empTotalItems,
    pageData: pagedStaff,
    pageSize: empPageSize,
  } = usePagination(visibleStaff, empPageSizeValue);
  useEffect(() => { setEmpPage(1); }, [query, branchFilter, deptFilter, designationFilter, roleFilter, statusFilter, mainFilter]); // eslint-disable-line
  // Clear sub-filters when switching main category
  useEffect(() => {
    setBranchFilter("");
    setDeptFilter("");
    setDesignationFilter("");
    setRoleFilter("");
    setStatusFilter("");
    setQuery("");
  }, [mainFilter]); // eslint-disable-line

  return (
    <>
      <section className="attendance-page employees-page">
        <header className="attendance-page-head">
          <h1>Employees</h1>
          <div className="emp-header-actions">
            <div className="emp-mobile-header-search">
              <Search size={14} />
              <input
                aria-label="Search employees"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search employees"
              />
              {query && (
                <button type="button" aria-label="Clear employee search" onClick={() => setQuery("")}>
                  <X size={13} />
                </button>
              )}
            </div>
            <button className="emp-add-btn" onClick={openCreate}>
              + Add Emp
            </button>
          </div>
        </header>

        {/* Toolbar row - buttons right, selection info left when active */}
        <div className="emp-table-toolbar">
          <div className="emp-table-toolbar-top">
            <div className="emp-table-toolbar-left">
            {selectedIds.size > 0 ? (
              <span className="emp-bulk-count">
                <strong>{selectedIds.size}</strong> employee{selectedIds.size > 1 ? "s" : ""} selected
              </span>
            ) : (
              <div className={`emp-mobile-filters-box ${mobileFiltersOpen ? "is-open" : ""}`}>
                <button
                  type="button"
                  className="emp-mobile-filters-toggle"
                  onClick={() => setMobileFiltersOpen((open) => !open)}
                  aria-expanded={mobileFiltersOpen}
                >
                  Filters
                  <ChevronDown size={15} />
                </button>
                <div className="emp-filters-group">
                <div className="filter-with-search">
                  <div className={`emp-status-filter-wrap employee-main-filter-wrap ${mainFilter && mainFilter !== "" ? "has-filter" : ""}`}>
                    <SelectFilter
                      value={mainFilter}
                      onChange={v => setMainFilter(v)}
                      clearable
                      placeholder="All Employees"
                      options={[
                        { value: "", label: "All Employees", icon: <Users size={16} /> },
                        { value: "active", label: "Active", icon: <CheckCircle2 size={16} /> },
                        { value: "inactive", label: "Inactive", icon: <Ban size={16} /> },
                      ]}
                    />
                  </div>
                  <div className="attendance-search emp-search-box">
                    <Search size={15} />
                    <input
                      aria-label="Search employees"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="Search employees"
                    />
                    {query && (
                      <button type="button" className="filter-search-clear" aria-label="Clear employee search" onClick={() => setQuery("")}>
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
                {currentRole !== "branch_manager" && (
                  <div className={`emp-status-filter-wrap ${branchFilter ? "has-filter" : ""}`}>
                    <SelectFilter
                      value={branchFilter}
                      onChange={v => setBranchFilter(v)}
                      clearable
                      placeholder="Branch"
                      options={[
                        { value: "", label: "All Branches", icon: <Building2 size={16} /> },
                        ...branches.map(b => ({ value: String(b.branch_id), label: b.name, icon: <MapPin size={16} /> }))
                      ]}
                    />
                  </div>
                )}
                <div className={`emp-status-filter-wrap ${deptFilter ? "has-filter" : ""}`}>
                  <SelectFilter
                    value={deptFilter}
                    onChange={v => setDeptFilter(v)}
                    clearable
                    placeholder="Department"
                    options={[
                      { value: "", label: "All Departments", icon: <Briefcase size={16} /> },
                      ...departments.map(d => ({ value: d, label: d }))
                    ]}
                  />
                </div>
                <div className={`emp-status-filter-wrap ${designationFilter ? "has-filter" : ""}`}>
                  <SelectFilter
                    value={designationFilter}
                    onChange={v => setDesignationFilter(v)}
                    clearable
                    placeholder="Designation"
                    options={[
                      { value: "", label: "All Designations", icon: <Target size={16} /> },
                      ...designations.map(d => ({ value: d, label: d }))
                    ]}
                  />
                </div>
                <div className={`emp-status-filter-wrap ${roleFilter ? "has-filter" : ""}`}>
                  <SelectFilter
                    value={roleFilter}
                    onChange={v => setRoleFilter(v)}
                    clearable
                    placeholder="Role"
                    options={[
                      { value: "", label: "All Roles", icon: <ShieldCheck size={16} /> },
                      ...availableRoles.map((role) => {
                        const value = role.value;
                        const label = role.label || value;
                        const icon =
                          normalizeRoleKey(value) === "super_admin" ? <ShieldCheck size={16} /> :
                          normalizeRoleKey(value) === "branch_manager" ? <Briefcase size={16} /> :
                          normalizeRoleKey(value) === "floor_manager" ? <PersonStanding size={16} /> :
                          <UserRound size={16} />;

                        return { value, label, icon };
                      })
                    ]}
                  />
                </div>
              </div>
              </div>
            )}
            </div>
            </div>
          <div className="emp-table-toolbar-right emp-bulk-actions-row">
              <button
                type="button"
                className="emp-mobile-toolbar-filters-toggle"
                onClick={() => setMobileFiltersOpen((open) => !open)}
                aria-expanded={mobileFiltersOpen}
              >
                Filters
                <ChevronDown size={14} />
              </button>
            {selectedIds.size > 0 && (
              <button className="emp-bulk-clear-btn" onClick={() => setSelectedIds(new Set())}>
                <X size={14} />
                Clear
              </button>
            )}
            <button
              className={`emp-bulk-upload-btn ${bulkUploadOpen ? 'active' : ''}`}
              onClick={() => setBulkUploadOpen(true)}
            >
              <Upload size={14} />
              Bulk upload
            </button>
            <button
              className={`emp-bulk-assign-btn ${bulkOpen ? 'active' : ''}`}
              onClick={() => {
                if (selectedIds.size === 0) {
                  setBulkWarn(true);
                  setTimeout(() => setBulkWarn(false), 3000);
                } else {
                  setBulkOpen(true);
                }
              }}
            >
              <Settings size={14} />
              Bulk assign
            </button>
          </div>
        </div>

        {/* Warning toast */}
        {bulkWarn && (
          <div className="emp-bulk-toast">
            <CircleAlert size={17} aria-hidden="true" />
            <span>Select employees first to bulk assign</span>
          </div>
        )}

        <div className="attendance-table-wrap">
          <table className="attendance-table emp-table">
            <thead>
              <tr>
                <th className="emp-checkbox-col">
                  <input
                    type="checkbox"
                    className="emp-checkbox"
                    checked={pagedStaff.length > 0 && pagedStaff.every(p => selectedIds.has(p.employee_id))}
                    onChange={(e) => {
                      setSelectedIds(prev => {
                        const next = new Set(prev);
                        pagedStaff.forEach(p => e.target.checked ? next.add(p.employee_id) : next.delete(p.employee_id));
                        return next;
                      });
                    }}
                  />
                </th>
                <th>S.No</th>
                <th>Employee Code</th>
                <th>Employee Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Department</th>
                <th>Designation</th>
                <th>D.O.J</th>
                {mainFilter !== "active" && <th>L.W.D</th>}
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedStaff.length ? (
                pagedStaff.map((person, index) => (
                  <tr
                    key={person.employee_id}
                    className={selectedIds.has(person.employee_id) ? "emp-row-selected" : ""}
                  >
                    <td className="emp-checkbox-col">
                      <input
                        type="checkbox"
                        className="emp-checkbox"
                        checked={selectedIds.has(person.employee_id)}
                        onChange={(e) => {
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            e.target.checked ? next.add(person.employee_id) : next.delete(person.employee_id);
                            return next;
                          });
                        }}
                      />
                    </td>
                    <td>{(empPage - 1) * empPageSize + index + 1}</td>
                    <td className="emp-id">{person.employee_code || ""}</td>
                    <td className="emp-name">{person.full_name}</td>
                    <td>{person.email || ""}</td>
                    <td>{person.phone || person.mobile || ""}</td>
                    <td>{person.department || ""}</td>
                    <td>{person.designation || ""}</td>
                    <td>
                      {person.date_of_joining
                        ? new Date(person.date_of_joining).toLocaleDateString(
                            undefined,
                            { day: "2-digit", month: "short", year: "numeric" },
                          )
                        : "-"}
                    </td>
                    {mainFilter !== "active" && (
                      <td>
                        {person.last_working_day
                          ? new Date(person.last_working_day).toLocaleDateString(
                              undefined,
                              { day: "2-digit", month: "short", year: "numeric" },
                            )
                          : "-"}
                      </td>
                    )}
                    <td>
                      {(() => {
                        const statusValue = (person.employment_type || person.employment_status || "active").toLowerCase();
                        const statusClass = statusValue === "inactive"
                          ? "absent"
                          : statusValue === "ex-employee" || statusValue === "terminated" || statusValue === "resigned"
                            ? "ex-employee"
                            : "present";

                        return (
                          <span className={`attendance-status ${statusClass}`}>
                            {person.employment_type || person.employment_status || ""}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="emp-actions">
                      <button
                        className="emp-action-btn"
                        onClick={() => openViewEmployee(person)}
                      >
                        View
                      </button>
                      <button
                        className="emp-action-btn"
                        onClick={() => openEdit(person)}
                      >
                        Edit
                      </button>
                      {canViewEmployeeHistory && (
                        <button
                          className="emp-action-btn"
                          onClick={() => viewEmployeeHistory(person)}
                        >
                          History
                        </button>
                      )}
                      {(() => {
                        const isInactive = (person.employment_type || person.employment_status || "active").toLowerCase() === "inactive";

                        return (
                          <button
                            className={`emp-action-btn ${isInactive ? "success" : "danger"}`}
                            onClick={() => isInactive ? activate(person) : deactivate(person)}
                          >
                            {isInactive ? "Activate" : "Deactivate"}
                          </button>
                        );
                      })()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={mainFilter !== "active" ? 12 : 11} className="attendance-no-results">
                    No employees found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={empPage}
          totalPages={empTotalPages}
          totalItems={empTotalItems}
          pageSize={empPageSize}
          onPageChange={setEmpPage}
        />
      </section>
      {open && (
        <div className="modal-backdrop employee-modal-backdrop">
          <form className="user-modal" onSubmit={save}>
            <div className="modal-head">
              <div>
                <h2>{editing ? "Edit Employee" : "Create Employee"}</h2>
                </div>
              <button
                type="button"
                className="modal-close"
                onClick={closeEmployeeModal}
              >
                <X size={16} />
              </button>
            </div>
            <div className="form-grid">
              {!editing ? (
                <>
                  <label>
                    <span>Branch <i className="required-star" aria-hidden="true">*</i></span>
                    <select
                      value={form.branch_id}
                      onChange={(event) => update("branch_id", event.target.value)}
                      required
                    >
                      {branches.map((branch) => (
                        <option key={branch.branch_id} value={branch.branch_id}>
                          {branch.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Role / permission <i className="required-star" aria-hidden="true">*</i></span>
                    <SelectFilter
                      value={form.role_code || availableRoles[0]?.value || "staff"}
                      onChange={(v) => update("role_code", v)}
                      placeholder="Select role"
                      options={roleOptions}
                    />
                  </label>
                  <label>
                    <span>Full name <i className="required-star" aria-hidden="true">*</i></span>
                    <input
                      value={form.full_name}
                      onChange={(event) => update("full_name", event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      value={form.email}
                      onChange={(event) => update("email", event.target.value)}
                    />
                  </label>
                  <label>
                    <span>Phone <i className="required-star" aria-hidden="true">*</i></span>
                    <input
                      value={form.phone}
                      onChange={(event) => update("phone", event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    <span>Password <i className="required-star" aria-hidden="true">*</i></span>
                    <input
                      type="password"
                      minLength="8"
                      value={form.initial_password}
                      onChange={(event) => update("initial_password", event.target.value)}
                      required
                      placeholder="Enter password"
                    />
                  </label>
                  <label>
                    Department
                    <SelectFilter
                      value={form.department}
                      onChange={(value) => update("department", value)}
                      placeholder="Select department"
                      options={departments.map((department) => ({ value: department, label: department }))}
                    />
                  </label>
                  <label>
                    Designation
                    <SelectFilter
                      value={form.designation}
                      onChange={(value) => update("designation", value)}
                      placeholder="Select designation"
                      options={designations.map((designation) => ({ value: designation, label: designation }))}
                    />
                  </label>
                </>
              ) : (
                <>
                  <label>
                    Branch
                    <select
                      value={form.branch_id}
                      onChange={(event) => update("branch_id", event.target.value)}
                    >
                      {branches.map((branch) => (
                        <option key={branch.branch_id} value={branch.branch_id}>
                          {branch.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="employee-attendance-branches-field" ref={attendanceBranchesRef}>
                    <span>Additional attendance branches</span>
                    <button type="button" className={`bulk-attendance-branches-trigger${attendanceBranchesOpen ? " is-open" : ""}`} onClick={() => setAttendanceBranchesOpen((value) => !value)} aria-expanded={attendanceBranchesOpen}>
                      <span>{form.attendance_branch_ids?.length ? `${form.attendance_branch_ids.length} branch${form.attendance_branch_ids.length === 1 ? "" : "es"} selected` : "Select branches"}</span>
                      <ChevronDown size={16} />
                    </button>
                    {attendanceBranchesOpen && (
                      <div className="bulk-attendance-branches-menu employee-attendance-branches-menu">
                        {branches.map((branch) => {
                          const branchId = String(branch.branch_id);
                          const checked = (form.attendance_branch_ids || []).includes(branchId);
                          return (
                            <label className="employee-attendance-branch-option" key={branchId}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => update("attendance_branch_ids", checked
                                  ? form.attendance_branch_ids.filter((id) => id !== branchId)
                                  : [...(form.attendance_branch_ids || []), branchId])}
                              />
                              <span>{branch.name}</span>
                            </label>
                          );
                        })}
                        {!branches.length && <div className="bulk-attendance-branches-empty">No branches available</div>}
                      </div>
                    )}
                  </div>
                  <label>
                    Role / permission
                    <SelectFilter
                      value={form.role_code || availableRoles[0]?.value || "staff"}
                      onChange={(v) => update("role_code", v)}
                      placeholder="Select role"
                      options={roleOptions}
                    />
                  </label>
                  <label>
                    Full name
                    <input
                      value={form.full_name}
                      onChange={(event) => update("full_name", event.target.value)}
                    />
                  </label>
                  <label>
                    Father / Husband name
                    <input
                      value={form.father_husband_name}
                      onChange={(event) => update("father_husband_name", event.target.value)}
                    />
                  </label>
                  <label>
                    Phone
                    <input
                      value={form.phone}
                      onChange={(event) => update("phone", event.target.value)}
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      value={form.email}
                      onChange={(event) => update("email", event.target.value)}
                    />
                  </label>
                  <label>
                    Gender
                    <SelectFilter
                      value={form.gender}
                      onChange={(value) => update("gender", value)}
                      placeholder="Select gender"
                      options={[
                        { value: "Male", label: "Male" },
                        { value: "Female", label: "Female" },
                        { value: "Other", label: "Other" },
                      ]}
                    />
                  </label>
                  <DateField
                    label="Date of birth"
                    value={form.date_of_birth}
                    onChange={(value) => update("date_of_birth", value)}
                    maxDate={new Date().toISOString().slice(0, 10)}
                  />
                  <label>
                    Category
                    <input
                      value={form.category}
                      onChange={(event) => update("category", event.target.value)}
                      placeholder="General / OBC / SC / ST"
                    />
                  </label>
                  <label>
                    Marital status
                    <MaritalStatusSelect
                      value={form.marital_status}
                      onChange={(value) => update("marital_status", value)}
                      placeholder="Select"
                    />
                  </label>
                  <label>
                    Aadhaar number
                    <input
                      value={form.aadhaar_number}
                      onChange={(event) => update("aadhaar_number", event.target.value)}
                    />
                  </label>
                  <label>
                    Address
                    <input
                      value={form.address}
                      onChange={(event) => update("address", event.target.value)}
                    />
                  </label>
                  <label>
                    Designation
                    <SelectFilter
                      value={form.designation}
                      onChange={(value) => update("designation", value)}
                      placeholder="Select designation"
                      options={[
                        ...(form.designation && !designations.includes(form.designation)
                          ? [{ value: form.designation, label: form.designation }]
                          : []),
                        ...designations.map((designation) => ({ value: designation, label: designation })),
                      ]}
                    />
                  </label>
                  <label>
                    Department
                    <SelectFilter
                      value={form.department}
                      onChange={(value) => update("department", value)}
                      placeholder="Select department"
                      options={[
                        ...(form.department && !departments.includes(form.department)
                          ? [{ value: form.department, label: form.department }]
                          : []),
                        ...departments.map((department) => ({ value: department, label: department })),
                      ]}
                    />
                  </label>
                  <DateField
                    label="Date of joining"
                    value={form.date_of_joining}
                    onChange={(value) => update("date_of_joining", value)}
                  />
                  <DateField
                    label="Last working day (LWD)"
                    value={form.last_working_day}
                    onChange={(value) => update("last_working_day", value)}
                  />
                  <label>
                    UAN number
                    <input
                      value={form.uan_number}
                      onChange={(event) => update("uan_number", event.target.value)}
                    />
                  </label>
                  <label>
                    EPF number
                    <input
                      value={form.epf_number}
                      onChange={(event) => update("epf_number", event.target.value)}
                    />
                  </label>
                  <label>
                    ESIC number
                    <input
                      value={form.esic_number}
                      onChange={(event) => update("esic_number", event.target.value)}
                    />
                  </label>
                  <DateField
                    label="Previous employment joining date"
                    value={form.previous_employment_joining_date}
                    onChange={(value) => update("previous_employment_joining_date", value)}
                  />
                  <label>
                    Pay scale
                    <input
                      value={form.pay_scale}
                      onChange={(event) => update("pay_scale", event.target.value)}
                    />
                  </label>
                  <label>
                    Basic salary
                    <input
                      type="number"
                      step="0.01"
                      value={form.basic_salary}
                      onChange={(event) => update("basic_salary", event.target.value)}
                    />
                  </label>
                  <label>
                    HRA
                    <input
                      type="number"
                      step="0.01"
                      value={form.hra}
                      onChange={(event) => update("hra", event.target.value)}
                    />
                  </label>
                  <label>
                    Other allowances
                    <input
                      type="number"
                      step="0.01"
                      value={form.other_allowances}
                      onChange={(event) => update("other_allowances", event.target.value)}
                    />
                  </label>
                  <label>
                    PAN number
                    <input
                      value={form.pan_number}
                      onChange={(event) => update("pan_number", event.target.value)}
                    />
                  </label>
                  <label>
                    Bank account number
                    <input
                      value={form.bank_account_number}
                      onChange={(event) => update("bank_account_number", event.target.value)}
                    />
                  </label>
                  <label>
                    Bank IFSC
                    <input
                      value={form.bank_ifsc}
                      onChange={(event) => update("bank_ifsc", event.target.value)}
                    />
                  </label>
                  <label>
                    Bank branch
                    <input
                      value={form.bank_branch}
                      onChange={(event) => update("bank_branch", event.target.value)}
                    />
                  </label>
                  <label>
                    Bank name
                    <input
                      value={form.bank_name}
                      onChange={(event) => update("bank_name", event.target.value)}
                    />
                  </label>
                  <label>
                    Set new password
                    <input
                      type="password"
                      minLength="8"
                      value={form.initial_password}
                      onChange={(event) => update("initial_password", event.target.value)}
                      placeholder="Leave blank to keep current password"
                    />
                  </label>
                </>
              )}
              {!editing || employeeArraysAvailable ? <>
                <EmployeeArrayEditor title="Family particulars" rows={form.family_members} onChange={(rows) => update("family_members", rows)} addLabel="Add family member" fields={[
                  { key: "name", label: "Name" }, { key: "date_of_birth", label: "Date of birth", type: "date" }, { key: "relationship", label: "Relationship" }, { key: "aadhaar_number", label: "Aadhaar number" },
                ]} />
                <EmployeeArrayEditor title="Educational qualifications" rows={form.educations} onChange={(rows) => update("educations", rows)} addLabel="Add education" fields={[
                  { key: "qualification", label: "Qualification" }, { key: "year_of_passing", label: "Year of passing", type: "number", min: 1900, max: 2200 }, { key: "institute_university", label: "Institute / university" }, { key: "marks_percentage", label: "Marks / percentage", type: "number", min: 0, max: 100, step: "0.01" },
                ]} />
                <EmployeeArrayEditor title="Previous employment details" rows={form.previous_employments} onChange={(rows) => update("previous_employments", rows)} addLabel="Add employment" fields={[
                  { key: "organization", label: "Organization" }, { key: "from_date", label: "From", type: "date" }, { key: "to_date", label: "To", type: "date" }, { key: "nature_of_work", label: "Nature of work" },
                ]} />
              </> : <p className="employee-array-api-note">The employee details API does not return family, education, or previous employment records, so these cannot be safely edited yet.</p>}
            </div>
            {message && <p className="form-message">{message}</p>}
            <div className="modal-actions">
              <button
                type="button"
                className="outline-action"
                onClick={closeEmployeeModal}
              >
                Cancel
              </button>
              <button type="submit" className="primary-action">Save</button>
            </div>
          </form>
        </div>
      )}
      {viewEmployee && (
        <div className="modal-backdrop employee-details-backdrop">
          <section className="user-modal employee-details-modal">
            <div className="modal-head">
              <div className="employee-details-profile-pic">
                {viewEmployee.photo_url ? (
                  <img src={viewEmployee.photo_url} alt="" />
                ) : (
                  (viewEmployee.full_name || "NA")
                    .split(" ")
                    .map((part) => part[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()
                )}
              </div>
              <div>
                <p className="eyebrow">EMPLOYEE DETAILS</p>
              </div>
              <button type="button" className="modal-close" onClick={closeViewEmployee} aria-label="Close employee details">
                <X size={16} />
              </button>
            </div>
            <div className="employee-details-grid">
              {[
                ["Employee code", viewEmployee.employee_code],
                ["Email", viewEmployee.email],
                ["Phone", viewEmployee.phone || viewEmployee.mobile],
                ["Gender", viewEmployee.gender],
                ["Date of birth", viewEmployee.date_of_birth],
                ["Category", viewEmployee.category],
                ["Marital status", viewEmployee.marital_status],
                ["Aadhaar number", viewEmployee.aadhaar_number],
                ["Address", viewEmployee.address],
                ["Department", viewEmployee.department],
                ["Designation", viewEmployee.designation],
                ["Date of joining", viewEmployee.date_of_joining],
                ["Last working day (LWD)", viewEmployee.last_working_day],
                ["UAN number", viewEmployee.uan_number],
                ["EPF number", viewEmployee.epf_number],
                ["ESIC number", viewEmployee.esic_number],
                ["Previous employment joining date", viewEmployee.previous_employment_joining_date],
                ["Pay scale", viewEmployee.pay_scale],
                ["Basic salary", viewEmployee.basic_salary],
                ["HRA", viewEmployee.hra],
                ["Other allowances", viewEmployee.other_allowances],
                ["PAN number", viewEmployee.pan_number],
                ["Bank account number", viewEmployee.bank_account_number],
                ["Bank IFSC", viewEmployee.bank_ifsc],
                ["Bank branch", viewEmployee.bank_branch],
                ["Bank name", viewEmployee.bank_name],
                ["Employment status", viewEmployee.employment_status],
                ["Role", viewEmployee.role_code || viewEmployee.role],
                ["Branch", branches.find((branch) => String(branch.branch_id) === String(viewEmployee.branch_id))?.name],
                ["Additional attendance branches", branches.filter((branch) => (viewEmployee.attendance_branch_ids || []).map(String).includes(String(branch.branch_id))).map((branch) => branch.name).join(", ") || "None"],
              ].map(([label, value]) => (
                <div className="employee-detail-item" key={label}>
                  <span>{label}</span>
                  <strong>{value || ""}</strong>
                </div>
              ))}
              {["family_members", "educations", "previous_employments"].every((key) => Array.isArray(viewEmployee[key])) ? <>
                <EmployeeArrayDisplay title="Family particulars" rows={viewEmployee.family_members} columns={[["Name", "name"], ["Date of birth", "date_of_birth"], ["Relationship", "relationship"], ["Aadhaar number", "aadhaar_number"]]} />
                <EmployeeArrayDisplay title="Educational qualifications" rows={viewEmployee.educations} columns={[["Qualification", "qualification"], ["Year of passing", "year_of_passing"], ["Institute / university", "institute_university"], ["Marks / percentage", "marks_percentage"]]} />
                <EmployeeArrayDisplay title="Previous employment details" rows={viewEmployee.previous_employments} columns={[["Organization", "organization"], ["From", "from_date"], ["To", "to_date"], ["Nature of work", "nature_of_work"]]} />
              </> : <p className="employee-array-api-note">Family, education, and previous employment records are not included in the employee details API response.</p>}
            </div>
          </section>
        </div>
      )}
      {employeeHistory && (
        <div className="modal-backdrop employee-history-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setEmployeeHistory(null); }}>
          <section className="user-modal employee-history-modal" role="dialog" aria-modal="true" aria-label={`History for ${employeeHistory.employee.full_name}`}>
            <div className="modal-head">
              <div>
                <p className="eyebrow">EMPLOYEE HISTORY</p>
                <h2>{employeeHistory.employee.full_name}</h2>
                <small>{employeeHistory.employee.employee_code}</small>
              </div>
              <button type="button" className="modal-close" onClick={() => setEmployeeHistory(null)} aria-label="Close employee history"><X size={16} /></button>
            </div>
            {historyLoading ? <p className="employee-history-state">Loading history…</p>
              : historyError ? <p className="employee-history-state error" role="alert">{historyError}</p>
                : (
                  <div className="employee-history-scroll">
                    <section className="employee-history-section">
                      <h3>Branch and service history</h3>
                      {serviceHistoryError
                        ? <p className="employee-history-state error" role="alert">{serviceHistoryError}</p>
                        : employeeHistory.services.length > 0 ? (
                        <ol className="employee-history-list employee-service-list">
                          {employeeHistory.services.map((service) => {
                            const branch = branches.find((item) => String(item.branch_id) === String(service.branch_id));
                            const actor = staff.find((person) => String(person.employee_id) === String(service.changed_by_employee_id));
                            const started = parseUTC(service.started_at)?.toLocaleDateString() || "—";
                            const ended = service.ended_at ? parseUTC(service.ended_at)?.toLocaleDateString() || "—" : null;
                            return (
                              <li className="employee-history-item" key={service.id}>
                                <span className="employee-history-marker employee-service-marker" aria-hidden="true" />
                                <div className="employee-history-content">
                                  <div className="employee-history-title">
                                    <strong>{service.branch_name || branch?.name || "Branch not recorded"}{service.branch_code ? ` (${service.branch_code})` : ""}</strong>
                                    <time dateTime={service.started_at}>{ended ? `${started} – ${ended}` : `Since ${started} · Current`}</time>
                                  </div>
                                  <p>{[service.designation, service.department].filter(Boolean).join(" · ") || "No designation or department recorded"}</p>
                                  {service.changed_by_employee_id && <small>Recorded by {actor?.full_name || actor?.employee_code || "team member"}</small>}
                                </div>
                              </li>
                            );
                          })}
                        </ol>
                        ) : <p className="employee-history-section-empty">No service history has been recorded.</p>}
                    </section>
                    <section className="employee-history-section">
                      <h3>Activity history</h3>
                      {employeeHistory.entries.length ? (
                        <ol className="employee-history-list">
                          {employeeHistory.entries.map((entry) => {
                            const actor = staff.find((person) => String(person.employee_id) === String(entry.actor_employee_id));
                            const changes = getAuditChanges(entry);
                            const actionLabel = {
                              CREATE_EMPLOYEE: "Employee created",
                              UPDATE_EMPLOYEE: "Employee details updated",
                              UPDATE_MY_PROFILE: "Employee profile updated",
                              TRANSFER_EMPLOYEE: "Branch transferred",
                              DEACTIVATE_EMPLOYEE: "Employee deactivated",
                              ACTIVATE_EMPLOYEE: "Employee activated",
                            }[entry.action] || entry.description || entry.action.replace(/_/g, " ");
                            return (
                              <li className="employee-history-item" key={entry.id}>
                                <span className="employee-history-marker" aria-hidden="true" />
                                <div className="employee-history-content">
                                  <div className="employee-history-title"><strong>{actionLabel}</strong><time dateTime={entry.created_at}>{parseUTC(entry.created_at)?.toLocaleString() || "—"}</time></div>
                                  <p>By {actor?.full_name || actor?.employee_code || entry.actor_role || "System"}{entry.actor_role ? ` · ${entry.actor_role.replace(/_/g, " ")}` : ""}</p>
                                  {changes.length > 0 && <ul>{changes.map(({ key, oldValue, newValue }) => (
                                    <li key={key}>
                                      <span>{auditFieldLabels[key]}</span>
                                      <strong>
                                        <span>From: {formatAuditValue(key, oldValue)}</span>
                                        <ArrowRight size={12} aria-hidden="true" />
                                        <span>To: {formatAuditValue(key, newValue)}</span>
                                      </strong>
                                    </li>
                                  ))}</ul>}
                                  {entry.description && !["CREATE_EMPLOYEE", "UPDATE_EMPLOYEE", "TRANSFER_EMPLOYEE", "DEACTIVATE_EMPLOYEE", "ACTIVATE_EMPLOYEE"].includes(entry.action) && <small>{entry.description}</small>}
                                </div>
                              </li>
                            );
                          })}
                        </ol>
                      ) : <p className="employee-history-section-empty">No activity history has been recorded.</p>}
                    </section>
                    {employeeHistory.services.length === 0 && !serviceHistoryError && employeeHistory.entries.length === 0 && (
                      <p className="employee-history-state">No history has been recorded for this employee yet.</p>
                    )}
                  </div>
                )}
          </section>
        </div>
      )}
      {bulkOpen && (
        <BulkAssignModal
          selectedIds={selectedIds}
          branches={branches}
          departments={departmentRecords}
          designations={designationRecords}
          token={token}
          onClose={() => { setBulkOpen(false); setSelectedIds(new Set()); }}
          onDone={() => { setBulkOpen(false); setSelectedIds(new Set()); window.location.reload(); }}
        />
      )}
      {bulkUploadOpen && (
        <BulkUploadModal
          token={token}
          onClose={() => setBulkUploadOpen(false)}
        />
      )}
    </>
  );
}

function BulkAssignModal({ selectedIds, branches, departments: departmentRecords = [], designations: designationRecords = [], token, onClose, onDone }) {
  const [form, setForm] = useState({ branch_id: "", attendance_branch_ids: null, department: "", designation: "", role_code: "", shift: "" });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [attendanceBranchesOpen, setAttendanceBranchesOpen] = useState(false);
  const attendanceBranchesRef = useRef(null);
  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  useEffect(() => {
    if (!attendanceBranchesOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (attendanceBranchesRef.current && !attendanceBranchesRef.current.contains(event.target)) {
        setAttendanceBranchesOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [attendanceBranchesOpen]);

  const departments = departmentRecords.map((item) => item.name).filter(Boolean).sort();
  const designations = designationRecords.map((item) => item.name).filter(Boolean).sort();
  const roles = [
    { value: "staff", label: "Staff" },
    { value: "floor_manager", label: "Floor Manager" },
    { value: "branch_manager", label: "Branch Manager" },
    { value: "super_admin", label: "Super Admin" },
    { value: "hr", label: "HR" },
  ];

  const save = async (e) => {
    e.preventDefault();
    // Build payload - only include fields that were filled in
    const payload = {};
    if (form.branch_id) {
      payload.branch_id = form.branch_id;
    }
    if (form.attendance_branch_ids !== null) {
      payload.attendance_branch_ids = form.attendance_branch_ids;
    }
    if (form.department)  payload.department  = form.department;
    if (form.designation) payload.designation = form.designation;
    if (form.role_code)   payload.role_code   = form.role_code;
    if (form.shift)       payload.shift       = form.shift;
    if (!Object.keys(payload).length) { setMessage("Select at least one field to assign."); return; }

    setSaving(true);
    setMessage(`Updating ${selectedIds.size} employee${selectedIds.size > 1 ? "s" : ""}-`);
    const results = await Promise.all(
      [...selectedIds].map(id =>
        apiFetch(`/staff/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }, token)
      )
    );
    const failed = results.filter(r => !r.ok).length;
    if (failed) { setMessage(`${failed} update(s) failed. Please try again.`); setSaving(false); return; }
    onDone();
  };

  return (
    <div className="modal-backdrop">
      <form className="user-modal bulk-assign-modal" onSubmit={save}>
        <div className="modal-head">
          <div>
            <p className="eyebrow">BULK ASSIGN</p>
            <h2>Assign to {selectedIds.size} employee{selectedIds.size > 1 ? "s" : ""}</h2>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <p className="bulk-assign-hint">
          Leave a field blank to keep each employee's existing value.
        </p>
        <div className="form-grid">
          <label>
            Branch
            <SelectFilter
              value={form.branch_id}
              onChange={v => update("branch_id", v)}
              placeholder="- keep existing -"
              options={[
                { value: "", label: "- keep existing -" },
                ...branches.map(b => ({ value: String(b.branch_id), label: b.name }))
              ]}
            />
          </label>
          <div className="bulk-attendance-branches-label" ref={attendanceBranchesRef}>
            <div className="bulk-attendance-branches-heading">
              <span>Additional attendance branches</span>
              {form.attendance_branch_ids !== null && form.attendance_branch_ids.length > 0 && <button type="button" className="bulk-attendance-branches-clear" onClick={() => update("attendance_branch_ids", [])}>Clear</button>}
            </div>
            <button type="button" className={`bulk-attendance-branches-trigger${attendanceBranchesOpen ? " is-open" : ""}`} onClick={() => setAttendanceBranchesOpen((open) => !open)} aria-expanded={attendanceBranchesOpen}>
              <span>{form.attendance_branch_ids?.length ? `${form.attendance_branch_ids.length} branch${form.attendance_branch_ids.length === 1 ? "" : "es"} selected` : "Select branches"}</span>
              <ChevronDown size={16} />
            </button>
            {attendanceBranchesOpen && (
              <div className="bulk-attendance-branches-menu">
                {branches.map((branch) => {
                  const branchId = String(branch.branch_id);
                  const selected = (form.attendance_branch_ids || []).includes(branchId);
                  return (
                    <label className="bulk-attendance-branch-option" key={branchId}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => {
                          const current = form.attendance_branch_ids || [];
                          update("attendance_branch_ids", selected
                            ? current.filter((id) => id !== branchId)
                            : [...current, branchId]);
                        }}
                      />
                      <span>{branch.name}</span>
                    </label>
                  );
                })}
                {!branches.length && <div className="bulk-attendance-branches-empty">No branches available</div>}
              </div>
            )}
          </div>
          <label>
            Department
            <SelectFilter
              value={form.department}
              onChange={v => update("department", v)}
              placeholder="- keep existing -"
              options={[
                { value: "", label: "- keep existing -" },
                ...departments.map(d => ({ value: d, label: d }))
              ]}
            />
          </label>
          <label>
            Designation
            <SelectFilter
              value={form.designation}
              onChange={v => update("designation", v)}
              placeholder="- keep existing -"
              options={[
                { value: "", label: "- keep existing -" },
                ...designations.map(d => ({ value: d, label: d }))
              ]}
            />
          </label>
          <label>
            Role
            <SelectFilter
              value={form.role_code}
              onChange={v => update("role_code", v)}
              placeholder="- keep existing -"
              options={[
                { value: "", label: "- keep existing -" },
                ...roles.map(r => ({ value: r.value, label: r.label }))
              ]}
            />
          </label>
          <label>
            Shift
            <SelectFilter
              value={form.shift}
              onChange={v => update("shift", v)}
              placeholder="- keep existing -"
              options={[
                { value: "", label: "- keep existing -" },
                { value: "morning", label: "Morning" },
                { value: "afternoon", label: "Afternoon" },
                { value: "evening", label: "Evening" },
                { value: "night", label: "Night" },
              ]}
            />
          </label>
        </div>
        {message && <p className="form-message">{message}</p>}
        <div className="modal-actions">
          <button type="button" className="outline-action" onClick={onClose}>Cancel</button>
          <button className="primary-action" disabled={saving}>
            {saving ? "Saving-" : "Apply"}
          </button>
        </div>
      </form>
    </div>
  );
}

function BulkUploadModal({ token, onClose }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");

  const downloadTemplate = () => {
    const headers = [
      "branch_code",
      "role_code",
      "full_name",
      "father_husband_name",
      "phone",
      "email",
      "gender",
      "date_of_birth",
      "category",
      "marital_status",
      "aadhaar_number",
      "address",
      "designation",
      "department",
      "date_of_joining",
      "last_working_day",
      "uan_number",
      "epf_number",
      "esic_number",
      "previous_employment_joining_date",
      "pay_scale",
      "basic_salary",
      "hra",
      "other_allowances",
      "pan_number",
      "bank_account_number",
      "bank_ifsc",
      "bank_branch",
      "bank_name",
      "initial_password",
    ];

    const exampleRow = [
      "HIW-BR01",
      "staff",
      "John Doe",
      "John",
      "+919876543210",
      "john.doe@example.com",
      "Male",
      "1995-06-12",
      "General",
      "Single",
      "123456789012",
      "12 Main Street, City",
      "Software Engineer",
      "Engineering",
      "2024-01-15",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "Welcome@123",
    ];

    const sheet = XLSX.utils.aoa_to_sheet([headers, exampleRow]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Employees");
    XLSX.writeFile(workbook, "hiwaga_employee_bulk_template.xlsx");
    setMessage("Template downloaded successfully!");
    setTimeout(() => setMessage(""), 3000);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setMessage("");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage("Please select a file to upload.");
      return;
    }

    if (!token) {
      setMessage("Session expired. Please log in again.");
      return;
    }

    setUploading(true);
    setMessage("Uploading...");

    try {
      const formData = new FormData();
      formData.append("file", selectedFile, selectedFile.name);

      const response = await apiFetch(
        "/staff/bulk-upload",
        {
          method: "POST",
          body: formData,
        },
        token,
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const cleanMessage = normalizeErrorMessage(data, "Bulk upload failed");
        throw new Error(cleanMessage);
      }

      setMessage(data.message || `File "${selectedFile.name}" uploaded successfully.`);
      setSelectedFile(null);
      const input = document.getElementById("bulk-upload-file-input");
      if (input) input.value = "";
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1000);
    } catch (error) {
      setMessage(error.message || "Unable to upload file");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="user-modal bulk-upload-modal">
        <div className="modal-head bulk-upload-head">
          <div className="bulk-upload-title-wrap">
            <p className="eyebrow bulk-upload-label">Bulk Upload</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="bulk-upload-content">
          <div className="bulk-upload-section">
            <h3>Step 1: Download Template</h3>
            <p>Download the template and keep the backend column names exactly as required.</p>
            <button 
              type="button" 
              className="bulk-download-btn"
              onClick={downloadTemplate}
            >
              <Download size={16} />
              Download template
            </button>
          </div>

          <div className="bulk-upload-divider"></div>

          <div className="bulk-upload-section">
            <h3>Step 2: Upload File</h3>
            <p>Select your filled Excel file to upload employee data.</p>
            
            <div className="bulk-file-input-area">
              <input
                id="bulk-upload-file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <button
                type="button"
                className="bulk-select-file-btn"
                onClick={() => document.getElementById("bulk-upload-file-input").click()}
              >
                <Upload size={16} />
                {selectedFile ? selectedFile.name : "Select file"}
              </button>
            </div>

            {selectedFile && (
              <button
                type="button"
                className="bulk-upload-submit-btn"
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? "Uploading..." : "Upload & Import"}
              </button>
            )}
          </div>
        </div>

        {message && (
          <p className={`bulk-upload-message ${message.includes("success") ? "success" : ""}`}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
}

function PermissionManagement({ staff, branches, token }) {
  return (
    <section className="admin-view panel">
      <div className="admin-view-head">
        <div>
          <p className="eyebrow">ACCESS CONTROL</p>
          <h2>Permissions</h2>
        </div>
        <span className="row-count">Role-based RBAC</span>
      </div>
      <div className="permission-grid">
        {[
          ["super_admin", "Full organization access"],
          ["branch_manager", "Manage users and attendance in own branch"],
          ["floor_manager", "View branch attendance"],
          ["staff", "Check in and view own history"],
        ].map(([role, description]) => (
          <div className="permission-card" key={role}>
            <ShieldCheck size={18} />
            <div>
              <strong>{role}</strong>
              <small>{description}</small>
            </div>
          </div>
        ))}
      </div>
      <p className="permission-note">
        User role changes are managed from the Staff tab using the Edit action.
      </p>
    </section>
  );
}

function AdminTable({ title, eyebrow, columns, rows }) {
  return (
    <section className="admin-view panel">
      <div className="admin-view-head">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h2>{title}</h2>
        </div>
        <span className="row-count">{rows.length} records</span>
      </div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row, index) => (
                <tr key={`${row[0]}-${index}`}>
                  {row.map((cell, cellIndex) => (
                    <td key={`${cell}-${cellIndex}`}>
                      {cellIndex === row.length - 1 ? (
                        <span className="table-status">{cell}</span>
                      ) : (
                        cell
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="empty-table">
                  No records available yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LegacyAttendanceDashboard({
  branches,
  liveMetrics,
  liveSegments,
  isLive,
  checkedIn,
}) {
  return (
    <>
      <section className="overview panel">
        <div className="section-title">
          <span>
            <FileText size={15} /> ATTENDANCE OVERVIEW
          </span>
        </div>
        <div className="metric-grid">
          {metrics.map(
            ([label, fallback, trend, direction, color, Icon], index) => (
              <div className={`metric ${color}`} key={label}>
                <div className="metric-top">
                  <span>{label}</span>
                  <i>
                    <Icon size={15} />
                  </i>
                </div>
                <strong>{liveMetrics[index][0] || fallback}</strong>
                <div className="metric-bottom">
                  <small>{liveMetrics[index][1]}</small>
                  <em className={direction}>{isLive ? "LIVE" : trend}</em>
                </div>
              </div>
            ),
          )}
        </div>
      </section>
      <div className="lower-panels">
        <section className="panel segments">
          <div className="panel-title">
            <span>
              <Zap size={15} /> ATTENDANCE SEGMENTS
            </span>
          </div>
          <div className="segment-list">
            {segments.map(
              ([title, description, badge, fallback, color], index) => (
                <div className="segment" key={title}>
                  <span className={`segment-dot ${color}`} />
                  <div className="segment-copy">
                    <strong>{title}</strong>
                    <small>{description}</small>
                  </div>
                  <span className={`badge ${color}`}>{badge}</span>
                  <b>{isLive ? liveSegments[index][0] : fallback}</b>
                </div>
              ),
            )}
          </div>
        </section>
        <section className="panel contacts">
          <div className="panel-title">
            <span>
              <Users size={15} /> STAFF OVERVIEW
            </span>
          </div>
          <div className="contact-highlight">
            <Sparkles size={16} />
            <strong>{checkedIn || (isLive ? 0 : 96)}</strong> Present today from{" "}
            <strong>{liveMetrics[3][0] || (isLive ? 0 : 128)}</strong> Active
            Staff
          </div>
          <div className="contact-grid">
            <div className="contact-card">
              <div>
                <span>BRANCHES</span>
                <strong>{branches.length}</strong>
                <small>Branches connected to your organization.</small>
              </div>
            </div>
            <div className="contact-card">
              <div>
                <span>CHECK-INS</span>
                <strong>{liveMetrics[0][0]}</strong>
                <small>Employees who checked in today.</small>
              </div>
            </div>
            <div className="contact-card">
              <div>
                <span>GEOFENCE PASSES</span>
                <strong>{liveSegments[0][0]}</strong>
                <small>Attendance checks inside the branch radius.</small>
              </div>
            </div>
            <div className="contact-card">
              <div>
                <span>OUTSIDE RADIUS</span>
                <strong>{liveMetrics[4][0]}</strong>
                <small>Checks flagged for manager review.</small>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

function AttendancePage({ staff = [], branches = [], attendance = [], currentUser, onAttendanceRefresh, departments: departmentRecords = [], designations: designationRecords = [], token = "" }) {
  const [scope, setScope] = useState("All");
  const [selfAttendanceRecords, setSelfAttendanceRecords] = useState([]);
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState("All");
  const [range, setRange] = useState("This month");
  const [query, setQuery] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const selectAttendanceEmployee = (employeeId) => {
    setSelectedEmployeeId(String(employeeId || ""));
    if (!employeeId) {
      if (/^\/attendance\/employee\/[^/]+\/?$/i.test(window.location.pathname)) navigateToPath("/attendance");
      return;
    }
    const person = staff.find((candidate) => String(candidate.employee_id) === String(employeeId));
    navigateToPath(`/attendance/employee/${encodeURIComponent(person?.employee_code || employeeId)}`);
  };
  useEffect(() => {
    const syncAttendanceRoute = () => {
      const match = window.location.pathname.match(/^\/attendance\/employee\/([^/]+)\/?$/i);
      if (!match) {
        setSelectedEmployeeId("");
        return;
      }
      const employeeCode = decodeURIComponent(match[1]);
      const person = staff.find((candidate) =>
        String(candidate.employee_code || candidate.employee_id).toLowerCase() === employeeCode.toLowerCase(),
      );
      if (person) setSelectedEmployeeId(String(person.employee_id));
    };
    syncAttendanceRoute();
    window.addEventListener("popstate", syncAttendanceRoute);
    return () => window.removeEventListener("popstate", syncAttendanceRoute);
  }, [staff]);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");
  const [recordsPerPage] = useState(10);
  const [attendanceViewMode, setAttendanceViewMode] = useState("list");
  const attendancePageSize = attendanceViewMode === "grid" ? 16 : recordsPerPage;
  const [selfieModal, setSelfieModal] = useState(null); // { url, loading, error }
  const [attendanceEdit, setAttendanceEdit] = useState(null);
  const [attendanceActionDay, setAttendanceActionDay] = useState(null);
  const [attendanceEditBusy, setAttendanceEditBusy] = useState(false);
  const [attendanceEditError, setAttendanceEditError] = useState("");
  const canEditAttendance = ["hr", "super_admin"].includes(
    String(currentUser?.role_code || localStorage.getItem("hiwaga_role") || "").trim().toLowerCase().replace(/[\s-]+/g, "_"),
  );
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
  
  const departments = departmentRecords.map((item) => item.name).filter(Boolean).sort();
  const designations = designationRecords.map((item) => item.name).filter(Boolean).sort();
  
  const localStorageId = localStorage.getItem("hiwaga_employee_id");
  // Match against staff array to get the exact employee_id used in attendance records
  const selfStaff = staff.find(
    (person) =>
      String(person.employee_id) === String(localStorageId) ||
      String(person.user_id) === String(localStorageId),
  );
  const employeeId = selfStaff
    ? String(selfStaff.employee_id)
    : String(currentUser?.employee_id || localStorageId);
  useEffect(() => {
    if (!token || !employeeId || employeeId === "null") {
      setSelfAttendanceRecords([]);
      return undefined;
    }
    let ignore = false;
    apiFetch(`/attendance/employee/${encodeURIComponent(employeeId)}/history?page_size=100`, {}, token)
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error("Unable to load your attendance history.");
        const records = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        if (!ignore) setSelfAttendanceRecords(records);
      })
      .catch(() => { if (!ignore) setSelfAttendanceRecords([]); });
    return () => { ignore = true; };
  }, [employeeId, token]);
  const attendanceForDisplay = [...new Map(
    [...attendance, ...selfAttendanceRecords].map((record) => [record.id || `${record.employee_id}-${record.timestamp}-${record.check_type}`, record]),
  ).values()];
  // Debug: log IDs to help trace mismatch
  if (process.env.NODE_ENV !== "production") {
    console.log("[Self attendance] localStorageId:", localStorageId, "selfStaff:", selfStaff?.employee_id, "employeeId used:", employeeId, "attendance employee_ids:", [...new Set(attendance.map(r => r.employee_id))]);
  }

  const fetchSelfie = async (attendanceId) => {
    setSelfieModal({ url: null, loading: true, error: null });
    try {
      const response = await fetch(
        `${API_URL}/attendance/${attendanceId}/selfie`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error("Selfie not available");
      const data = await response.json();
      const url = data.preview_url;
      if (!url) throw new Error("Selfie not available");
      // Preload image before showing modal to prevent double-flash
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });
      setSelfieModal({ url, loading: false, error: null });
    } catch {
      setSelfieModal({ url: null, loading: false, error: "Selfie not available" });
    }
  };

  const closeSelfie = () => setSelfieModal(null);
  const getEmployeeAttendanceStart = (person) => {
    if (!person) return null;
    const candidate =
      person.date_of_joining ||
      person.created_at ||
      person.createdAt ||
      person.created_date ||
      person.joining_date ||
      person.createdOn ||
      person.created_on ||
      person.date_created ||
      person.registered_at ||
      person.registration_date ||
      person.user_created_at ||
      person.hire_date ||
      person.join_date;
    if (!candidate) return null;
    const parsed = new Date(candidate);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  };
  const selectedEmployee = staff.find(
    (person) => String(person.employee_id) === String(selectedEmployeeId),
  );
  const targetEmployee =
    scope === "Self"
      ? selfStaff || currentUser || null
      : selectedEmployee || currentUser || selfStaff || null;
  const getEmployeeAttendanceStartForId = (id) => {
    if (!id) return null;
    const person =
      staff.find(
        (candidate) =>
          String(candidate.employee_id) === String(id) ||
          String(candidate.user_id) === String(id),
      ) ||
      (String(currentUser?.employee_id) === String(id) || String(currentUser?.user_id) === String(id)
        ? currentUser
        : null);
    return getEmployeeAttendanceStart(person);
  };
  const now = new Date();
  const rangeStart = (() => {
    const date = new Date(now);
    let baseStart;
    if (range === "Custom range")
      baseStart = customStart ? new Date(`${customStart}T00:00:00`) : new Date(0);
    else if (range === "This month")
      baseStart = new Date(now.getFullYear(), now.getMonth(), 1);
    else if (range === "This year")
      baseStart = new Date(now.getFullYear(), 0, 1);
    else {
      date.setDate(date.getDate() - Number(range.match(/\d+/)?.[0] || 30));
      baseStart = date;
    }
    const employeeStart = getEmployeeAttendanceStart(targetEmployee);
    if (employeeStart && baseStart < employeeStart) {
      return new Date(employeeStart);
    }
    return baseStart;
  })();
  const rangeEnd =
    range === "Custom range" && customEnd
      ? new Date(`${customEnd}T23:59:59`)
      : now;
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  useEffect(() => {
    if (range !== "Custom range") return;
    if (!customStart) setCustomStart(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
    if (!customEnd) setCustomEnd(new Date().toISOString().slice(0, 10));
  }, [range, customStart, customEnd]);
  useEffect(() => {
    const searchInput = document.querySelector(".attendance-search input");
    if (searchInput)
      searchInput.placeholder =
        scope === "All" && !selectedEmployeeId
          ? "Search employees"
          : "Search dates (e.g. 14/09/2026)";
  }, [scope, selectedEmployeeId]);
  const filterOptions = useMemo(
    () => ({
      branches: branches
        .map((branch) => ({
          value: String(branch.branch_id),
          label: branch.name,
        }))
        .filter((branch) => branch.value && branch.label),
      departments: [
        ...new Set(
          staff.map((person) => person.department?.trim()).filter(Boolean),
        ),
      ].sort(),
      designations: [
        ...new Set(
          staff.map((person) => person.designation?.trim()).filter(Boolean),
        ),
      ].sort(),
    }),
    [branches, staff],
  );
  const rows = staff
    .filter((person) => {
      const employmentStatus = String(person.employment_status || "").trim().toLowerCase();
      const isDeactivated = person.is_active === false || ["inactive", "terminated", "resigned", "ex-employee"].includes(employmentStatus);
      const matchesScope =
        scope === "All" || String(person.employee_id) === String(employeeId);
      const matchesBranch =
        !branchFilter || String(person.branch_id) === branchFilter;
      const matchesDepartment =
        !departmentFilter || person.department?.trim() === departmentFilter;
      const matchesDesignation =
        !designationFilter || person.designation?.trim() === designationFilter;
      return (
        !isDeactivated && matchesScope && matchesBranch && matchesDepartment && matchesDesignation
      );
    })
    .map((person) => {
      const records = attendanceForDisplay.filter(
        (record) =>
          String(record.employee_id) === String(person.employee_id) &&
          parseUTC(record.timestamp) >= todayStart &&
          parseUTC(record.timestamp) < tomorrowStart,
      );
      const checkIn = records.find((record) => record.check_type === "in");
      const checkOut = records.find((record) => record.check_type === "out");
      const checkInTime = checkIn ? parseUTC(checkIn.timestamp) : null;
      const checkOutTime = checkOut ? parseUTC(checkOut.timestamp) : null;
      const elapsedMinutes =
        checkInTime && checkOutTime
          ? Math.max(0, Math.round((checkOutTime - checkInTime) / 60000))
          : 0;
      const hours = elapsedMinutes
        ? `${Math.floor(elapsedMinutes / 60)}h ${elapsedMinutes % 60}m`
        : "-";
      const initials = (person.full_name || "NA")
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
      const score = checkIn
        ? Math.min(100, Math.round((elapsedMinutes / 540) * 100))
        : 0;
      return [
        person.employee_id,
        initials,
        person.employee_code || "",
        person.full_name || "Unnamed employee",
        person.designation || "Employee",
        person.department || "Unassigned",
        checkIn ? "Present" : "Absent",
        checkInTime
          ? checkInTime.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "-",
        hours,
        score,
        checkInTime ? checkInTime.getTime() : Number.POSITIVE_INFINITY,
      ];
    })
    .sort((a, b) => a[10] - b[10] || a[3].localeCompare(b[3]));
  const visibleRows = rows.filter((row) => {
    const matchesQuery = `${row[2]} ${row[3]} ${row[4]} ${row[5]}`
      .toLowerCase()
      .includes(query.toLowerCase());
    const matchesStatus =
      attendanceStatusFilter === "All" ||
      (attendanceStatusFilter === "Present" && row[6] === "Present") ||
      (attendanceStatusFilter === "Absent" && row[6] === "Absent");
    return matchesQuery && matchesStatus;
  });
  const recordRows = (id) => {
    const employeeStart = getEmployeeAttendanceStartForId(id);
    const effectiveStart = employeeStart && rangeStart < employeeStart ? employeeStart : rangeStart;

    const attendanceDays = attendanceForDisplay
      .filter(
        (record) =>
          String(record.employee_id) === String(id) &&
          parseUTC(record.timestamp) >= effectiveStart &&
          parseUTC(record.timestamp) <= rangeEnd,
      )
      .reduce((days, record) => {
        const date = parseUTC(record.timestamp);
        const key = date.toDateString();
        days[key] ||= {
          date,
          checkIn: null,
          checkOut: null,
          checkInId: null,
          checkOutId: null,
          checkInSelfie: false,
          checkOutSelfie: false,
          checkInEdited: false,
          checkInEditedBy: null,
          checkOutEdited: false,
          checkOutEditedBy: null,
        };
        if (record.check_type === "in") {
          days[key].checkIn = date;
          days[key].checkInId = record.id || record.attendance_id;
          days[key].checkInSelfie = !!record.selfie_available;
          days[key].checkInEdited = !!record.edited;
          days[key].checkInEditedBy = record.edited_by || null;
        }
        if (record.check_type === "out") {
          days[key].checkOut = date;
          days[key].checkOutId = record.id || record.attendance_id;
          days[key].checkOutSelfie = !!record.selfie_available;
          days[key].checkOutEdited = !!record.edited;
          days[key].checkOutEditedBy = record.edited_by || null;
        }
        return days;
      }, {});

    const allDays = {};
    const current = new Date(effectiveStart);
    while (current <= rangeEnd) {
      const key = current.toDateString();
      const date = new Date(current);
      allDays[key] = attendanceDays[key] || { 
        date, 
        checkIn: null, 
        checkOut: null, 
        checkInId: null, 
        checkOutId: null, 
        checkInSelfie: false, 
        checkOutSelfie: false,
        checkInEdited: false,
        checkInEditedBy: null,
        checkOutEdited: false,
        checkOutEditedBy: null,
      };
      current.setDate(current.getDate() + 1);
    }

    return Object.values(allDays)
      .sort((a, b) => b.date - a.date)
      .filter((day) => {
        if (employeeStart && day.date < employeeStart) return false;
        return day.date
          .toLocaleDateString()
          .toLowerCase()
          .includes(query.toLowerCase());
      });
  };
  const selfEmployeeId = currentUser
    ? String(currentUser.employee_id || currentUser.user_id || employeeId)
    : employeeId;
  const selfRows = recordRows(
    scope === "Self" ? selfEmployeeId : selectedEmployeeId,
  );

  // Pagination for the "All" staff directory view
  const {
    page: attAllPage,
    setPage: setAttAllPage,
    totalPages: attAllTotalPages,
    totalItems: attAllTotalItems,
    pageData: pagedVisibleRows,
    pageSize: attAllPageSize,
  } = usePagination(visibleRows, attendancePageSize);
  useEffect(() => { setAttAllPage(1); }, [scope, query, attendanceStatusFilter, branchFilter, departmentFilter, designationFilter, selectedEmployeeId, attendancePageSize]); // eslint-disable-line

  // Pagination for the "Self" / per-employee daily view
  const {
    page: attSelfPage,
    setPage: setAttSelfPage,
    totalPages: attSelfTotalPages,
    totalItems: attSelfTotalItems,
    pageData: pagedSelfRows,
    pageSize: attSelfPageSize,
  } = usePagination(selfRows, recordsPerPage);
  useEffect(() => { setAttSelfPage(1); }, [scope, range, query, selectedEmployeeId, recordsPerPage]); // eslint-disable-line
  const formatTime = (date) =>
    date
      ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "-";
  const formatHours = (day) => {
    if (!day.checkIn || !day.checkOut) return "-";
    const minutes = Math.max(
      0,
      Math.round((day.checkOut - day.checkIn) / 60000),
    );
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  };
  const startAttendanceEdit = (id, timestamp, checkType) => {
    const value = new Date(timestamp);
    const localDate = new Date(value.getTime() - value.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    const localTime = new Date(value.getTime() - value.getTimezoneOffset() * 60000).toISOString().slice(11, 16);
    setAttendanceEdit({ id, checkType, date: localDate, value: localTime });
    setAttendanceEditError("");
  };
  const saveAttendanceEdit = async () => {
    if (!attendanceEdit?.value) return;
    setAttendanceEditBusy(true);
    setAttendanceEditError("");
    try {
      const response = await fetch(`${API_URL}/attendance/${encodeURIComponent(attendanceEdit.id)}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ timestamp: new Date(`${attendanceEdit.date}T${attendanceEdit.value}`).toISOString() }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(normalizeErrorMessage(data, "Unable to update attendance."));
      setAttendanceEdit(null);
      await onAttendanceRefresh?.();
    } catch (error) {
      setAttendanceEditError(error.message || "Unable to update attendance.");
    } finally {
      setAttendanceEditBusy(false);
    }
  };
  const dailyTable = (
    <table className="attendance-table self-attendance-table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Check-in</th>
          <th>Check-out</th>
          <th>Working hours</th>
          <th>Status</th>
          <th>Selfie</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {pagedSelfRows.map((day) => (
          <tr key={day.date.toISOString()}>
            <td>
              {day.date.toLocaleDateString(undefined, {
                day: "2-digit",
                month: "short",
                year: "numeric",
                weekday: "short",
              })}
            </td>
            <td>
              {formatTime(day.checkIn)}
              {day.checkInEdited && <small className="attendance-edit-meta">Edited by {day.checkInEditedBy || "Unknown user"}</small>}
            </td>
            <td>
              {formatTime(day.checkOut)}
              {day.checkOutEdited && <small className="attendance-edit-meta">Edited by {day.checkOutEditedBy || "Unknown user"}</small>}
            </td>
            <td>{formatHours(day)}</td>
            <td>
              <span
                className={`attendance-status ${day.checkIn ? "present" : "absent"}`}
              >
                {day.checkIn ? "Present" : "Absent"}
              </span>
            </td>
            <td>
              <div className="selfie-cell">
                {day.checkInSelfie && (
                  <button
                    type="button"
                    className="selfie-thumb-btn"
                    title="Check-in selfie"
                    onClick={() => fetchSelfie(day.checkInId)}
                  >
                    IN
                  </button>
                )}
                {day.checkOutSelfie && (
                  <button
                    type="button"
                    className="selfie-thumb-btn out"
                    title="Check-out selfie"
                    onClick={() => fetchSelfie(day.checkOutId)}
                  >
                    OUT
                  </button>
                )}
                {!day.checkInSelfie && !day.checkOutSelfie && (
                  <span className="selfie-none">-</span>
                )}
              </div>
            </td>
            <td>
              <div className="attendance-row-actions">
                {canEditAttendance && (day.checkInId || day.checkOutId) ? <button type="button" className="attendance-action-button" onClick={() => setAttendanceActionDay(day)}>Edit</button> : <span className="selfie-none">-</span>}
              </div>
            </td>
          </tr>
        ))}
        {!selfRows.length && (
          <tr>
            <td colSpan="7" className="attendance-no-results">
              No attendance records found for this filter.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
  
  const directoryFilters = (
    <div className="employee-directory-filters">
      <div className="att-filter-group">
        <span className="att-filter-label">Filters</span>
        <SelectFilter
          value={branchFilter}
          onChange={setBranchFilter}
          placeholder="All branches"
          options={[
            { value: "", label: "All branches" },
            ...filterOptions.branches.map((b) => ({ value: b.value, label: b.label })),
          ]}
        />
      </div>
      <SelectFilter
        value={departmentFilter}
        onChange={setDepartmentFilter}
        placeholder="All departments"
        options={[
          { value: "", label: "All departments" },
          ...filterOptions.departments.map((d) => ({ value: d, label: d })),
        ]}
      />
      <SelectFilter
        value={designationFilter}
        onChange={setDesignationFilter}
        placeholder="All designations"
        options={[
          { value: "", label: "All designations" },
          ...filterOptions.designations.map((d) => ({ value: d, label: d })),
        ]}
      />
      {(branchFilter || departmentFilter || designationFilter) && (
        <button
          type="button"
          className="clear-directory-filters"
          onClick={() => {
            setBranchFilter("");
            setDepartmentFilter("");
            setDesignationFilter("");
          }}
        >
          Clear filters
        </button>
      )}
    </div>
  );
  return (
    <section className="attendance-page">
      <header className="attendance-page-head" style={{justifyContent: 'flex-start', textAlign: 'left'}}>
        <div style={{textAlign: 'left', width: '100%'}}>
          <h1 style={{textAlign: 'left'}}>
            {selectedEmployee
              ? `${selectedEmployee.full_name} attendance`
              : "Attendance"}
          </h1>
        </div>
        {selectedEmployee && (
          <button
            type="button"
            className="attendance-back"
            onClick={() => selectAttendanceEmployee("")}
          >
            <ArrowLeft size={15} /> ← Back to all attendance
          </button>
        )}
      </header>
      
      {/* Filters toolbar */}
      <div className="emp-table-toolbar" style={{ justifyContent: "flex-start", gap: "16px" }}>
        <div className="emp-table-toolbar-left">
          <div className="emp-filters-group">
            {/* Scope Filter: Self / All */}
            <div className="filter-with-search">
              <div className={`emp-status-filter-wrap ${scope && scope !== "" ? "has-filter" : ""}`}>
                <SelectFilter
                  value={scope}
                  onChange={v => { setScope(v); selectAttendanceEmployee(""); }}
                  clearable={scope === "All"}
                  clearValue="Self"
                  placeholder="Scope"
                  options={[
                    { value: "Self", label: "Self", icon: <UserRound size={16} /> },
                    { value: "All", label: "All", icon: <Users size={16} /> },
                  ]}
                />
              </div>
              <div className={`emp-status-filter-wrap ${attendanceStatusFilter !== "All" ? "has-filter" : ""}`}>
                <SelectFilter
                  value={attendanceStatusFilter}
                  onChange={setAttendanceStatusFilter}
                  placeholder="Attendance status"
                  options={[
                    { value: "All", label: "All statuses" },
                    { value: "Present", label: "Present" },
                    { value: "Absent", label: "Absent" },
                  ]}
                />
              </div>
              <div className="attendance-search emp-search-box">
                <Search size={15} />
                <input
                  aria-label="Search attendance"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={
                    scope === "All" && !selectedEmployeeId
                      ? "Search employees"
                      : "Search dates"
                  }
                />
                {query && (
                  <button type="button" className="filter-search-clear" aria-label="Clear attendance search" onClick={() => setQuery("")}>
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            
            {/* Date Range Filter (show in Self view or when specific employee is selected) */}
            {(scope === "Self" || selectedEmployee) && (
              <>
                <div className={`emp-status-filter-wrap ${range && range !== "" ? "has-filter" : ""}`}>
                  <SelectFilter
                    value={range}
                    onChange={v => setRange(v)}
                    clearable
                    placeholder="This month"
                    options={[
                      { value: "This month", label: "This month", icon: <CalendarDays size={16} /> },
                      { value: "This year", label: "This year", icon: <CalendarDays size={16} /> },
                      { value: "Last 30 days", label: "Last 30 days", icon: <Clock3 size={16} /> },
                      { value: "Last 60 days", label: "Last 60 days", icon: <Clock3 size={16} /> },
                      { value: "Last 90 days", label: "Last 90 days", icon: <Clock3 size={16} /> },
                      { value: "Custom range", label: "Custom range", icon: <CalendarRange size={16} /> },
                    ]}
                  />
                </div>
                {range === "Custom range" && (
                  <div className="custom-range-inline">
                    <CustomDateField
                      label="Start"
                      value={customStart}
                      onChange={setCustomStart}
                    />
                    <CustomDateField
                      label="End"
                      value={customEnd}
                      onChange={setCustomEnd}
                      maxDate={new Date().toISOString().slice(0, 10)}
                    />
                  </div>
                )}
              </>
            )}
            
            {/* Filters dropdown button (contains Branch, Department, Designation) */}
            {scope === "All" && !selectedEmployee && (
              <div className="emp-status-filter-wrap attendance-filters-dropdown-wrap">
                <button
                  type="button"
                  className="sf-trigger attendance-filters-trigger"
                  onClick={() => setShowFiltersDropdown(!showFiltersDropdown)}
                  aria-expanded={showFiltersDropdown}
                >
                  <SlidersHorizontal size={16} />
                  <span className="sf-value">Filters</span>
                  {(branchFilter || departmentFilter || designationFilter) && (
                    <span className="filter-badge">{[branchFilter, departmentFilter, designationFilter].filter(Boolean).length}</span>
                  )}
                  <ChevronDown size={16} className="sf-chevron" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Filters dropdown panel (pushes table down when visible) */}
        {showFiltersDropdown && scope === "All" && !selectedEmployee && (
          <div className="attendance-filters-panel">
            {/* Branch Filter */}
            <div className="emp-status-filter-wrap">
              <SelectFilter
                value={branchFilter}
                onChange={setBranchFilter}
                clearable
                placeholder="Branch"
                options={[
                  { value: "", label: "All Branches", icon: <Building2 size={16} /> },
                  ...branches.map(b => ({ value: String(b.branch_id), label: b.name, icon: <MapPin size={16} /> }))
                ]}
              />
            </div>
            
            {/* Department Filter */}
            <div className="emp-status-filter-wrap">
              <SelectFilter
                value={departmentFilter}
                onChange={setDepartmentFilter}
                clearable
                placeholder="Department"
                options={[
                  { value: "", label: "All Departments", icon: <Briefcase size={16} /> },
                  ...departments.map(d => ({ value: d, label: d }))
                ]}
              />
            </div>
            
            {/* Designation Filter */}
            <div className="emp-status-filter-wrap">
              <SelectFilter
                value={designationFilter}
                onChange={setDesignationFilter}
                clearable
                placeholder="Designation"
                options={[
                  { value: "", label: "All Designations", icon: <Target size={16} /> },
                  ...designations.map(d => ({ value: d, label: d }))
                ]}
              />
            </div>
          </div>
        )}

        <div
          className="emp-table-toolbar-right"
          style={{ marginLeft: "auto", position: "relative", right: "12px" }}
        >
          {scope === "All" && !selectedEmployee && (
            <div className="emp-view-toggle" aria-label="Attendance view">
              <button
                className={`emp-view-btn ${attendanceViewMode === "list" ? "active" : ""}`}
                onClick={() => setAttendanceViewMode("list")}
                title="List view"
                type="button"
              >
                <List size={16} />
              </button>
              <button
                className={`emp-view-btn ${attendanceViewMode === "grid" ? "active" : ""}`}
                onClick={() => setAttendanceViewMode("grid")}
                title="Grid view"
                type="button"
              >
                <LayoutGrid size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="attendance-table-wrap">
        {scope === "Self" || selectedEmployee ? (
          dailyTable
        ) : attendanceViewMode === "list" ? (
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Employee Code</th>
                <th>Department</th>
                <th>Designation</th>
                <th>Today</th>
                <th>Check-in</th>
                <th>Hours</th>
                <th>Attendance</th>
              </tr>
            </thead>
            <tbody>
              {pagedVisibleRows.length ? pagedVisibleRows.map(
                ([id, initials, employeeId, name, role, team, status, checkIn, hours, score]) => (
                  <tr
                    className="attendance-selectable-row"
                    key={id}
                    onClick={() => {
                      selectAttendanceEmployee(id);
                      setRange("Last 30 days");
                    }}
                  >
                    <td>
                      <div className="attendance-person">
                        <span>{initials}</span>
                        <div><strong>{name}</strong></div>
                      </div>
                    </td>
                    <td>{employeeId}</td>
                    <td>{team}</td>
                    <td>{role}</td>
                    <td><span className={`attendance-status ${status.toLowerCase().replace(" ", "-")}`}>{status}</span></td>
                    <td>{checkIn}</td>
                    <td>{hours}</td>
                    <td>
                      <div className="attendance-score">
                        <i><b style={{ width: `${score}%` }} /></i>
                        <strong>{score}%</strong>
                      </div>
                    </td>
                  </tr>
                ),
              ) : (
                <tr><td colSpan="8" className="attendance-no-results">No attendance records found for the selected filters.</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <div className="attendance-card-grid">
            {pagedVisibleRows.length ? pagedVisibleRows.map(
              ([id, initials, employeeId, name, role, team, status, checkIn, hours], index) => (
                <div
                  className="emp-card attendance-employee-card"
                  key={id}
                  onClick={() => {
                    selectAttendanceEmployee(id);
                    setRange("Last 30 days");
                  }}
                >
                  <div className="emp-card-header">
                    <div className={`emp-card-avatar palette-${index % 6}`}>{initials}</div>
                    <div className="emp-card-title-section">
                      <h3 className="emp-card-title">{name}</h3>
                      <p className="emp-card-subtitle">
                        <span className="emp-card-meta-item">
                          <Target size={11} /> {role || "Employee"}
                        </span>
                        <span className="emp-card-meta-item">
                          <Briefcase size={11} /> {team || ""}
                        </span>
                      </p>
                    </div>
                  </div>
                  <span className={`emp-card-status-badge ${status.toLowerCase().replace(" ", "-")}`}>
                    <i /> {status}
                  </span>
                  <div className="emp-card-attendance-row">
                    <span>
                      <Clock3 size={13} /> {checkIn}
                    </span>
                    <span>
                      <LogOut size={13} /> {hours}
                    </span>
                  </div>
                </div>
              ),
            ) : (
              <div className="attendance-no-results">No attendance records found for the selected filters.</div>
            )}
          </div>
        )}
      </div>
      
      {/* Self/Employee view pagination */}
      {(scope === "Self" || selectedEmployee) && (
        <Pagination
          page={attSelfPage}
          totalPages={attSelfTotalPages}
          totalItems={attSelfTotalItems}
          pageSize={attSelfPageSize}
          onPageChange={setAttSelfPage}
        />
      )}
      
      {/* All employees view pagination */}
      {!(scope === "Self" || selectedEmployee) && (
        <div className="attendance-pagination">
          <Pagination
            page={attAllPage}
            totalPages={attAllTotalPages}
            totalItems={attAllTotalItems}
            pageSize={attAllPageSize}
            onPageChange={setAttAllPage}
          />
        </div>
      )}

      {/* Selfie modal */}
      {attendanceActionDay && (
        <div className="selfie-modal-backdrop" onClick={() => setAttendanceActionDay(null)}>
          <div className="attendance-edit-modal" role="dialog" aria-modal="true" aria-labelledby="attendance-action-title" onClick={(event) => event.stopPropagation()}>
            <h2 id="attendance-action-title">Choose attendance time to edit</h2>
            <div className="attendance-edit-actions">
              {attendanceActionDay.checkInId && <button type="button" className="outline-action" onClick={() => { startAttendanceEdit(attendanceActionDay.checkInId, attendanceActionDay.checkIn, "in"); setAttendanceActionDay(null); }}>Edit check-in</button>}
              {attendanceActionDay.checkOutId && <button type="button" className="outline-action" onClick={() => { startAttendanceEdit(attendanceActionDay.checkOutId, attendanceActionDay.checkOut, "out"); setAttendanceActionDay(null); }}>Edit check-out</button>}
            </div>
            <div className="attendance-edit-buttons"><button type="button" className="outline-action" onClick={() => setAttendanceActionDay(null)}>Cancel</button></div>
          </div>
        </div>
      )}
      {attendanceEdit && (
        <div className="selfie-modal-backdrop" onClick={() => !attendanceEditBusy && setAttendanceEdit(null)}>
          <div className="attendance-edit-modal" role="dialog" aria-modal="true" aria-labelledby="attendance-edit-title" onClick={(event) => event.stopPropagation()}>
            <h2 id="attendance-edit-title">Edit check-{attendanceEdit.checkType === "in" ? "in" : "out"} time</h2>
            <label>Date</label>
            <p className="attendance-edit-date">{new Date(`${attendanceEdit.date}T00:00:00`).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}</p>
            <label htmlFor="attendance-edit-time">Time</label>
            <input id="attendance-edit-time" type="time" value={attendanceEdit.value} onChange={(event) => setAttendanceEdit({ ...attendanceEdit, value: event.target.value })} />
            {attendanceEditError && <p className="attendance-edit-error" role="alert">{attendanceEditError}</p>}
            <div className="attendance-edit-buttons">
              <button type="button" className="outline-action" disabled={attendanceEditBusy} onClick={() => setAttendanceEdit(null)}>Cancel</button>
              <button type="button" className="primary-action" disabled={attendanceEditBusy || !attendanceEdit.value} onClick={saveAttendanceEdit}>{attendanceEditBusy ? "Saving…" : "Save changes"}</button>
            </div>
          </div>
        </div>
      )}
      {selfieModal && (
        <div className="selfie-modal-backdrop" onClick={closeSelfie}>
          <div className="selfie-modal" onClick={(e) => e.stopPropagation()}>
            {selfieModal.loading && <div className="selfie-loading">Loading...</div>}
            {selfieModal.error && <div className="selfie-error">{selfieModal.error}</div>}
            {selfieModal.url && (
              <>
                <img src={selfieModal.url} alt="Check-in selfie" />
                <button type="button" className="selfie-modal-close" onClick={closeSelfie} aria-label="Close selfie preview"><X size={16} /></button>
              </>
            )}
          </div>
        </div>
      )}
      
      {(scope === "Self" || selectedEmployee) && console.log('Pagination hidden: scope =', scope, 'selectedEmployee =', selectedEmployee)}
      {!(scope === "Self" || selectedEmployee) && console.log('Pagination shown: totalPages =', attAllTotalPages, 'totalItems =', attAllTotalItems, 'pageSize =', attAllPageSize)}
    </section>
  );
}

function AttendanceDashboard(props) {
  return <LegacyAttendanceDashboard {...props} />;
}

function HomeDashboard({
  currentUser,
  staff,
  branches,
  attendance,
  token,
  onAddStaff,
  onAddBranch,
  onOpenRequest,
}) {
  const [requests, setRequests] = useState([]);
  const [requestCountRequests, setRequestCountRequests] = useState([]);
  const [todayLeaveRequests, setTodayLeaveRequests] = useState([]);
  const [selfAttendance, setSelfAttendance] = useState([]);
  const roleKey = normalizeRoleKey(localStorage.getItem("hiwaga_role") || "");
  const canReview = ["super_admin", "branch_manager", "floor_manager"].includes(roleKey);
  const [requestTab, setRequestTab] = useState(() => canReview ? "team" : "self");
  const [statisticsScope, setStatisticsScope] = useState(() => canReview ? "team" : "self");

  useEffect(() => {
    const employeeId = currentUser?.employee_id;
    if (!token || !employeeId) {
      setSelfAttendance([]);
      return undefined;
    }
    let ignore = false;
    apiFetch(`/attendance/employee/${encodeURIComponent(employeeId)}/history?page_size=100`, {}, token)
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(normalizeErrorMessage(data, "Unable to load your attendance."));
        const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        if (!ignore) setSelfAttendance(items);
      })
      .catch(() => { if (!ignore) setSelfAttendance([]); });
    return () => { ignore = true; };
  }, [currentUser?.employee_id, token]);

  useEffect(() => {
    if (!token) {
      setRequests([]);
      setRequestCountRequests([]);
      return undefined;
    }

    let ignore = false;
    const loadHomeRequests = async () => {
      try {
        const endpoint = requestTab === "team" && canReview ? "/requests" : "/requests/me";
        const response = await apiFetch(endpoint, {}, token);
        const data = await response.json().catch(() => []);
        if (!response.ok) {
          throw new Error(normalizeErrorMessage(data, "Unable to load requests."));
        }
        if (!ignore) {
          const list = Array.isArray(data) ? data : [];
          
          // Enrich requests with employee names (same logic as RequestsPage)
          const enrichedRequests = list.map(req => {
            let employeeFullName = req.full_name;
            
            if (!employeeFullName) {
              // Check if this is the current user's request
              const isCurrentUser = currentUser && (
                String(currentUser.employee_id) === String(req.employee_id) ||
                String(currentUser.user_id) === String(req.employee_id) ||
                String(currentUser.employee_code) === String(req.employee_id)
              );
              
              if (isCurrentUser) {
                employeeFullName = currentUser.full_name;
              } else {
                // Find employee in staff array
                const employee = staff.find(person => 
                  person.employee_id === req.employee_id || 
                  person.employee_code === req.employee_id
                );
                
                if (employee) {
                  employeeFullName = employee.full_name;
                }
              }
            }
            
            return {
              ...req,
              employee_full_name: employeeFullName
            };
          });
          
          setRequests(enrichedRequests);
          if (requestTab === "team" || !canReview) setRequestCountRequests(enrichedRequests);
        }
      } catch {
        if (!ignore) setRequests([]);
      }
    };

    loadHomeRequests();
    return () => {
      ignore = true;
    };catio
  }, [canReview, requestTab, token, staff, currentUser]);

  useEffect(() => {
    if (!token) {
      setTodayLeaveRequests([]);
      return undefined;
    }

    let ignore = false;
    const loadLeaveToday = async () => {
      try {
        const endpoint = canReview ? "/requests" : "/requests/me";
        const response = await apiFetch(endpoint, {}, token);
        const data = await response.json().catch(() => []);
        if (!response.ok) {
          throw new Error(normalizeErrorMessage(data, "Unable to load leave data."));
        }
        if (!ignore) {
          const todayIso = new Date().toISOString().slice(0, 10);
          const filtered = (Array.isArray(data) ? data : []).filter((request) => {
            if (request.request_type !== "leave") return false;
            const fromDate = request.from_date;
            const toDate = request.to_date || request.from_date;
            return Boolean(fromDate && toDate && todayIso >= fromDate && todayIso <= toDate);
          }).map((request) => {
            let employeeFullName = request.full_name;
            if (!employeeFullName) {
              const isCurrentUser = currentUser && (
                String(currentUser.employee_id) === String(request.employee_id) ||
                String(currentUser.user_id) === String(request.employee_id) ||
                String(currentUser.employee_code) === String(request.employee_id)
              );
              if (isCurrentUser) {
                employeeFullName = currentUser.full_name;
              } else {
                const employee = staff.find((person) =>
                  person.employee_id === request.employee_id ||
                  person.employee_code === request.employee_id,
                );
                if (employee) {
                  employeeFullName = employee.full_name;
                }
              }
            }
            return { ...request, employee_full_name: employeeFullName };
          });
          setTodayLeaveRequests(filtered);
        }
      } catch {
        if (!ignore) setTodayLeaveRequests([]);
      }
    };

    loadLeaveToday();
    return () => {
      ignore = true;
    };
  }, [canReview, token, staff, currentUser]);

  useEffect(() => {
    const bottomGrid = document.querySelector(
      ".home-dashboard > .home-two-column:last-child",
    );
    if (!bottomGrid || bottomGrid.querySelector(".home-extra-row"))
      return undefined;
    const extraRow = document.createElement("div");
    extraRow.className = "home-extra-row";
    const today = new Date();
    const escapeHtml = (value) => String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
    const birthdayEvents = staff
      .filter((person) => person.date_of_birth && person.full_name)
      .map((person) => {
        const birthDate = new Date(`${String(person.date_of_birth).slice(0, 10)}T00:00:00`);
        if (Number.isNaN(birthDate.getTime())) return null;
        let nextBirthday = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate());
        if (nextBirthday < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
          nextBirthday = new Date(today.getFullYear() + 1, birthDate.getMonth(), birthDate.getDate());
        }
        return { name: person.full_name, date: nextBirthday };
      })
      .filter(Boolean)
      .sort((first, second) => first.date - second.date)
      .slice(0, 7);
    const eventMarkup = birthdayEvents.length
      ? birthdayEvents.map((event) => {
          const initials = event.name
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase() || "")
            .join("") || "??";
          return `<article><i class="home-extra-item-icon event-icon">${initials}</i><strong>${escapeHtml(event.name)}</strong><span>${event.date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}</span><em>Birthday</em></article>`;
        }).join("")
      : '<div class="home-extra-empty"><span class="holiday-placeholder-icon">&#128197;</span><span>No upcoming events</span></div>';
    extraRow.innerHTML = `<section class="home-panel home-extra-panel events-panel"><div class="home-panel-title"><span>🎉 EVENTS</span></div><div class="home-extra-scroll">${eventMarkup}</div><div class="home-scrollbar"><span></span></div></section><section class="home-panel home-extra-panel holidays-panel"><div class="home-panel-title"><span>HOLIDAYS</span></div><div class="home-extra-scroll"><div class="home-empty holidays-empty"><span>No holidays today</span></div></div></section>`;
    const leaveIcon = document.querySelector(".home-leave .home-empty svg");
    const holidaysEmpty = extraRow.querySelector(".holidays-empty");
    if (leaveIcon && holidaysEmpty) {
      holidaysEmpty.prepend(leaveIcon.cloneNode(true));
    }
    bottomGrid.parentElement.appendChild(extraRow);
    extraRow.querySelectorAll(".home-extra-scroll").forEach((scrollArea) => {
      const thumb = scrollArea.parentElement.querySelector(
        ".home-scrollbar span",
      );
      const updateThumb = () => {
        if (!thumb) return;
        const range = scrollArea.scrollHeight - scrollArea.clientHeight;
        thumb.style.transform = `translateY(${range ? (scrollArea.scrollTop / range) * 112 : 0}px)`;
      };
      scrollArea.addEventListener("scroll", updateThumb);
      updateThumb();
    });
    return () => extraRow.remove();
  }, [staff]);
  useEffect(() => {
    const todayTitle = document.querySelector(".home-mock-title");
    const monthIcon = document.querySelector(
      ".home-period .home-panel-title svg",
    );
    if (!todayTitle || !monthIcon || todayTitle.querySelector("svg"))
      return undefined;
    const icon = monthIcon.cloneNode(true);
    icon.setAttribute("aria-hidden", "true");
    todayTitle.prepend(icon);
    return () => icon.remove();
  }, []);
  useEffect(() => {
    const title = document.querySelector(".home-mock-title");
    if (!title || title.querySelector(".admin-attendance-action"))
      return undefined;
    const ownRecords = new Map();
    [...attendance, ...selfAttendance].forEach((record) => {
      if (currentUser && String(record.employee_id) === String(currentUser.employee_id)) {
        ownRecords.set(record.id || `${record.timestamp}-${record.check_type}`, record);
      }
    });
    const todayRecords = [...ownRecords.values()].filter(
      (record) => parseUTC(record.timestamp).toDateString() === new Date().toDateString(),
    );
    const checkType =
      todayRecords.some((record) => record.check_type === "in") &&
      !todayRecords.some((record) => record.check_type === "out")
        ? "out"
        : "in";
    const formatAttendanceTime = (record) =>
      record
        ? parseUTC(record.timestamp).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "--:--";
    const statPills = document.querySelectorAll(
      ".home-mock-stat-row .home-mock-pill",
    );
    if (statPills[0]) {
      statPills[0].querySelector(".home-mock-icon").textContent = "⇥";
      statPills[0].querySelector("strong").textContent = formatAttendanceTime(
        todayRecords.find((record) => record.check_type === "in"),
      );
    }
    if (statPills[1]) {
      statPills[1].classList.add("checkout-pill");
      statPills[1].querySelector(".home-mock-icon").textContent = "⇤";
      statPills[1].querySelector("span:not(.home-mock-icon)").textContent =
        "CHECK-OUT";
      statPills[1].querySelector("strong").textContent = formatAttendanceTime(
        todayRecords.find((record) => record.check_type === "out"),
      );
    }
    const action = document.createElement("button");
    const modal = document.createElement("div");
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");
    const preview = document.createElement("img");
    const capture = document.createElement("button");
    const retake = document.createElement("button");
    const submit = document.createElement("button");
    const cancel = document.createElement("button");
    const shiftPicker = document.createElement("div");
    const shiftPickerPanel = document.createElement("div");
    const shiftPickerHeader = document.createElement("div");
    const shiftPickerTitle = document.createElement("strong");
    const shiftPickerClose = document.createElement("button");
    const branchPickerLabel = document.createElement("label");
    const branchPickerSelect = document.createElement("div");
    const branchPickerValue = document.createElement("span");
    const branchPickerOptions = document.createElement("div");
    const shiftPickerLabel = document.createElement("label");
    const shiftPickerSelect = document.createElement("div");
    const shiftPickerValue = document.createElement("span");
    const shiftPickerOptions = document.createElement("div");
    const shiftPickerActions = document.createElement("div");
    const shiftPickerCancel = document.createElement("button");
    const shiftPickerContinue = document.createElement("button");
    const shiftPickerError = document.createElement("p");
    let stream = null;
    let capturedBlob = null;
    let selectedBranchId = "";
    let selectedShiftId = "";
    action.type = "button";
    action.className = `admin-attendance-action ${checkType === "in" ? "is-check-in" : "is-check-out"}`;
    action.textContent = checkType === "in" ? "CHECK IN" : "CHECK OUT";
    modal.className = "admin-camera-modal";
    video.className = "admin-camera-video";
    video.autoplay = true;
    video.muted = true;
    video.setAttribute("playsinline", "true");
    preview.className = "admin-selfie-preview";
    preview.alt = "Selfie preview";
    preview.style.display = "none";
    capture.type = "button";
    capture.className = "primary-action";
    capture.textContent = "Capture selfie";
    retake.type = "button";
    retake.className = "outline-action";
    retake.textContent = "Retake";
    retake.style.display = "none";
    submit.type = "button";
    submit.className = "primary-action";
    submit.textContent = checkType === "in" ? "Submit check-in" : "Submit check-out";
    submit.style.display = "none";
    cancel.type = "button";
    cancel.className = "outline-action";
    cancel.textContent = "Cancel";
    shiftPicker.className = "admin-shift-picker-modal";
    shiftPickerPanel.className = "admin-shift-picker-panel";
    shiftPickerHeader.className = "admin-shift-picker-header";
    shiftPickerTitle.textContent = checkType === "in" ? "Select your branch and shift" : "Select your branch";
    shiftPickerClose.type = "button";
    shiftPickerClose.className = "modal-close";
    shiftPickerClose.setAttribute("aria-label", "Close shift selection");
    shiftPickerClose.textContent = "-";
    branchPickerLabel.className = "admin-shift-picker-label";
    branchPickerLabel.textContent = "Branch";
    branchPickerSelect.className = "admin-shift-picker-select";
    branchPickerSelect.setAttribute("role", "button");
    branchPickerSelect.setAttribute("tabindex", "0");
    branchPickerValue.className = "admin-shift-picker-value";
    branchPickerValue.textContent = "Select a branch";
    branchPickerOptions.className = "admin-shift-picker-options";
    shiftPickerLabel.className = "admin-shift-picker-label";
    shiftPickerLabel.textContent = "Work shift";
    shiftPickerSelect.className = "admin-shift-picker-select";
    shiftPickerSelect.setAttribute("role", "button");
    shiftPickerSelect.setAttribute("tabindex", "0");
    shiftPickerValue.className = "admin-shift-picker-value";
    shiftPickerOptions.className = "admin-shift-picker-options";
    shiftPickerCancel.type = "button";
    shiftPickerCancel.className = "outline-action";
    shiftPickerCancel.textContent = "Cancel";
    shiftPickerContinue.type = "button";
    shiftPickerContinue.className = "primary-action";
    shiftPickerContinue.textContent = "Continue to camera";
    shiftPickerContinue.disabled = true;
    shiftPickerError.className = "admin-shift-picker-error";
    shiftPickerError.style.display = "none";
    shiftPickerActions.className = "admin-shift-picker-actions";
    shiftPickerActions.append(shiftPickerCancel, shiftPickerContinue);
    branchPickerSelect.append(branchPickerValue, branchPickerOptions);
    branchPickerLabel.appendChild(branchPickerSelect);
    shiftPickerSelect.append(shiftPickerValue, shiftPickerOptions);
    shiftPickerLabel.appendChild(shiftPickerSelect);
    shiftPickerHeader.append(shiftPickerTitle, shiftPickerClose);
    shiftPickerPanel.append(shiftPickerHeader, branchPickerLabel);
    if (checkType === "in") shiftPickerPanel.appendChild(shiftPickerLabel);
    shiftPickerPanel.append(shiftPickerError, shiftPickerActions);
    shiftPicker.appendChild(shiftPickerPanel);
    const wrapper = document.createElement("div");
    wrapper.className = "admin-camera-wrapper";
    const actionsRow = document.createElement("div");
    actionsRow.className = "admin-camera-actions";
    actionsRow.append(capture, retake, submit, cancel);
    wrapper.append(video, preview, canvas);
    modal.append(wrapper, actionsRow);
    const showPreview = () => {
      video.style.display = "none";
      capture.style.display = "none";
      preview.style.display = "block";
      retake.style.display = "inline-flex";
      submit.style.display = "inline-flex";
    };
    const showCamera = () => {
      preview.style.display = "none";
      retake.style.display = "none";
      submit.style.display = "none";
      video.style.display = "block";
      capture.style.display = "inline-flex";
      capturedBlob = null;
    };
    const stopCamera = () => {
      if (stream) stream.getTracks().forEach((track) => track.stop());
      stream = null;
      modal.remove();
      shiftPicker.remove();
    };
    const openCameraAfterShift = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        alert("Camera access is not supported in this browser.");
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: false,
        });
        video.srcObject = stream;
        showCamera();
        document.body.append(modal);
      } catch {
        alert("Camera permission was denied or the camera is unavailable.");
      }
    };
    shiftPickerCancel.addEventListener("click", () => shiftPicker.remove());
    shiftPickerClose.addEventListener("click", () => shiftPicker.remove());
    branchPickerSelect.addEventListener("click", (event) => {
      if (event.target !== branchPickerSelect && event.target !== branchPickerValue) return;
      branchPickerSelect.classList.toggle("is-open");
      branchPickerOptions.classList.toggle("is-visible");
    });
    shiftPickerSelect.addEventListener("click", (event) => {
      if (event.target !== shiftPickerSelect && event.target !== shiftPickerValue) return;
      shiftPickerSelect.classList.toggle("is-open");
      shiftPickerOptions.classList.toggle("is-visible");
    });
    branchPickerSelect.addEventListener("change", async () => {
      selectedBranchId = branchPickerSelect.dataset.value || "";
      selectedShiftId = "";
      shiftPickerSelect.dataset.value = "";
      shiftPickerValue.textContent = "Select a shift";
      shiftPickerOptions.innerHTML = "";
      shiftPickerContinue.disabled = !selectedBranchId || checkType === "in";
      shiftPickerError.textContent = "";
      shiftPickerError.style.display = "none";
      if (!selectedBranchId || checkType !== "in") return;
      shiftPickerValue.textContent = "Loading shifts…";
      try {
        const response = await apiFetch(
          `/attendance/available-shifts?branch_id=${encodeURIComponent(selectedBranchId)}`,
          {},
          localStorage.getItem("hiwaga_access_token") || "",
        );
        const shifts = response.ok ? await response.json().catch(() => []) : [];
        if (selectedBranchId !== branchPickerSelect.dataset.value) return;
        if (!Array.isArray(shifts) || !shifts.length) {
          shiftPickerValue.textContent = "No active shifts available";
          return;
        }
        shiftPickerValue.textContent = "Select a shift";
        shifts.forEach((shift) => {
          const option = document.createElement("button");
          option.type = "button";
          option.className = "admin-shift-picker-option";
          option.dataset.value = shift.shift_id;
          option.textContent = `${shift.name} (${String(shift.start_time).slice(0, 5)} - ${String(shift.end_time).slice(0, 5)})`;
          option.addEventListener("pointerdown", (event) => {
            event.stopPropagation();
            event.preventDefault();
            shiftPickerSelect.dataset.value = shift.shift_id;
            selectedShiftId = shift.shift_id;
            shiftPickerValue.textContent = option.textContent;
            shiftPickerContinue.disabled = false;
            shiftPickerOptions.querySelectorAll(".admin-shift-picker-option").forEach((item) => {
              item.classList.toggle("is-selected", item === option);
            });
            shiftPickerOptions.classList.remove("is-visible");
            shiftPickerSelect.classList.remove("is-open");
          });
          option.addEventListener("click", (event) => event.stopPropagation());
          shiftPickerOptions.appendChild(option);
        });
      } catch {
        if (selectedBranchId === branchPickerSelect.dataset.value) {
          shiftPickerValue.textContent = "Unable to load shifts";
        }
      }
    });
    shiftPickerContinue.addEventListener("click", () => {
      if (!selectedBranchId) {
        shiftPickerError.textContent = "Select a branch to continue.";
        shiftPickerError.style.display = "block";
        return;
      }
      selectedShiftId = shiftPickerSelect.dataset.value || "";
      if (checkType === "in" && !selectedShiftId) {
        shiftPickerError.textContent = "Select a work shift to continue.";
        shiftPickerError.style.display = "block";
        return;
      }
      shiftPicker.remove();
      openCameraAfterShift();
    });
    action.addEventListener("click", async () => {
      selectedBranchId = "";
      selectedShiftId = "";
      shiftPickerSelect.dataset.value = "";
      shiftPickerValue.textContent = "Select a shift";
      shiftPickerOptions.innerHTML = "";
      branchPickerSelect.dataset.value = "";
      branchPickerValue.textContent = "Select a branch";
      branchPickerOptions.innerHTML = "";
      branchPickerOptions.classList.remove("is-visible");
      branchPickerSelect.classList.remove("is-open");
      shiftPickerContinue.disabled = true;
      try {
        const response = await apiFetch(
          "/attendance/available-branches",
          {},
          localStorage.getItem("hiwaga_access_token") || "",
        );
        const branches = response.ok ? await response.json().catch(() => []) : [];
        if (!Array.isArray(branches) || !branches.length) {
          alert("No attendance branches are available for your account.");
          return;
        }
        branches.forEach((branch) => {
          const option = document.createElement("button");
          option.type = "button";
          option.className = "admin-shift-picker-option";
          option.dataset.value = branch.branch_id;
          option.textContent = `${branch.name}${branch.is_default ? " (Default)" : ""}`;
          option.addEventListener("pointerdown", (event) => {
            event.stopPropagation();
            event.preventDefault();
            branchPickerSelect.dataset.value = branch.branch_id;
            selectedBranchId = branch.branch_id;
            branchPickerValue.textContent = option.textContent;
            branchPickerOptions.querySelectorAll(".admin-shift-picker-option").forEach((item) => {
              item.classList.toggle("is-selected", item === option);
            });
            branchPickerOptions.classList.remove("is-visible");
            branchPickerSelect.classList.remove("is-open");
            branchPickerSelect.dispatchEvent(new Event("change"));
          });
          option.addEventListener("click", (event) => event.stopPropagation());
          branchPickerOptions.appendChild(option);
        });
        document.body.append(shiftPicker);
      } catch {
        alert("Unable to load attendance branches. Please try again.");
      }
    });
    cancel.addEventListener("click", stopCamera);
    retake.addEventListener("click", async () => {
      if (!stream) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "user" },
            audio: false,
          });
          video.srcObject = stream;
        } catch {
          alert("Camera permission was denied or the camera is unavailable.");
          return;
        }
      }
      showCamera();
    });
    capture.addEventListener("click", async () => {
      const width = video.videoWidth || 640;
      const height = video.videoHeight || 480;
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d")?.drawImage(video, 0, 0, width, height);
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.92),
      );
      if (!blob) return;
      capturedBlob = blob;
      preview.src = URL.createObjectURL(blob);
      // stop camera stream while showing preview
      if (stream) { stream.getTracks().forEach((t) => t.stop()); stream = null; }
      showPreview();
    });
    submit.addEventListener("click", async () => {
      if (!capturedBlob) return;
      stopCamera();
      action.disabled = true;
      action.textContent = "Getting location...";
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const body = new FormData();
          body.append("latitude", position.coords.latitude);
          body.append("longitude", position.coords.longitude);
          body.append("device_info", navigator.userAgent);
          body.append("branch_id", selectedBranchId);
          if (checkType === "in") body.append("shift_id", selectedShiftId);
          body.append(
            "selfie",
            new File([capturedBlob], `admin-selfie-${Date.now()}.jpg`, {
              type: "image/jpeg",
            }),
          );
          const response = await apiFetch(
            `/attendance/${checkType === "in" ? "checkin" : "checkout"}`,
            { method: "POST", body },
            localStorage.getItem("hiwaga_access_token") || "",
          );
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            alert(
              data.detail === "Account has no assigned branch"
                ? "Assign a branch to this admin account before recording attendance."
                : data.detail ||
                    `Unable to ${checkType === "in" ? "check in" : "check out"}.`,
            );
            action.disabled = false;
            action.textContent = checkType === "in" ? "CHECK IN" : "CHECK OUT";
            return;
          }
          window.location.reload();
        },
        () => {
          alert("Location permission is required for attendance.");
          action.disabled = false;
          action.textContent = checkType === "in" ? "CHECK IN" : "CHECK OUT";
        },
      );
    });
    title.append(action);
    return () => {
      stopCamera();
      action.remove();
    };
  }, [attendance, selfAttendance, currentUser?.employee_id]);
  const formatTime = (record) =>
    record
      ? parseUTC(record.timestamp).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "--:--";
  const todayRecords = attendance.filter(
    (record) => parseUTC(record.timestamp).toDateString() === new Date().toDateString(),
  );
  const checkedIn = todayRecords.filter((record) => record.check_type === "in").length;
  const checkedOut = todayRecords.filter((record) => record.check_type === "out").length;
  const activeStaff = staff.filter((person) => person.employment_status === "active").length;
  const pendingRequests = requestCountRequests.filter((request) => request.status === "pending").length;
  const now = new Date();
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const thisYearStart = new Date(now.getFullYear(), 0, 1);
  const toLocalDay = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime())
        ? null
        : new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    const datePart = String(value).slice(0, 10);
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
    if (dateMatch) return new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]));
    const parsed = parseUTC(value);
    return parsed && !Number.isNaN(parsed.getTime())
      ? new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
      : null;
  };
  const employeeIdentifiers = new Set(
    [currentUser?.employee_id, currentUser?.user_id, currentUser?.employee_code, localStorage.getItem("hiwaga_employee_id")]
      .filter((value) => value !== undefined && value !== null && String(value).trim())
      .map(String),
  );
  
  console.log('🔍 EMPLOYEE IDENTIFIERS DEBUG 🔍:', {
    currentUser_employee_id: currentUser?.employee_id,
    currentUser_user_id: currentUser?.user_id, 
    currentUser_employee_code: currentUser?.employee_code,
    localStorage_employee_id: localStorage.getItem("hiwaga_employee_id"),
    employeeIdentifiers: Array.from(employeeIdentifiers),
    sampleAttendanceRecords: attendance.slice(0, 3).map(record => ({
      user_id: record.user_id,
      employee_id: record.employee_id,
      employee_code: record.employee_code,
      checkIn: record.checkIn,
      wouldMatchUserId: record?.user_id !== undefined && record?.user_id !== null && employeeIdentifiers.has(String(record.user_id)),
      wouldMatchEmployeeId: record?.employee_id !== undefined && record?.employee_id !== null && employeeIdentifiers.has(String(record.employee_id))
    }))
  });
  
  const belongsToCurrentUser = (record) => {
    // Check multiple possible field names based on actual DB structure
    const matchesUserId = record?.user_id !== undefined && record?.user_id !== null && employeeIdentifiers.has(String(record.user_id));
    const matchesEmployeeId = record?.employee_id !== undefined && record?.employee_id !== null && employeeIdentifiers.has(String(record.employee_id));
    const matchesEmployeeCode = record?.employee_code !== undefined && record?.employee_code !== null && employeeIdentifiers.has(String(record.employee_code));
    
    return matchesUserId || matchesEmployeeId || matchesEmployeeCode;
  };
  const employeeProfile = staff.find((person) =>
    [person.employee_id, person.user_id, person.employee_code].some((value) =>
      value !== undefined && value !== null && employeeIdentifiers.has(String(value)),
    ),
  );
  const joiningDate = toLocalDay(currentUser?.date_of_joining || employeeProfile?.date_of_joining);
  const effectiveStart = (periodStart) =>
    joiningDate && joiningDate > periodStart ? new Date(joiningDate) : new Date(periodStart);
  const effectiveMonthStart = effectiveStart(thisMonthStart);
  const effectiveYearStart = effectiveStart(thisYearStart);

  const countInRange = (items, fieldName, start, end) =>
    items.filter((item) => {
      const rawValue = item?.[fieldName] ?? item?.created_at;
      const date = rawValue ? parseUTC(rawValue) : null;
      return date && date >= start && date <= end;
    }).length;

  const currentUserRequests = requestCountRequests.filter(belongsToCurrentUser);
  const currentUserAttendance = [...new Map(
    [...attendance, ...selfAttendance]
      .filter(belongsToCurrentUser)
      .map((record) => [record.id || `${record.employee_id}-${record.timestamp}-${record.check_type}`, record]),
  ).values()];

  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterdayRecords = currentUserAttendance.filter(
    (record) =>
      parseUTC(record.timestamp).toDateString() ===
      yesterdayDate.toDateString(),
  );
  const yesterdayCheckIn = yesterdayRecords.find(
    (record) => record.check_type === "in",
  );
  const yesterdayCheckOut = yesterdayRecords.find(
    (record) => record.check_type === "out",
  );
  const yesterdayLabel = yesterdayDate.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const monthLeaveCount = countInRange(
    currentUserRequests.filter((request) => request.request_type === "leave"),
    "created_at",
    effectiveMonthStart,
    now,
  );
  const monthApprovedLeaveCount = countInRange(
    currentUserRequests.filter((request) => request.request_type === "leave" && String(request.status).toLowerCase() === "approved"),
    "reviewed_at",
    effectiveMonthStart,
    now,
  );
  const monthPermissionCount = countInRange(
    currentUserRequests.filter((request) => request.request_type === "late_permission"),
    "created_at",
    effectiveMonthStart,
    now,
  );
  const calculateAbsentDays = (userAttendanceData, startDate, endDate, userRequests) => {
    const firstDay = toLocalDay(startDate);
    const lastDay = toLocalDay(endDate);
    if (!firstDay || !lastDay || firstDay > lastDay) return 0;

    const dayKey = (date) => date.toDateString();
    const daysWithCheckIn = new Set(
      userAttendanceData
        .filter((record) => String(record.check_type).toLowerCase() === "in")
        .map((record) => toLocalDay(record.timestamp))
        .filter((date) => date && date >= firstDay && date <= lastDay)
        .map(dayKey),
    );
    const excusedDays = new Set();
    userRequests
      .filter((request) => String(request.status).toLowerCase() === "approved")
      .forEach((request) => {
        const requestType = String(request.request_type).toLowerCase().replace(/-/g, "_");
        if (requestType === "weekly_off") {
          const offDate = toLocalDay(request.off_date || request.permission_date || request.from_date);
          if (offDate && offDate >= firstDay && offDate <= lastDay) excusedDays.add(dayKey(offDate));
        }
        if (requestType === "leave") {
          const leaveStart = toLocalDay(request.from_date);
          const leaveEnd = toLocalDay(request.to_date || request.from_date);
          if (!leaveStart || !leaveEnd) return;
          const cursor = new Date(Math.max(firstDay.getTime(), leaveStart.getTime()));
          const overlapEnd = new Date(Math.min(lastDay.getTime(), leaveEnd.getTime()));
          for (; cursor <= overlapEnd; cursor.setDate(cursor.getDate() + 1)) {
            excusedDays.add(dayKey(cursor));
          }
        }
      });

    let absentCount = 0;
    for (const cursor = new Date(firstDay); cursor <= lastDay; cursor.setDate(cursor.getDate() + 1)) {
      const key = dayKey(cursor);
      if (!daysWithCheckIn.has(key) && !excusedDays.has(key)) absentCount += 1;
    }
    return absentCount;
  };

  const monthLogCount = calculateAbsentDays(currentUserAttendance, effectiveMonthStart, now, currentUserRequests);

  const yearLeaveCount = countInRange(
    currentUserRequests.filter((request) => request.request_type === "leave"),
    "created_at",
    effectiveYearStart,
    now,
  );
  const yearApprovedLeaveCount = countInRange(
    currentUserRequests.filter((request) => request.request_type === "leave" && String(request.status).toLowerCase() === "approved"),
    "reviewed_at",
    effectiveYearStart,
    now,
  );
  const yearPermissionCount = countInRange(
    currentUserRequests.filter((request) => request.request_type === "late_permission"),
    "created_at",
    effectiveYearStart,
    now,
  );
  const yearLogCount = calculateAbsentDays(currentUserAttendance, effectiveYearStart, now, currentUserRequests);

  const cards = [
    ["CHECK-INS", checkedIn, "Today", CheckCircle2, "green"],
    ["CHECK-OUTS", checkedOut, "Today", LogOut, "rose"],
    ["ABSENT", Math.max(activeStaff - checkedIn, 0), "Today", UserRound, "blue"],
    ["REQUESTS", pendingRequests, "Open", Send, "purple"],
  ];
  return (
    <div className="home-dashboard">
      <div className="home-welcome">
        <h2>
          Welcome back, {currentUser?.full_name?.split(" ")[0] || "Admin"} <span>👋</span>
        </h2>
        <p>Here's what's happening with your team today.</p>
      </div>
      <div className="home-directory-grid">
        {cards.map(([title, value, detail, Icon, tone]) => (
          <div className={`home-directory-card ${tone}`} key={title}>
            <div className="home-directory-copy">
              <span>{title}</span>
              <strong>{value}</strong>
              <small>{detail}</small>
            </div>
            <div className="home-directory-icon">
              <Icon size={18} />
            </div>
            <div className="home-directory-live">LIVE</div>
          </div>
        ))}
      </div>
      <div className="home-two-column">
        <section className="home-panel home-today home-mock-today">
          <div className="home-panel-title home-mock-title">
            <span>Today</span>
            <small>
              {new Date().toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                weekday: "short",
              })}
            </small>
          </div>
          <div className="home-mock-stat-row">
            <div className="home-mock-pill green">
              <span className="home-mock-icon">⇥</span>
              <span>CHECK-IN</span>
              <strong>--:--</strong>
            </div>
            <div className="home-mock-pill blue">
              <span className="home-mock-icon">⇤</span>
              <span>CHECK-OUT</span>
              <strong>--:--</strong>
            </div>
          </div>
        </section>
        <section className="home-panel home-yesterday is-working-day">
          <HomePanelTitle title="YESTERDAY" suffix={yesterdayLabel} />
          <div className="home-yesterday-times">
            <HomeStat
              icon={Clock3}
              label="CHECK-IN"
              value={formatTime(yesterdayCheckIn)}
              tone="green"
            />
            <HomeStat
              icon={Clock3}
              label="CHECK-OUT"
              value={formatTime(yesterdayCheckOut)}
              tone="red"
            />
          </div>
        </section>
      </div>
      <div className="home-two-column">
        <HomePeriod
          title="THIS MONTH"
          items={["Leaves Applied", "Leaves Approved", "Permissions", "Logs"]}
          values={[
            String(monthLeaveCount),
            String(monthApprovedLeaveCount),
            String(monthPermissionCount),
            String(monthLogCount),
          ]}
        />
        <HomePeriod
          title="THIS YEAR"
          items={[
            "Leaves Applied",
            "Leaves Approved",
            "Permissions",
            "Logs",
          ]}
          values={[
            String(yearLeaveCount),
            String(yearApprovedLeaveCount),
            String(yearPermissionCount),
            String(yearLogCount),
          ]}
        />
      </div>
      <div className="home-two-column">
        <section className="home-panel home-requests">
          <HomePanelTitle title="REQUESTS" />
          <div className="home-request-tabs">
            <button
              type="button"
              className={requestTab === "self" ? "active" : ""}
              onClick={() => setRequestTab("self")}
            >
              Self
            </button>
            <button
              type="button"
              className={requestTab === "team" ? "active" : ""}
              onClick={() => setRequestTab("team")}
              disabled={!canReview}
              aria-disabled={!canReview}
            >
              Team
            </button>
          </div>
          {requests.length ? (
            <div className="home-requests-scroll">
              {requests.map((request) => (
                <div className="home-request-row" key={request.id || `${request.request_type}-${request.created_at}`}>
                  {requestTab === "team" && request.employee_full_name && (
                    <small className="home-request-employee">{request.employee_full_name}</small>
                  )}
                  <strong>{request.request_type === "leave" ? "Leave" : request.request_type === "early_logout" ? "Early logout" : "Late login"}</strong>
                  <span>{request.status}</span>
                  <b>
                    {request.request_type === "leave"
                      ? request.from_date && request.to_date
                        ? `${request.from_date} – ${request.to_date}`
                        : "Leave request"
                      : `${request.permission_date || "Permission request"}${request.expected_departure_time ? ` - Leave by ${request.expected_departure_time}` : request.expected_arrival_time ? ` - Arrive by ${request.expected_arrival_time}` : ""}`}
                  </b>
                  <button 
                    className="home-request-open-btn"
                    type="button"
                    onClick={() => onOpenRequest && onOpenRequest(request.id)}
                  >
                    Open
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="home-empty">
              <Send size={32} />
              <span>Open Requests to submit or review requests.</span>
            </div>
          )}
        </section>
        <section className="home-panel home-leave">
          <HomePanelTitle title="ON LEAVE TODAY" suffix="(your team)" />
          {todayLeaveRequests.length ? (
            <div className="home-leave-list">
              {todayLeaveRequests.map((request, index) => {
                const fullName = request.employee_full_name || "Employee";
                const initials = fullName
                  .split(/\s+/)
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((part) => part[0]?.toUpperCase() || "")
                  .join("") || "E";
                const avatarTone = ["blue", "green", "purple"][index % 3];
                const leaveDate = request.from_date && request.to_date && request.from_date === request.to_date
                  ? request.from_date
                  : `${request.from_date || ""} – ${request.to_date || request.from_date || ""}`;
                return (
                  <div className="home-leave-person" key={request.id || `${request.employee_id || "employee"}-${request.from_date}-${request.to_date}`}>
                    <div className={`home-leave-avatar ${avatarTone}`}>{initials}</div>
                    <div>
                      <strong>{fullName}</strong>
                      <small>{leaveDate}</small>
                    </div>
                    <em>Leave</em>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="home-empty">
              <CalendarDays size={32} />
              <span>No one is on leave today</span>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function HomePanelTitle({ title, suffix }) {
  const Icon =
    title === "REQUESTS"
      ? Send
      : title === "ON LEAVE TODAY"
        ? UserRound
        : CalendarDays;
  return (
    <div className="home-panel-title">
      <Icon size={17} />
      <span>{title}</span>
      {suffix && <small>{suffix}</small>}
    </div>
  );
}
function HomeStat({ icon: Icon, label, value, tone }) {
  return (
    <div className="home-stat">
      <span className={`home-stat-icon ${tone}`}>
        <Icon size={17} />
      </span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
      </div>
    </div>
  );
}
function HomePeriodWithFilter({ title, items, values, scope, onScopeChange, canReview }) {
  return (
    <section className="home-panel home-period">
      <div className="home-panel-title-with-filter">
        <HomePanelTitle title={title} />
        {canReview && (
          <div className="home-period-tabs">
            <div>
              <button
                type="button"
                className={scope === "self" ? "active" : ""}
                onClick={() => onScopeChange("self")}
              >
                Self
              </button>
              <button
                type="button"
                className={scope === "team" ? "active" : ""}
                onClick={() => onScopeChange("team")}
              >
                Team
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="home-period-grid">
        {items.map((item, index) => (
          <div key={item}>
            <span>{item === "Logs" ? "Absent" : item}</span>
            <strong>{values[index]}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}

function HomePeriod({ title, items, values }) {
  return (
    <section className="home-panel home-period">
      <HomePanelTitle title={title} />
      <div className="home-period-grid">
        {items.map((item, index) => (
          <div key={item}>
            <span>{item === "Logs" ? "Absent" : item}</span>
            <strong>{values[index]}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}

export default App;
